import { NAME_MAX_LENGTH, NAME_MIN_LENGTH } from "../constants/names"
import { MAX_QUERY_LENGTH, MAX_SLUG_LENGTH, MIN_PASSWORD_LENGTH, MIN_SLUG_LENGTH } from "../constants/validationLimits"
import { MAX_LIMIT, MIN_LIMIT } from "../enums/pages"
import { IntlKey } from "./index"

export const EN: Record<IntlKey, string> = {
  accepted: "Accepted",
  accessRights: "Access Rights",
  increment: "Increment",
  decrement: "Decrement",
  actions: "Actions",
  add: "Add",
  agbCode: "AGB code",
  agenda: "Agenda",
  answered: "Answered",
  approved: "Approved",
  areYouSure: "Are you sure?",
  assignment: "Assignment",
  cancel: "Cancel",
  caregiver: "Caregiver",
  closed: "Closed",
  completed: "Completed",
  copy: "Copy",
  copyAndEdit: "Make a copy",
  count: "Count",
  countryOfBirth: "Country of birth",
  created: "Created",
  createdAt: "Created at",
  date: "Date",
  defaultFollowUp: "Default follow-up",
  "delete.confirm": "Yes, delete",
  "delete.confirmTitle": "This action cannot be undone",
  delete: "Delete",
  email: "Email",
  communication: "Communication",
  description: "Description",
  followUp: "Follow-up",
  isAdmin: "Is admin",
  label: "Label",
  labels: "Labels",
  less: "Less",
  "locale.en-GB": "English",
  "input.en-GB": "English text",
  "locale.nl-NL": "Dutch",
  "input.nl-NL": "Dutch text",
  logo: "Logo",
  me: "Me",
  totpEnabled: "2FA enabled",
  more: "More",
  new: "New",
  "no.data.found": "No data found",
  "data.loading": "We're fetching your data. This may take a moment.",
  "table.showAll": "Show all",
  "table.showLess": "Show less",
  no: "No",
  none: "None",
  notApplicable: "N/A",
  notLinked: "Not linked",
  ok: "Okay",
  open: "Open",
  "options.open": "Open options",
  options: "Options",
  send: "Send",
  resolve: "Resolve",
  retry: "Retry",
  loading: "Loading...",
  submit: "Submit",

  "pageNotFound.description":
    "There can be multiple reasons. Make sure you entered the correct URL. The page may have been deleted or you no longer have access.",
  "pageNotFound.title": "Page not found",

  "pageInvitation.send.registration": "Share registration link",
  "pageInvitation.send.appointment": "Share appointment link",

  "pageInvitation.modal.title.registration": "Share single-use registration link",
  "pageInvitation.modal.title.appointment": "Share single-use appointment link",

  "pageInvitation.field.practice": "Practice",
  "pageInvitation.field.practice.placeholder": "Pick a practice",
  "pageInvitation.field.page": "Page",
  "pageInvitation.field.page.placeholder": "Pick a page",
  "pageInvitation.field.recipientEmail": "Recipient email",
  "pageInvitation.field.recipientEmail.placeholder": "name@example.com",
  "pageInvitation.field.recipientPatient": "Patient",
  "pageInvitation.field.recipientPatient.email": "The invitation will be sent to {email}.",
  "pageInvitation.field.recipientPatient.noEmail":
    "This patient has no email on file. Pick another patient or add an email to their record first.",
  "pageInvitation.field.message": "Message",
  "pageInvitation.message.default.registration":
    "With this personal link you can register at our practice. Click the link and follow the steps to complete your registration.",
  "pageInvitation.message.default.appointment":
    "With this personal link you can book an appointment at our practice. Click the link and pick a time that suits you.",
  "pageInvitation.infoBanner.registration":
    "The link still works when the chosen registration site page is closed for new registrations.",
  "pageInvitation.infoBanner.appointment": "This link skips the consultation-preparation questionnaire.",
  "pageInvitation.conditions.label": "Select what applies to this registration:",
  "pageInvitation.conditions.description":
    "We have preselected the items that normally apply (to your regular registrations).",
  "pageInvitation.conditions.enforcePostalCodeArea": "Lives within postal code area",
  "pageInvitation.conditions.passant": "Passant (temporary patient, stays registered with their own GP)",
  "pageInvitation.conditions.passant.hint":
    "A passant is never registered in ION; the registration is sent to the HIS and no letter is sent to the previous GP. Does the passant live outside your postal code area? Then untick 'Lives within postal code area'.",
  "pageInvitation.conditions.bsnRequired": "BSN required",
  "pageInvitation.conditions.bsnRequired.hint":
    "Only switch this off for a passant without Dutch nationality (tourist, expat). Without a BSN no COV check is performed and no insurance details are asked. A Dutch passant still needs a valid BSN for the declaration.",
  "pageInvitation.conditions.pharmacyRequired": "Pharmacy required",
  "pageInvitation.conditions.pharmacyRequired.hint":
    "Only switch this off if the patient has no pharmacy here (yet). No pharmacy is asked and none is sent to the HIS. For a passant without a BSN the Insurance & pharmacy step is then skipped entirely.",
  "pageInvitation.warning.noPagesForPractice":
    "This practice has no suitable page yet. Configure one in the page settings first.",
  "pageInvitation.warning.existingActiveInvitation":
    "An active invitation already exists for this address. Sending a new link does not revoke the previous one — it stays valid until it expires.",
  "pageInvitation.shareVia.label": "Share via",
  "pageInvitation.shareVia.email": "Email",
  "pageInvitation.shareVia.link": "Link",
  "pageInvitation.shareVia.qr": "QR code",
  "pageInvitation.link.description": "This link works once, and is not tied to an email address or person.",
  "pageInvitation.link.requiresPage": "Pick a page first to generate a link.",
  "pageInvitation.link.generateFailed": "Generating the link failed.",
  "pageInvitation.link.copy": "Copy",
  "pageInvitation.link.copied": "Copied",
  "pageInvitation.link.copiedToast": "Link and expiry date copied",
  "pageInvitation.link.copyFailed": "Copying failed. The link is selected; copy it manually (Ctrl+C / Cmd+C).",
  "pageInvitation.qr.description": "This QR code works once, and is not tied to an email address or person.",
  "pageInvitation.qr.requiresPage": "Pick a page first to generate a QR code.",
  "pageInvitation.qr.generateFailed": "Generating the QR code failed.",
  "pageInvitation.qr.generate": "Generate QR code",
  "pageInvitation.qr.generateAnother": "Generate another QR code",
  "pageInvitation.qr.settingsChanged":
    "The settings changed after this QR code was generated. Generate a new QR code to apply them.",
  "pageInvitation.qr.expiresOn": "The QR code is valid until {date}.",
  "pageInvitation.link.generate": "Generate link",
  "pageInvitation.link.generateAnother": "Generate another link",
  "pageInvitation.link.createdOn": "Created on {date}.",
  "pageInvitation.link.settingsChanged":
    "The settings changed after this link was generated. Generate a new link to apply them.",
  "pageInvitation.preview.show": "View preview",
  "pageInvitation.preview.edit": "Edit message",
  "pageInvitation.preview.requiresPage": "Select a page and write a message to see the email preview.",
  "pageInvitation.preview.unavailable": "Email preview is unavailable for this practice.",
  "pageInvitation.submit": "Send",
  "pageInvitation.cancel": "Cancel",
  "pageInvitation.share.heading": "Invitation sent",
  "pageInvitation.share.emailSent": "The link has been emailed to {email}.",
  "pageInvitation.share.emailFailed":
    "Sending the email failed. Copy the link below and share it with {email} yourself.",
  "pageInvitation.share.expiresOn": "The link is valid until {date}.",
  "pageInvitation.share.sendAnother": "Send another invitation",
  "pageInvitation.share.close": "Close",

  "userInvitation.add": "Invite colleague",
  "userInvitation.invite.admin": "Invite",
  "userInvitation.modal.subtitle": "The colleague receives a one-time invitation link by email.",
  "userInvitation.modal.subtitle.admin":
    "Manage as platform admin. The colleague gets access to the selected practices in one go.",
  "userInvitation.field.admin": "Administrator",
  "userInvitation.field.temporaryAccess": "Temporary access",
  "userInvitation.field.practices": "Practices",
  "userInvitation.field.practices.placeholder": "Select practices",
  "userInvitation.field.email.helper": "The invitation is sent to this address.",
  "userInvitation.field.validUntil.helper":
    "Access ends on this date. The invitation link itself expires after 7 days — it can be resent.",
  "userInvitation.submit": "Send invitation",
  "userInvitation.error.duplicate":
    "An active invitation already exists for this name and email address. Use 'resend' or withdraw the existing invitation.",
  "userInvitation.toast.sent": "Invitation sent",
  "userInvitation.toast.resent": "Invitation resent",
  "userInvitation.toast.withdrawn": "Invitation withdrawn",
  "userInvitation.toast.emailFailed": "The invitation was created, but the email could not be sent. Try resending it.",
  "userInvitation.toast.actionFailed": "Action failed, please try again.",
  "userInvitation.toast.someAlreadyInvited":
    "{count, plural, one {# practice already had an invitation and was skipped} other {# practices already had an invitation and were skipped}}.",
  "userInvitation.table.title": "Pending invitations",
  "userInvitation.table.empty": "No pending invitations",
  "userInvitation.column.status": "Status",
  "userInvitation.column.practice": "Practice",
  // Expiry of the invitation link itself, not the access `validUntil`
  "userInvitation.column.expires": "Link expires",
  "userInvitation.status.sent": "Invitation sent",
  "userInvitation.status.expired": "Invitation expired",
  "userInvitation.action.resend": "Resend invitation",
  "userInvitation.action.withdraw": "Withdraw invitation",
  "userInvitation.withdraw.confirm": "Are you sure you want to withdraw this invitation?",
  "userInvitation.public.invite": "Invite",
  "userInvitation.public.accept.welcome": "Welcome, {name}",
  "userInvitation.public.accept.subtitle": "{scope} has invited you to access the practice.",
  "userInvitation.public.accept.subtitle.bare": "You have been invited to MijnPraktijk.",
  "userInvitation.public.accept.existing.one":
    "This email already has an account. Log in to connect it to this invitation.",
  "userInvitation.public.accept.existing.many":
    "This email has multiple accounts. Choose one and log in to connect it to this invitation.",
  "userInvitation.public.accept.loginWith": "Log in with your account",
  "userInvitation.public.accept.createNew": "Create a new account",
  "userInvitation.public.accept.createNew.subtitle": "A separate MijnPraktijk account",
  "userInvitation.public.accept.or": "or",
  "userInvitation.public.accept.unexpected":
    "Did this invitation arrive unexpectedly? Ignore this page, or contact the practice manager.",
  "userInvitation.public.accept.expired.title": "Invitation not available",
  "userInvitation.public.accept.expired.body":
    "This invitation has expired or has already been used. Ask the sender for a new invitation.",
  "userInvitation.public.accept.connecting": "Connecting…",
  "userInvitation.public.accept.error":
    "Connecting your account failed. This account may not belong to the invited email address. You can ask the practice manager for a new invitation link.",
  "userInvitation.public.accept.createTitle": "Create your account",
  "userInvitation.public.accept.createSubtitle": "Enter your name and choose a username and password.",
  "userInvitation.public.accept.usernameHint": "Only letters, digits, periods and hyphens. At least 5 characters.",
  "userInvitation.public.accept.passwordHint":
    "At least {min} characters, with a letter, a digit and a special character.",
  "userInvitation.public.accept.step.details": "Details",
  "userInvitation.public.accept.step.twoFactor": "Two-step verification",
  "userInvitation.public.accept.totpTitle": "Set up two-step verification",
  "userInvitation.public.accept.totpSubtitle": "We require 2FA to access patient data.",

  "pagination.columnSelect": "Select columns",
  "pagination.searchOptions": "Search options",
  "pagination.searchOptions.reset": "Enable all",
  "pagination.filter.apply": "Apply",
  "pagination.filter.clear": "Clear filters",
  "pagination.filter.remove": "Remove filter",
  "pagination.filter": "Filters",
  "pagination.next": "Next",
  "pagination.perPage": "{value} per page",
  "pagination.previous": "Previous",
  "pagination.show": "Show",
  "pagination.total": "{total, plural, one {# result} other {# results}}",

  insurerName: "Health insurance",
  insuranceNumber: "Policy number",
  pharmacy: "Pharmacy",
  priority: "Priority",
  "questionnaireName.dropdown": "Select questionnaire",
  questionnaireName: "Questionnaire name",
  readMore: "Read more",
  rejected: "Rejected",
  reply: "Reply",
  scrollToBottom: "Scroll to bottom",
  sentToHis: "Sent to HIS",
  showMore: "Show more",
  showPatient: "Show Patient",
  state: "Status",
  status: "Status",
  sentToHisStatus: "Send status",
  statusChangedTo: "Status changed to {status}",
  subject: "Subject",
  task: "Task",
  time: "Time",
  to: "to",
  today: "Today",
  type: "Type",
  unanswered: "Unanswered",
  undecided: "Pending",
  processing: "Processing",
  unknown: "Unknown",
  urgency: "Urgency",
  walkInConsult: "Digital walk-in consult",
  title: "Title",
  subTitle: "Sub title",
  sender: "Sender",
  receiver: "Receiver",
  yes: "Yes",
  "yesOrNo.select": "Select yes or no",
  "important.text": "Important",
  expandWithCount: "+ {count, plural, one {# more} other {# more}}",
  approximateDistance: "Approximate distance",
  active: "Active",
  inactive: "Inactive",
  enabled: "Enabled",
  "action.enable": "Enable",
  disabled: "Disabled",
  "action.disable": "Disable",

  "error.fallback.title": "Something went wrong",
  "error.fallback.description":
    "We are sorry, but something unexpected happened. Our team has been notified. You can reload the page and try again.",
  "error.fallback.tryAgain": "Try again",

  "limit.reached": "Limit of {limit} reached",

  edit: "Edit",
  "edit.cancel": "Cancel edit",

  validUntil: "Valid until",
  "validUntil.inactive": "Deactivated",
  "validUntil.inactive.reason":
    "This account was deactivated, because the user has not logged in for {weeks} weeks. Last active: {lastActivity}",

  "filter.next7days": "Next 7 days",
  "filter.next30days": "Next 30 days",
  "filter.all": "All",
  "filter.past7days": "Past 7 days",
  "filter.past30days": "Past 30 days",
  "filter.past3months": "Past 3 months",
  "filter.past6months": "Past 6 months",
  "filter.pastYear": "Past year",
  "filter.custom": "Custom",
  "filter.from": "From",
  "filter.to": "To",

  "sortBy.default.ASC": "Ascending",
  "sortBy.default.DESC": "Descending",
  "sortBy.alphabetical.ASC": "A - Z",
  "sortBy.alphabetical.DESC": "Z - A",
  "sortBy.numeric.ASC": "Low to high",
  "sortBy.numeric.DESC": "High to low",
  "sortBy.age.ASC": "Young - old",
  "sortBy.age.DESC": "Old - young",
  "sortBy.distance.ASC": "Close by first",
  "sortBy.distance.DESC": "Far away first",
  "sortBy.boolean.ASC": "Yes first",
  "sortBy.boolean.DESC": "Yes last",
  "sortBy.date.ASC": "Oldest first",
  "sortBy.date.DESC": "Recent first",
  "sortBy.postalCode.ASC": "1000 - 9999",
  "sortBy.postalCode.DESC": "9999 - 1000",
  "sortBy.clear": "Clear sorting",

  "emptyState.noData.title": "Nothing found",
  "emptyState.noData.description": "There is currently no data available here.",
  "emptyState.filtered.title": "No results with these filters",
  "emptyState.filtered.description": "We cannot find any data with your search query or applied filters.",

  name: "Name",
  "name.required": "Name is required",
  "name.unique": "Name must be unique",

  icpc: "ICPC code",
  icpcCode: "ICPC code",
  "icpcCode.placeholder": "Search ICPC code",
  "icpcCode.search": "Type to search ICPC code",
  "icpcCode.required": "ICPC code is required",

  "notes.add.tooltip": "Add notes",
  "notes.added.successfully": "Note added successfully",
  "notes.updated.successfully": "Note updated successfully",
  "notes.removed.successfully": "Note removed successfully",

  "copyToClipboard.click": "Click to copy",
  "copyToClipboard.copied": "Copied",

  "practiceSearch.title": "Welcome to {name}",
  "practiceSearch.national.title": "Find your new GP here",
  "practiceSearch.national.subtext":
    "See which GP practices near you are accepting new patients and register online right away.",
  "practiceSearch.searchZipCode": "What is your postcode?",
  "practiceSearch.searchZipCode.placeholder": "Enter your postcode, for example 2512 AB",
  "practiceSearch.searchZipCode.submit": "Search practice",
  "practiceSearch.searchZipCode.reset": "Reset search",
  "practiceSearch.searchZipCode.results": "Search results",
  "practiceSearch.searchZipCode.noResults":
    "Unfortunately, we have not found any practices that accept patients in your area",
  "practiceSearch.searchZipCode.noResults.clarification":
    "This does not mean they don't exist: they may not be registered with us. Check websites of practices in your area to find out if they accept patients.",
  "practiceSearch.searchZipCode.register": "Register",
  "practiceSearch.searchZipCode.registrationClosed": "This practice is not accepting registrations at the moment",
  "practiceSearch.searchZipCode.disclaimer":
    "Is there no suitable practice in your area? Please contact your health insurer.",

  "postalCodeWaitingList.title": "Should we let you know if there is room?",
  "postalCodeWaitingList.description":
    "Some practices are closed for registrations.{br}Would you like to receive an email when a practice in your postcode area ({postalCode}) opens?",
  "postalCodeWaitingList.email.placeholder": "Email address",
  "postalCodeWaitingList.submit": "Keep me informed",
  "postalCodeWaitingList.numberOfPeople.prefix": "I'm looking for a place for",
  "postalCodeWaitingList.numberOfPeople.suffix": "people",
  "postalCodeWaitingList.success.title": "Confirm your email address",
  "postalCodeWaitingList.success.description":
    "We have sent an email to {email}. Please click the link in the email to confirm your subscription.",
  "postalCodeWaitingList.verify.title": "Email verified",
  "postalCodeWaitingList.verify.description":
    "Your email address was successfully verified.{br}We will then email you when a practice in your area opens for registrations.",
  "postalCodeWaitingList.error.title": "Something went wrong",
  "postalCodeWaitingList.error.description":
    "We could not verify your email. Try again by reloading this page or try again later.",
  "postalCodeWaitingList.reload": "Reload page",
  "postalCodeWaitingList.invalidVerificationCode": "Invalid verification code",
  "postalCodeWaitingList.settings.title": "Confirmed",
  "postalCodeWaitingList.settings.description":
    "You will receive an email when one of the practices below is open for registration.",
  "postalCodeWaitingList.settings.subtitle": "General practitioners in postcode area {postalCode}",
  "postalCodeWaitingList.settings.update.success": "Your settings have been saved successfully.",
  "postalCodeWaitingList.unsubscribe.title": "No more emails about new practices in your area",
  "postalCodeWaitingList.unsubscribe.description":
    "If you do nothing, you will be automatically unsubscribed after three months.{br}{br}Click the button below to unsubscribe immediately. You can always subscribe again later.",
  "postalCodeWaitingList.unsubscribe.confirm": "Unsubscribe now",
  "postalCodeWaitingList.unsubscribe.success.title": "Unsubscribed",
  "postalCodeWaitingList.unsubscribe.success.description":
    "You have been successfully unsubscribed.{br}You will no longer receive notifications about practices in this postal code area.",
  "postalCodeWaitingList.unsubscribe.error.title": "Something went wrong",
  "postalCodeWaitingList.unsubscribe.error.description":
    "We could not unsubscribe you. Try again by clicking the button or try again later.",

  careGroups: "Care groups",
  careGroup: "Care group",
  "careGroup.settings": "Care group settings",
  "careGroup.name": "Name",
  "careGroup.name.placeholder": "Fill in name",
  "careGroup.slug": "Slug",
  "careGroup.slug.placeholder": "Fill in the domain name text",
  "careGroup.slug.fullAddress": "The web address is: ",
  "careGroup.logo": "Logo",
  "careGroup.select.placeholder": "Choose care group",
  "careGroup.create.success": "Added care group {name}",
  "careGroup.page.general": "General",
  "careGroup.page.serviceArea": "Postal code area",
  "careGroup.serviceArea.description":
    "On the ‘Practice insights’ page, the care group can see a range of data: the practices affiliated with the care group, as well as general-practice insights for the area where it is active.",
  "careGroup.serviceArea.allowed.title": "Within which postal code area <u>may</u> the care group view data?",
  "careGroup.serviceArea.allowed.description":
    "This is configured from within MijnPraktijk, and is roughly the area you serve.",
  "careGroup.serviceArea.filter.title": "Within which postal code area does the care group <u>want</u> to view data?",
  "careGroup.serviceArea.filter.description":
    "This is configured by the care group, and is exactly the area you serve. It is a filter on the area above.",
  "careGroup.serviceArea.edit": "Edit",
  "careGroup.serviceArea.pc4.range": "From {from} to {to}",
  "careGroup.serviceArea.pc6.replacedOnSave":
    "This area is configured as a postcode-6 regular expression. Saving postcode-4 areas will replace it.",
  "careGroup.serviceArea.empty": "Not set yet",
  "careGroup.phoneNumber": "Phone number",
  "careGroup.phoneNumber.placeholder": "Enter phone number",
  "careGroup.email": "Email address",
  "careGroup.email.placeholder": "Enter email address",
  "careGroup.externalWebsiteUrl": "Contact page URL",
  "careGroup.externalWebsiteUrl.placeholder": "Enter contact page URL",
  "careGroup.externalDomains.title": "Where the landing page appears",
  "careGroup.externalDomains.body":
    "Show this care group's landing page on one of our other web addresses. Visitors see the page there and aren't redirected to mijnpraktijk.nl.",
  "careGroup.externalDomains.placeholder": "regio.{domain1}, regio.{domain2}",
  "careGroup.externalDomains.info":
    "Only subdomains of {domain1} and {domain2}. Separate multiple domains with a comma.",
  "careGroup.externalDomains.validationInvalid": "This is not a valid hostname",
  "careGroup.page.logo": "Logo",
  "careGroup.page.theme": "Appearance",
  "careGroup.page.theme.intro":
    "Style the public care group page. Changes are immediately displayed in the preview at the bottom.",
  "careGroup.theme.title": "Page title",
  "careGroup.theme.title.placeholder": "E.g. Welcome to your care group",
  // ICU escaping: `''` is a literal quote, `'{name}'` a literal {name} — renders as '{name}'
  "careGroup.theme.title.info":
    "The title of the public search page. Use '''{name}''' to insert the care group's name.",
  "careGroup.theme.description": "Description",
  "careGroup.theme.accentColor": "Accent color",
  "careGroup.theme.accentColor.info": "Headings on the public page.",
  "careGroup.theme.backgroundColor": "Background color",
  "careGroup.theme.backgroundColor.info": "Background of the page.",
  "careGroup.theme.buttonBackgroundColor": "Button color",
  "careGroup.theme.buttonBackgroundColor.info": "Background of buttons.",
  "careGroup.theme.buttonTextColor": "Button text color",
  "careGroup.theme.buttonTextColor.info": "Text on buttons.",
  "careGroup.theme.illustration": "Illustration",
  "color.useDefault": "Use default",
  "careGroup.theme.viewPublicPage": "View the public page",
  "validation.hexColor.invalid": "This is not a valid color code (e.g. #3E79F7)",
  "careGroup.page.authorizations": "Colleagues",
  "careGroup.page.privacyStatement": "Privacy statement",
  "careGroup.page.privacyStatement.nl": "Privacy statement - Dutch",
  "careGroup.page.privacyStatement.en": "Privacy statement - English",
  "careGroup.page.privacyStatement.placeholder": "Add your privacy statement here ...",
  "careGroup.dashboard.tab.region": "By area",
  "careGroup.dashboard.tab.practice": "Per practice",
  "careGroup.dashboard.picker.title": "Choose a care group",
  "careGroup.dashboard.picker.description":
    "You have access to multiple care groups. Click on the care group you are interested in.",
  "careGroup.dashboard.empty.title": "No data available yet",
  "careGroup.dashboard.empty.description": "The dashboard for this care group isn't ready yet. Check back later.",
  "careGroup.dashboard.builtAt": "Data collected on {date}",
  "careGroup.dashboard.rebuild.action": "Refresh data",
  "careGroup.dashboard.rebuild.success": "This care group's dashboard data has been rebuilt.",
  "careGroup.dashboard.rebuild.alreadyRunning": "A dashboard build is already running. Try again in a few minutes.",
  "careGroup.dashboard.rebuild.error": "Refreshing the dashboard data failed.",
  "careGroup.dashboard.practiceCoverage.title": "Postcode area of our practices",
  "careGroup.dashboard.practiceCoverage.description": "Hover over an area to see the practice name and status.",
  "careGroup.dashboard.practiceCoverage.practices.title": "Practices",
  "careGroup.dashboard.practiceCoverage.tooltip.open": "Open for registration",
  "careGroup.dashboard.practiceCoverage.tooltip.closed": "Closed for registration",
  "careGroup.dashboard.practiceCoverage.section.showAll": "Show all",
  "careGroup.dashboard.practiceCoverage.section.showLess": "Show less",
  "careGroup.dashboard.practiceCoverage.noArea": "No postcode area configured",
  "careGroup.dashboard.practiceCoverage.noArea.explanation":
    "This practice has no working postcode area configured, so it cannot be shown on the map.",
  "careGroup.dashboard.practiceCoverage.display.title": "Map display",
  "careGroup.dashboard.practiceCoverage.display.practices": "Practice locations",
  "careGroup.dashboard.widget.error": "This widget couldn't be loaded.",
  "careGroup.dashboard.widget.staleData":
    "The data for this overview isn't available yet. It will be rebuilt overnight.",
  "careGroup.dashboard.filter.area.label": "Area",
  "careGroup.dashboard.filter.area.empty": "No areas available",
  "careGroup.dashboard.filter.dateRange.label": "Period",
  "careGroup.dashboard.registrationRates.title": "Registrations per practice, over time",
  "careGroup.dashboard.registrationRates.column.practice": "Practice",
  "careGroup.dashboard.registrationRates.column.newRegistrations": "Registrations",
  "careGroup.dashboard.registrationRates.column.accepted": "Accepted or completed",
  "careGroup.dashboard.registrationRates.column.averageLeadTime": "Lead time (average)",
  "careGroup.dashboard.registrationRates.column.averageLeadTime.tooltip":
    "The average number of days between creating a registration and accepting it. Registrations that have not been accepted yet are not counted.",
  "careGroup.dashboard.registrationRates.column.medianLeadTime": "Lead time (median)",
  "careGroup.dashboard.registrationRates.column.medianLeadTime.tooltip":
    "The median number of days between creating and accepting a registration. Half of the registrations are accepted within this number of days, the other half take longer. Registrations that have not been accepted yet are not counted.",
  "careGroup.dashboard.registrationRates.totals.allPractices": "All practices",
  "careGroup.dashboard.registrationRates.totals.footnote":
    "Average and median are calculated over all registrations together - every registration carries equal weight. It is not the average of the per-practice averages.",
  "careGroup.dashboard.practiceStatus.title": "Practice status overview",
  "careGroup.dashboard.practiceStatus.column.practice": "Name",
  "careGroup.dashboard.practiceStatus.column.status": "Status",
  "careGroup.dashboard.practiceStatus.column.closeReason": "Closure reason",
  "careGroup.dashboard.practiceStatus.column.remainingCapacity": "Closes after X registrations",
  "careGroup.dashboard.practiceStatus.open": "Open",
  "careGroup.dashboard.practiceStatus.closed": "Closed",
  "careGroup.dashboard.practiceStatus.capacity.unknown": "Unknown",
  "careGroup.dashboard.practiceStatus.capacitySum.label": "Sum of 'Closes after X registrations'",
  "careGroup.dashboard.practiceStatus.capacitySum.basis":
    "Added up over practices that set a maximum ({counted} of {open} open practices in this overview)",
  "careGroup.dashboard.practiceStatus.capacitySum.disclaimer":
    "Do not use this number to estimate a region's intake capacity.",
  "careGroup.dashboard.practiceStatus.capacitySum.disclaimer.notMinimum":
    "<b>The number is not a minimum:</b> Practices that set no maximum are open without limit, but are not included in this sum ({withoutMax} of {open} open practices in this overview)",
  "careGroup.dashboard.practiceStatus.capacitySum.disclaimer.notMaximum":
    "<b>The number is not a maximum either:</b> Some practices enter a high number to show that they are open, without actually being able to take on that many patients.",
  "careGroup.dashboard.practiceStatusHeatmap.title": "Open & closed practices, over time",
  "careGroup.dashboard.practiceStatusHeatmap.column.practice": "Name",
  "careGroup.dashboard.practiceStatusHeatmap.state.open": "Open",
  "careGroup.dashboard.practiceStatusHeatmap.state.closed": "Closed",
  "careGroup.dashboard.practiceStatusHeatmap.state.unknown": "No data",
  "careGroup.dashboard.practiceStatusLineGraph.title": "Open & closed practices over time",
  "careGroup.dashboard.practiceStatusLineGraph.series.open": "Open practices",
  "careGroup.dashboard.practiceStatusLineGraph.series.closed": "Closed practices",
  "careGroup.dashboard.practiceStatusLineGraph.tooltip.open": "{count} open practices",
  "careGroup.dashboard.practiceStatusLineGraph.tooltip.closed": "{count} closed practices",
  "careGroup.dashboard.closureReasons.preTitle": "Answers from your practices to the question:",
  "careGroup.dashboard.closureReasons.title": "What do you need to reopen?",
  "careGroup.dashboard.closureReasons.description": "Practices could give multiple reasons.",
  "careGroup.dashboard.closureReasons.category.housing": "Housing",
  "careGroup.dashboard.closureReasons.category.gp_staff": "GP staff",
  "careGroup.dashboard.closureReasons.category.assistant_staff": "Assistant staff",
  "careGroup.dashboard.closureReasons.category.other": "Other",
  "careGroup.dashboard.notificationWaitingListHeatmap.title": "Number of people looking for a GP",
  "careGroup.dashboard.notificationWaitingListHeatmap.total.label": "Number of people looking for a GP",
  "careGroup.dashboard.notificationWaitingListHeatmap.total.description":
    "The total number of people currently signed up for the notification service (including family members).",
  "careGroup.dashboard.notificationWaitingListHeatmap.tooltip":
    "{postalCode}: {count, plural, one {# waiting} other {# waiting}}",
  "careGroup.dashboard.notificationWaitingListHeatmap.legend.count":
    "{count, plural, one {# waiting} other {# waiting}}",
  "careGroup.filter.label": "Select care group",
  "careGroup.filter.placeholder": "Choose care group from the list",
  careGroupIds: "Care group",

  "appointment.reason": "Reason for appointment",

  "symptoms.description":
    "Briefly describe the symptoms you are experiencing, the question you have or any other information that may be useful.",
  "symptoms.description.platoMedical":
    "Briefly describe any other complaints or questions you have that were not asked in the questionnaire.",
  "symptoms.description.platoAdministrative":
    "Briefly describe other details that were not asked in the questionnaire.",

  "validation.required.error": "This field is required",
  "validation.maxLength.error": "The text cannot be longer than {maxLength} characters",

  "submit.success": "Saved successfully",
  "submit.deleted": "Successfully deleted",
  "submit.error": "An error occurred, please try again...",
  "submit.error.fields": "Please enter all required fields",

  "form.select": "Select",
  "form.select.users.placeholder": "Select colleagues",

  emailAddress: "Email address",
  "emailAddress.validationRequired": "Please enter an email address",
  "emailAddress.validationInvalid": "Please enter a valid email address",
  "emailAddress.blockedDomain":
    "This email address uses a domain (@zorgmail.nl) that cannot receive emails from our system. Please use a different email address.",
  "emailAddress.validationNonAscii":
    "This email address contains special characters (such as accents) that we can't deliver to. Please use an address without accented characters.",

  "reloadWarning.confirm": "There are unsaved changes. Are you sure want to discard these changes?",

  "days.monday": "Monday",
  "days.tuesday": "Tuesday",
  "days.wednesday": "Wednesday",
  "days.thursday": "Thursday",
  "days.friday": "Friday",
  "days.saturday": "Saturday",
  "days.sunday": "Sunday",
  "days.holiday": "Holiday",
  "days.short.monday": "Mon",
  "days.short.tuesday": "Tue",
  "days.short.wednesday": "Wed",
  "days.short.thursday": "Thu",
  "days.short.friday": "Fri",
  "days.short.saturday": "Sat",
  "days.short.sunday": "Sun",
  "days.short.holiday": "Holiday",

  "calendar.previousMonth": "Previous month",
  "calendar.nextMonth": "Next month",
  "calendar.monday": "Mo",
  "calendar.tuesday": "Tu",
  "calendar.wednesday": "We",
  "calendar.thursday": "Th",
  "calendar.friday": "Fr",
  "calendar.saturday": "Sa",
  "calendar.sunday": "Su",

  "dateDifference.year": "Year",
  "dateDifference.years": "Years",
  "dateDifference.day": "Day",
  "dateDifference.days": "Days",
  "dateDifference.hour": "Hour",
  "dateDifference.hours": "Hours",
  "dateDifference.minute": "Minute",
  "dateDifference.minutes": "Minutes",
  "dateDifference.second": "Second",
  "dateDifference.seconds": "Seconds",
  "dateDifference.now": "Now",

  "dateRelative.weeks": "{value} {value, plural, =1 {week} other {weeks}} ago",
  "dateRelative.weeks.future": "In {value} {value, plural, =1 {week} other {weeks}}",
  "dateRelative.days": "{value} {value, plural, =1 {day} other {days}} ago",
  "dateRelative.days.future": "In {value} {value, plural, =1 {day} other {days}}",
  "dateRelative.hours": "{value} {value, plural, =1 {hour} other {hours}} ago",
  "dateRelative.hours.future": "In {value} {value, plural, =1 {hour} other {hours}}",
  "dateRelative.minutes": "{value} {value, plural, =1 {minute} other {minutes}} ago",
  "dateRelative.minutes.future": "In {value} {value, plural, =1 {minute} other {minutes}}",

  "timePicker.duration.minutes": "{minutes} min",
  "timePicker.duration.hours": "{hours, number} hr",

  "upload.logo": "Upload logo",
  "upload.logo.info": "A wide logo works best.",
  "upload.spreadsheetDetails": ".CSV or .XLSX up to 10MB",
  "upload.imageDetails": "PNG, JPG up to 10MB",
  "upload.dropzone": "Select a file or drag one here",
  "upload.required": "It is required to add a file.",
  "upload.action.1": "Select a file",
  "upload.action.2": "or drag one here",
  "upload.scanNotSuccessful": "Not all files are successfully scanned on viruses.",
  "upload.notAllowed": "This file format is not permitted for security reasons.",
  "upload.tooLarge": "This file is too large. The maximum size is {maxMb}MB.",
  "upload.threatsFound": "A possible virus has been found in this file.",
  "upload.noThreatsFound": "No viruses are found in this file.",
  "upload.unknown": "This file couldn't be scanned on viruses.",
  "upload.texteditor.issuesFound": "Issues have been found in the uploaded files. Remove these to send your message",
  "upload.texteditor.inlineImageSize":
    "The maximum size of an inline image is 8MB. For larger files you can use the file upload functionality.",

  "labels.placeholder": "Select labels",
  "labels.unavailable": "No labels available",

  appointments: "Appointments",
  "appointments.all": "All appointments",
  "appointments.upcoming": "Upcoming appointments",
  "appointments.previous": "Previous appointments",
  "appointments.type": "Appointment type",
  "appointments.type.validate": "Please select an appointment type",
  "appointments.his": "Schedule in HIS",
  "appointments.his.description": "Limited to available data, but appointment is automatically created in HIS",
  "appointments.manual": "Manual",
  "appointments.manual.description": "All dates possible, but will not be put in HIS",
  "appointments.note":
    "Both options will send a message and reminder to the patient. And for video appointments, a link for the meeting.",
  "appointments.appointment.type.validate": "Please select an appointment type",
  "appointments.agenda.placeholder": "Select agenda",
  "appointments.agenda.validate": "Please select an agenda",
  "appointments.agenda.noneConfigured":
    "This practice has no agendas set up yet. Without an agenda you cannot book an appointment. An administrator can add agendas under {settings} → {group} → {page}.",
  "appointments.appointmentType.placeholder": "Select type",
  "appointments.patient.validate": "Please select a patient",
  "appointments.date.placeholder": "Select a date",
  "appointments.date.validate": "Please select a date",
  "appointments.practice.placeholder": "Select practice",
  "appointments.time.placeholder": "Select time",
  "appointments.slots.loadFailed":
    "The available times could not be loaded. Try again later or book the appointment manually.",
  "appointments.time.from.label": "Time (from)",
  "appointments.time.to.label": "Time (to)",
  "appointments.time.notBefore": "Starting time must be before end time",
  "appointments.time.validate": "Please select a time",
  "appointments.remarks": "Optional remarks",
  "appointments.remarks.placeholder": "Remarks for in HIS (not for patient)",
  "appointments.past": "Past appointments",
  "appointment.from": "Appointment from",
  "appointment.to": "Appointment to",
  appointment: "Appointment",
  appointmentLower: "appointment",
  "appointment.remove.modal.title": "Delete appointment",
  "appointment.remove.modal.content": "Delete appointment with {patientName}?",
  "appointment.remove.modal.deletedInHis": "This appointment will also be deleted in the HIS.",
  "appointment.remove.modal.confirm": "Delete",
  "appointment.remove.success": "Appointment is deleted",
  "appointment.delete": "Delete",
  "appointment.details": "Appointment details",
  "appointment.details.agenda": "Agenda",
  "appointment.details.type": "Type",
  "appointment.details.symptoms": "Anamnesis / symptoms",
  "appointment.details.videoLink": "Video link",
  "appointment.type.video": "Video",
  "appointment.type.faceToFace": "Face-to-face",
  "appointment.type.phone": "Phone",
  "appointment.calendar.label": "Add to my calendar",
  "appointment.calendar.name": "{type} appointment at {agenda}",
  "appointment.dateTimeLength": "Date, Time & Length",
  "appointment.date": "Appointment date",
  "appointment.econsult.open": "Open consult",
  "appointment.errors.duplicate": "Cannot change the appointment, get in touch with the practice to change.",

  "econsults.status.label": "E-consult status",
  "econsults.status.select": "Select consult status",
  econsult: "E-consult",
  "econsult.type.video": "Video appointment",
  "econsult.create": "Create e-consult",
  "econsult.create.success": "New e-consult created",
  "econsult.list.type.header": "Type",
  "econsult.list.type.default": "E-consult",
  "econsult.list.type.appointment": "Appointment",
  "econsult.list.type.walkIn": "Digital walk-in consult",
  "econsult.list.type.stayHomeAdvice": "Home care advice",
  "econsult.list.type.outOfScope": "Not applicable",
  "econsult.list.type.phoneConsult": "Phone call",
  "econsult.list.type.open": "Open",
  "econsult.list.type.closed": "Closed",
  "econsult.list.info.header": "Info",
  "econsult.phoneConsult.patientBar.caption": "Follow-up on a phone callback",
  "econsult.closed": "The e-consult was closed at {date} by {closedByName}. It’s no longer possible to respond.",
  "econsult.startVideo": "Start video",
  "econsult.stopVideo": "Stop video",
  "econsult.patientAvailable": "Patient is available",
  "econsult.patientNotAvailable": "Patient not available",
  "econsult.appointmentBelongsTo": "Belongs to",
  "econsult.attachments": "Attachments",
  "econsult.attachFiles": "Attach files",
  "econsult.link.appointment": "New appointment link",
  "econsult.link.econsult": "New e-consult link",
  "econsult.responseTemplates.resetFilter": "Reset filter",
  "econsult.charge.label": "Declaration type",
  "econsult.charge.type.untilFiveMinutes": "Less than 5 minutes",
  "econsult.charge.type.untilTwentyMinutes": "Less than 20 minutes",
  "econsult.charge.type.moreThanTwentyMinutes": "More than 20 minutes",
  "econsult.charge.type.none": "No declaration",
  "econsult.close.open.text": "Once closed, it will no longer be possible to respond or start the video.",
  "econsult.close.open.button": "Close consult",
  "econsult.journalItem": "Journal item",
  "econsult.journalItem.title": "HIS journal items",
  "econsult.journalItem.headerNew": "New journal item",
  "econsult.journalItem.sentToHisBtn": "Send to HIS",
  "econsult.journalItem.contactType.label": "Contact type",
  "econsult.journalItem.contactType.option.C": "C: consult",
  "econsult.journalItem.contactType.option.EC": "EC: e-consult",
  "econsult.journalItem.contactType.option.VC": "VC: video consult",
  "econsult.journalItem.contactType.option.T": "T: phone consult",
  "econsult.journalItem.contactType.placeholder": "No types found",
  "econsult.journalItem.copyTooltip": "Copy to {label}-Rule",
  "econsult.journalItem.autoMedic.created": "Created in AutoMedic, but not yet processed",
  "econsult.journalItem.autoMedic.success": "Saved in HIS via AutoMedic",
  "econsult.journalItem.autoMedic.failed": "Error processing via AutoMedic",
  "econsult.journalItem.validation.empty": "Fill in at least one SOEP field.",
  "econsult.searchUsefulLink": "Add Thuisarts link",
  "econsult.message": "Message",
  "econsult.messages": "Messages",
  "econsult.message.success": "Message sent",
  "econsult.message.input": "Please enter a message",
  "econsult.reply.validation.empty": "Enter a message or add an attachment.",
  "econsult.message.patient": "Please select a patient",
  "econsult.message.createTemplate": "Save message as response template",
  "econsult.subjectPlaceHolder": "Subject:",
  "econsult.responseTextarea.placeholder": "Type a message to the patient",
  "econsult.searchResponseSuggestion": "Response suggestion",
  "econsult.responseTemplate": "Response template",
  "econsult.responseTemplate.button": "Response template",
  "econsult.responseTemplate.edit.title": "Edit response template",
  "econsult.responseTemplate.add.title": "Create response template",
  "econsult.responseTemplate.sub.header": "All response templates are available in the",
  "econsult.responseTemplate.settings.link": "practice settings.",
  "econsult.responseTemplateEmpty": "Go to practice config to configure these.",
  "econsult.otherUserTyping": "is typing a message",
  "econsult.close": "Close consult",
  "econsult.close.messageContent": "Send the message to the patient first. Then you can close the consult.",
  "econsult.close.futureAppointment":
    "A future appointment is linked to this. Closing it now would make the appointment impossible to open for the patient. Remove the linked appointment first.",
  "econsult.close.icpcRequired": "ICPC code is required",
  "econsult.close.success": "E-consult is closed",
  "econsult.send.patient": "Send to patient",
  "econsult.send.withoutHis": "Close without message to HIS",
  "econsult.send.his": "Send to HIS",
  "econsult.videoFrame.move": "Click here to move",
  "econsult.reopen": "Reopen e-consult",
  "econsult.patientIsOnline": "Patient is online",
  "econsult.attachmentPreview": "Attachment preview",
  "econsult.other.stayHomeAdvice": "Home care advice",
  "econsult.other.outOfScope": "Not applicable",
  "econsult.other.close": "Close",
  "econsult.other.reopen": "Reopen",
  "econsult.other.shownToPatient": "Shown to patient",
  "econsult.other.safetyNet": "Please note:",
  "econsult.triage.header": "{title}: ",
  "econsult.triage.outcome": "Outcome:",
  "econsult.triage.outcome.bodyPart": "Body part:",

  "plato.bodyPart.Abdomen": "Abdomen",
  "plato.bodyPart.Arms": "Arms",
  "plato.bodyPart.Back": "Back",
  "plato.bodyPart.Buttocks": "Buttocks",
  "plato.bodyPart.Chest": "Chest",
  "plato.bodyPart.Feet": "Feet",
  "plato.bodyPart.Genital": "Genital",
  "plato.bodyPart.Hands": "Hands",
  "plato.bodyPart.Head": "Head",
  "plato.bodyPart.Legs": "Legs",
  "plato.bodyPart.Neck": "Neck",
  "plato.bodyPart.Throat": "Throat",
  "plato.bodyPart.Other": "I don't know",

  "plato.questionnaire.ABCDE": "ABCDE",
  "plato.questionnaire.ADHD": "Concentration problems (ADHD/ADD)",
  "plato.questionnaire.ADHDChild": "Concentration problems (ADHD/ADD) for child",
  "plato.questionnaire.AbdominalPainAdult": "Abdominal pain adult",
  "plato.questionnaire.AbdominalPainChild": "Abdominal pain child",
  "plato.questionnaire.Addiction": "Addiction",
  "plato.questionnaire.AgeLimit": "Age limit",
  "plato.questionnaire.AllergicReaction": "Hay fever",
  "plato.questionnaire.AllergyFood": "Food allergy (intolerance)",
  "plato.questionnaire.AnkleFoot": "Ankle, foot or toe complaint",
  "plato.questionnaire.Anxiety": "Anxiety",
  "plato.questionnaire.ArmShoulder": "Arm complaints (including shoulder)",
  "plato.questionnaire.ArmShoulderChild": "Arm complaints (including shoulder) for child",
  "plato.questionnaire.BackPain": "Back pain",
  "plato.questionnaire.BedWettingChild": "Bed wetting child",
  "plato.questionnaire.BirthControl": "Birth control",
  "plato.questionnaire.BloodLossStool": "Blood loss stool",
  "plato.questionnaire.Breast": "Breast complaints",
  "plato.questionnaire.Breastfeeding": "Breastfeeding",
  "plato.questionnaire.BurnWound": "Burn wound",
  "plato.questionnaire.Buttocks": "Buttocks",
  "plato.questionnaire.CancelAppointment": "I want to cancel an appointment",
  "plato.questionnaire.CardiacImplant": "ICD (including pacemaker)",
  "plato.questionnaire.ChangePersonalInformation": "I want to report a change in my address, email, or phone number.",
  "plato.questionnaire.ChestPain": "Chest pain",
  "plato.questionnaire.Confusion": "Confusion",
  "plato.questionnaire.Constipation": "Constipation",
  "plato.questionnaire.Coughing": "Coughing",
  "plato.questionnaire.CryingBaby": "Crying baby",
  "plato.questionnaire.Dental": "Dental issues",
  "plato.questionnaire.Diabetes": "Diabetes",
  "plato.questionnaire.Diarrhea": "Diarrhea",
  "plato.questionnaire.Dizziness": "Dizziness",
  "plato.questionnaire.Drain": "Drain / feeding tube / catheter",
  "plato.questionnaire.Ear": "Ear complaints",
  "plato.questionnaire.Fainting": "Fainting or blackout",
  "plato.questionnaire.FeverAdult": "Fever Adult",
  "plato.questionnaire.FeverChild": "Fever Child",
  "plato.questionnaire.ForeignObject": "Foreign object",
  "plato.questionnaire.GeneralComplaint": "General complaint",
  "plato.questionnaire.GeneralComplaintPsychological": "General psychological complaint",
  "plato.questionnaire.Grief": "Grief",
  "plato.questionnaire.HandWrist": "Hand and wrist complaints",
  "plato.questionnaire.Headache": "Headache",
  "plato.questionnaire.HearingLossTinnitus": "Hearing loss Tinnitus",
  "plato.questionnaire.Hypertension": "Hypertension",
  "plato.questionnaire.InguinalSwelling": "Inguinal swelling",
  "plato.questionnaire.InsectSting": "Insect sting/ tick bite",
  "plato.questionnaire.Insomnia": "Sleep problems and sleeping pills",
  "plato.questionnaire.Intoxication": "Intoxication",
  "plato.questionnaire.LabResult": "I have a question about a lab result.",
  "plato.questionnaire.LegHip": "Leg complaints (including Hip)",
  "plato.questionnaire.MalaiseAdult": "Malaise adult",
  "plato.questionnaire.MedicationCosts": "I have a question regarding medication costs",
  "plato.questionnaire.MedicationDoesntWork": "My medication doesn't work",
  "plato.questionnaire.MedicationExplanation": "I want instructions for medication usage",
  "plato.questionnaire.MedicationStatement": "I need a medicine certificate because of a trip abroad",
  "plato.questionnaire.Menopause": "Menopause",
  "plato.questionnaire.MenstrualCycle": "Menstrual cycle",
  "plato.questionnaire.Mental": "Psychological problems",
  "plato.questionnaire.NauseaVomitingStomach": "Vomiting",
  "plato.questionnaire.Neck": "Neck complaints",
  "plato.questionnaire.Nose": "Nose complaints",
  "plato.questionnaire.NoseBleeding": "Nose Bleeding",
  "plato.questionnaire.Obesity": "Overweight",
  "plato.questionnaire.OtherComplaint": "My complaint is not listed",
  "plato.questionnaire.Pain": "Pain",
  "plato.questionnaire.Palpitations": "Palpitations",
  "plato.questionnaire.Paralysis": "Paralysed",
  "plato.questionnaire.PopulationSurvey": "PopulationSurvey",
  "plato.questionnaire.Pregnancy": "Pregnancy",
  "plato.questionnaire.PregnancyAbdominalPain": "Pregnancy (abdominal pain)",
  "plato.questionnaire.PregnancyChildBirth": "Pregnancy (child birth)",
  "plato.questionnaire.PregnancyConcerns": "Pregnancy (concerns)",
  "plato.questionnaire.PregnancyFluidLoss": "Pregnancy (fluid loss)",
  "plato.questionnaire.PregnancyOther": "Pregnancy (other)",
  "plato.questionnaire.PregnancyUrinaryTract": "Pregnancy (urinary tract)",
  "plato.questionnaire.PregnancyVaginalBloodLoss": "Pregnancy (vaginal blood loss)",
  "plato.questionnaire.Procedure": "Procedure",
  "plato.questionnaire.Rectal": "Rectal complaints",
  "plato.questionnaire.RedEyePainEye": "Red eye or eye pain",
  "plato.questionnaire.Referral": "Referral",
  "plato.questionnaire.ReproductiveOrganFemale": "Vaginal problems",
  "plato.questionnaire.ReproductiveOrganMale": "Reproductive organ male",
  "plato.questionnaire.RequestMedicalInformation": "I want to request medical information of my medical record",
  "plato.questionnaire.RescheduleAppointment": "I want to reschedule an appointment",
  "plato.questionnaire.Seizure": "Seizure",
  "plato.questionnaire.SexualAbuse": "Sexual abuse",
  "plato.questionnaire.SexualComplaint": "Sexual complaints",
  "plato.questionnaire.ShortnessOfBreath": "Shortness of breath",
  "plato.questionnaire.ShortnessOfBreathChild": "Shortness of breath (child)",
  "plato.questionnaire.Sick": "Sickness",
  "plato.questionnaire.SideEffect": "I want to report a side-effect of medication",
  "plato.questionnaire.Skin": "Skin",
  "plato.questionnaire.SkinChild": "Skin (child)",
  "plato.questionnaire.Smoking": "Quit smoking or vaping",
  "plato.questionnaire.SOA": "SOA",
  "plato.questionnaire.STD": "STD",
  "plato.questionnaire.Subfertility": "Fertility problems (difficulty conceiving)",
  "plato.questionnaire.Suicidal": "Suicidal",
  "plato.questionnaire.Testing": "Test Follow-up Type+Role+Urgency",
  "plato.questionnaire.Throat": "Throat",
  "plato.questionnaire.ThroatChild": "Throat (child)",
  "plato.questionnaire.TraumaAbdomen": "Abdominal trauma",
  "plato.questionnaire.TraumaArm": "Injury arm",
  "plato.questionnaire.TraumaArmLeg": "Injury arm/ leg",
  "plato.questionnaire.TraumaBack": "Injury back",
  "plato.questionnaire.TraumaChest": "Injury chest",
  "plato.questionnaire.TraumaEye": "Injury eye",
  "plato.questionnaire.TraumaFace": "Injury face",
  "plato.questionnaire.TraumaHead": "Injury head",
  "plato.questionnaire.TraumaLeg": "Injury leg",
  "plato.questionnaire.TraumaMulti": "Injury (multiple)",
  "plato.questionnaire.TraumaNeck": "Injury neck",
  "plato.questionnaire.TraumaNose": "Injury nose",
  "plato.questionnaire.TraumaThorax": "Injury thorax",
  "plato.questionnaire.Tremor": "Tremors",
  "plato.questionnaire.UnsubscribePractice": "I want to unsubscribe to this practice",
  "plato.questionnaire.UnusualBehavior": "Unusual behavior",
  "plato.questionnaire.UrinaryTract": "Urinary tract problems",
  "plato.questionnaire.Vaccination": "I have a question about vaccinations",
  "plato.questionnaire.VaginalBloodLoss": "Vaginal blood loss",
  "plato.questionnaire.Varices": "Varices",
  "plato.questionnaire.WeightLoss": "Unwanted weight loss",
  "plato.questionnaire.Wound": "Wound",

  "econsult.triage.outcome.urgency.h0": "Not applicable",
  "econsult.triage.outcome.urgency.h1": "Call the emergency line",
  "econsult.triage.outcome.urgency.h2": "Call the practice",
  "econsult.triage.outcome.urgency.h3": "Same day",
  "econsult.triage.outcome.urgency.h4": "Within 24 hours",
  "econsult.triage.outcome.urgency.h5": "Within 2 to 3 days",
  "econsult.triage.outcome.urgency.h6": "Within 3 to 5 days",
  "econsult.triage.outcome.urgency.h7": "Within 5 to 14 days",
  "econsult.triage.outcome.urgency.h8": "Stay-home advice",

  "econsult.triage.outcome.isCustomized":
    "This follow-up differs from the MijnPraktijk default, as someone from {practice} changed this. Things that could have been changed are the type of health care provider, and type of appointment.",
  "econsult.triage.outcome.explanation": "Adjusted:",
  "econsult.triage.outcome.explanation.customized":
    'The default MijnPraktijk outcome for these answers is "{original}". But within {practice} this has been changed to "{changed}"',
  "triage.count.tooltip": "Count in past 30 days",

  "triage.followUp.FaceToFace": "Make appointment",
  "triage.followUp.Video": "Video appointment",
  "triage.followUp.Phone": "By phone",
  "triage.followUp.Econsult": "E-consult",
  "triage.followUp.WalkInConsult": "Walk in consult",
  "triage.followUp.CALL_PRACTICE": "Call the practice",
  "triage.followUp.CALL_EMERGENCY_LINE": "Call the emergency line",
  "triage.followUp.STAY_HOME": "Stay home",

  "triage.followUpDuration.Default": "Default",
  "triage.followUpDuration.Long": "Long",

  "triage.customOutcome.followUpType": "Follow-up type",
  "triage.customOutcome.followUpType.placeholder": "Select follow-up type",
  "triage.customOutcome.followUpType.dropdown": "Select follow-up types",
  "triage.customOutcome.followUpDuration": "Follow-up duration",
  "triage.customOutcome.followUpDuration.placeholder": "Select duration",
  "triage.customOutcome.role": "Role",
  "triage.customOutcome.role.placeholder": "Select new role",
  "triage.customOutcome.role.dropdown": "Select roles",
  "triage.customOutcome.role.required": "Role is required",
  "triage.customOutcome.lastUpdate": "Last update",
  "triage.customOutcome.disabled": "It is only possible to change the outcome of H4 to H8",
  "triage.customOutcome.adviceText.nl-NL": "Stay home advice (Dutch text)",
  "triage.customOutcome.adviceText.en-GB": "Stay home advice (English text)",
  "triage.customOutcome.create.success": "Follow-up and role saved",
  "triage.customOutcome.customized": "Customized follow-up",
  "triage.customOutcome.reset": "Reset",

  "medication.filter.status": "Select status",
  "medication.undecided": "Unprocessed requests",
  "medication.approved": "Approved requests",
  "medication.rejected": "Rejected requests",
  "medication.all": "All requests",
  "medication.request": "Medication request",
  "medication.requestInfo": "Requested medication",
  medication: "Medication request",
  medicationRequest: "Medication Request",
  "medication.drug": "Drug name",
  "medication.strength": "Strength (e.g. mg/ml)",
  "medication.dosage": "Dosage (e.g. 2 per day)",
  "medication.amount": "Amount needed (e.g. 2 weeks)",
  "medication.comment": "Remark",

  patient: "Patient",
  patientAccount: "Patient Account",
  registrationRequest: "Registration Request",
  "patients.all": "All patients",
  "patients.new": "New registrations",
  "patients.rejected": "Rejected registrations",
  "patients.accepted": "Accepted registrations",
  "patients.completed": "Completed registrations",
  "patients.swaplist": "Patients exchange",
  "patients.edit": "Edit patient",
  "patients.registrationDate": "Registration date",
  "patients.removedDate": "Removal date",
  "patients.status": "Status",
  "patients.select.status": "Select status",
  "patients.statusActive": "Active",
  "patients.statusRejected": "Rejected",
  "patients.statusNew": "New",
  "patients.statusRemoved": "Removed",
  "patients.removedOn": "Removed on {date}",
  "patients.buttonRemove": "Remove",
  "patients.remove.modal.title": "Remove patient",
  "patients.remove.modal.content": "Are you sure you want to remove patient {patientName} from the practice?",
  "patients.remove.success": "Patient is removed",
  "patients.removeFromPractice": "Remove from practice",
  "patients.buttonReregister": "Re-register",
  "patients.reregister.modal.title": "Re-register patient",
  "patients.reregister.modal.content": "Are you sure you want to re-register patient {patientName} in the practice?",
  "patients.reregister.success": "Patient is re-registered",
  "patients.reregisterFromPractice": "Re-register to practice",
  "patients.patientNumber": "Patient number",

  "patient.tab.general": "General information",
  "patient.tab.history": "History",
  "patient.tab.registration": "Registration info",
  "patient.tab.accounts": "Accounts",
  "patient.accounts.his": "HIS",
  "patient.accounts.status": "Status",
  "patient.accounts.withDigiD": "DigiD",
  "patient.accounts.withDigiD.linked": "Linked",
  "patient.accounts.withDigiD.notLinked": "Not linked",
  "patient.accounts.patientId": "Patient ID",
  "patient.accounts.portalAccountId": "Portal account ID",
  "patient.accounts.created": "Created",
  "patients.hisCheck": "Check HIS link",
  "patient.hisCheck.known": "Patient is known in HIS with patient number {patientId}",
  "patient.hisCheck.unknown": "Patient is unknown in HIS",
  "deepLinks.openDossier": "Open dossier",
  "patient.details": "Patient details",

  "patients.upload.title": "Swap patients",
  "patients.upload.description":
    "Do you want to know which patients no longer live in your postcode area and are eligible for an exchange? Upload your patient file here, and we will help you further.",
  "patients.upload.checklist.1.title": "Select which practice you want to exchange your patients",
  "patients.upload.checklist.2.title": "Indicate from which zip codes you accept patients",
  "patients.upload.checklist.2.1": "You can do this in {settingsLink}",
  "patients.upload.checklist.2.1.link": "the settings",
  "patients.upload.checklist.2.noAccess":
    "Ask an administrator in the practice to do this in the settings, under 'Registration'",
  "patients.upload.checklist.3.title": "Upload your patient file",
  "patients.upload.checklist.3.1": "Export your entire patient file from your HIS, in CSV or Excel format. {link}",
  "patients.upload.checklist.3.1.link": "Read more",
  "patients.upload.checklist.3.2": "Upload it below (We comply with GDPR legislation)",
  "patients.upload.checklist.4.title": "Start trading",
  "patients.upload.checklist.4.1": "Receive a list of eligible patients",
  "patients.upload.checklist.4.2": "Select the patients you would like to exchange",
  "patients.upload.checklist.4.3":
    "Send them an email or letter via MijnPraktijk to inform them of available practices",
  "patients.upload.checklist.4.4": "Keep track of the status",

  "patients.upload.fileConfiguration.title": "Mapping Columns",
  "patients.upload.fileConfiguration.description":
    "Only rows with a date of birth, postcode and last name are considered. Make sure you select these in the 'destination column'.",
  "patients.upload.fileConfiguration.importedColumns": "Imported columns",
  "patients.upload.fileConfiguration.exampleData": "Examples from the imported data",
  "patients.upload.fileConfiguration.destination": "Destination column",
  "patients.upload.fileConfiguration.errors.uniqueAssignment": "Each destination column can only be selected once",

  "patients.upload.columnType.unassigned": "Unassigned",
  "patients.upload.columnType.firstName": "First name",
  "patients.upload.columnType.affix": "Affix",
  "patients.upload.columnType.lastName": "Last name",
  "patients.upload.columnType.email": "Email",
  "patients.upload.columnType.phone": "Phone",
  "patients.upload.columnType.street": "Street",
  "patients.upload.columnType.houseNumber": "House number",
  "patients.upload.columnType.houseNumberAddition": "House number addition",
  "patients.upload.columnType.postalCode": "Postcode",
  "patients.upload.columnType.city": "City",
  "patients.upload.columnType.bsn": "BSN",
  "patients.upload.columnType.gender": "Gender",
  "patients.upload.columnType.dateOfBirth": "Date of birth",

  "patients.upload.resultsoverview.patient": "Patient",
  "patients.upload.resultsoverview.place": "Current data",
  "patients.upload.resultsoverview.practices": "Possible practices",
  "patients.upload.resultsoverview.status": "Status",

  "patients.upload.fileUploaded.title": "Patient data imported",
  "patients.upload.fileUploaded.created":
    "{count, plural, one {<b>#</b> new patient has} other {<b>#</b> new patients have}} been imported",
  "patients.upload.fileUploaded.updated":
    "{count, plural, one {<b>#</b> patient record has} other {<b>#</b> patient records have}} been updated",
  "patients.upload.fileUploaded.existing":
    "{count, plural, one {<b>#</b> patient} other {<b>#</b> patients}} already existed and {count, plural, one {was} other {were}} not re-imported",
  "patients.upload.fileUploaded.submit": "Okay",

  "patients.upload.gdpr.title": "We are GDPR compliant",
  "patients.upload.gdpr.point.0":
    "As long as the patient is registered with your practice, you may store their data yourself. You may also store it with an organization that works in accordance with the General Data Protection Regulation (GDPR), and with which you have a processing agreement, such as MijnPraktijk.",
  "patients.upload.gdpr.point.1": "You can delete your data from us at any time.",
  "patients.upload.gdpr.point.2": "We use the data to facilitate patient exchange.",
  "patients.upload.gdpr.ok": "Okay",

  "patients.upload.decisionType.merge": "Merge",
  "patients.upload.decisionType.nothing": "Nothing",
  "patients.upload.decisionType.new-entry": "New entry",
  "patients.upload.columns.new": "New",
  "patients.upload.columns.existing": "Existing",
  "patients.upload.columns.action": "I want the new data...",

  "patients.upload.processedpersonalinformation.missingstreet": "Street unknown",
  "patients.upload.processedpersonalinformation.missinghousenumber": "House number unknown",
  "patients.upload.processedpersonalinformation.missingpostalcode": "Postcode unknown",
  "patients.upload.processedpersonalinformation.missingname": "Name unknown",

  "patients.upload.duplicates.breadcrumbs.1": "Patients",
  "patients.upload.duplicates.breadcrumbs.2": "Swap patients",
  "patients.upload.duplicates.breadcrumbs.3": "Upload patient file",
  "patients.upload.duplicates.question": "Is this the same person?",
  "patients.upload.duplicates.description":
    "The patients below appear to have been imported once before, but now have new data.",
  "patients.upload.duplicates.multipleMatches":
    "Several patients in the system look like this patient. Choose which one to update.",

  "patients.upload.row_number": "Row {n}",

  "patients.upload.invalidPatients.title": "These patients cannot be uploaded",
  "patients.upload.invalidPatients.summary":
    "{invalid} of {total} patients cannot be uploaded because of {errors, plural, one {# error} other {# errors}} in the file. {valid, plural, one {The other patient will be uploaded.} other {The other # patients will be uploaded.}}",
  "patients.upload.invalidPatients.summary.none":
    "None of the {total} patients can be uploaded because of {errors, plural, one {# error} other {# errors}} in the file.",
  "patients.upload.invalidPatients.question": "Do you still want to include these patients?",
  "patients.upload.invalidPatients.back": "Back",
  "patients.upload.invalidPatients.continue": "Continue anyway",

  "patients.upload.errordialog.description_line.1":
    "Check that you have selected the correct destination column everywhere",
  "patients.upload.errordialog.description_line.2":
    "Make sure each patient has at least a last name, a postcode and a date of birth",
  "patients.upload.errordialog.description_line.3": "Correct invalid values in the file and upload the file again",
  "patients.upload.errordialog.row": "Error in row",
  "patients.upload.errordialog.data": "Invalid value",
  "patients.upload.errordialog.error": "Error",
  "patients.upload.errordialog.column": "Destination column",
  "patients.upload.errordialog.columnTitle.1": "Column title in file",
  "patients.upload.errordialog.columnTitle.2": "The first value in this column",
  "patients.upload.errordialog.invalid.empty_required": "Must not be empty",
  "patients.upload.errordialog.invalid.invalid_format": "{type} is not valid",
  "patients.upload.errordialog.invalid.configuration": "{type} is required and must be selected",
  "patients.upload.errordialog.invalid.generic": "{type} is not valid",
  "duration.hours": "{count, plural, one {# hr} other {# hrs}}",
  "duration.minutes": "{count, plural, one {#m} other {#m}}",
  "duration.seconds": "{count, plural, one {#s} other {#s}}",

  "phone.call.durationTooltip": "Conversation took {duration}. Caller was in queue for {waitingTime}",
  "phone.call.commentsCount": "{count, plural, one {# comment} other {# comments}}",

  "phone.title": "Phone",
  "phone.choosePractice": "Choose a practice",
  "phone.chooseDevice": "Choose the phone you're using",
  "phone.chooseDevice.info": "Please select the phone you're using to make calls.",
  "phone.chooseDevice.add": "Device configuration",
  "phone.chooseDevice.device": "Device",
  "phone.offline":
    "The phone system is offline! MijnPraktijk administrators have been notified, and are working on a fix.",
  "phone.disconnected":
    "Phone system is not connected and is taking too long to reconnect. Please check your internet connection.",

  "phone.medicationRecordings": "Medication",
  "phone.medicationRecordings.none": "Mission complete!",
  "phone.medicationRecordings.none.info": "There are no open messages.",
  "phone.medicationRecordings.label.archived": "Completed repeat medication voicemail",
  "phone.medicationRecordings.label.unarchived": "Repeat medication voicemail",
  "phone.medicationRecordings.archived": "Completed",
  "phone.medicationRecordings.archived.info": "Completed medication requests are deleted after 30 days",
  "phone.medicationRecordings.archive": "Mark complete",
  "phone.medicationRecordings.archive.success": "Request is marked as completed",
  "phone.medicationRecordings.unarchive": "Move to incomplete",
  "phone.medicationRecordings.unarchive.success": "Request is moved to incomplete",

  "phone.metrics": "Statistics",
  "phone.metrics.dateRange": "Period",
  "phone.metrics.selectPractice": "Select a practice to view statistics",
  "phone.metrics.empty": "No data for this period",
  "phone.metrics.avg": "avg",
  "phone.metrics.incoming": "Incoming",
  "phone.metrics.answered": "Answered",
  "phone.metrics.unansweredNoRequest": "Unanswered, no request",
  "phone.metrics.callbackPlanned": "Callback planned",
  "phone.metrics.callbacks.scheduled": "Booked callbacks",
  "phone.metrics.normal": "Normal",
  "phone.metrics.consultation": "Colleague calls",
  "phone.metrics.callTypes.title": "Call types by weekday and hour",
  "phone.metrics.callbackOffered": "Callback option",
  "phone.metrics.callbackSlots": "Callback slots",
  "phone.metrics.slotsPerHour": "slots/h",
  "phone.metrics.waitingTime": "Waiting time",
  "phone.metrics.talkTime": "Talk time",
  "phone.metrics.volume.title": "Number of calls",
  "phone.metrics.times.title": "Waiting and talk time",
  "phone.metrics.consultation.title": "Colleague calls",
  "phone.metrics.emergency": "Emergency",
  "phone.metrics.medication.title": "Medication requests",
  "phone.metrics.completeThrough": "Figures up to {date}",
  "phone.metrics.emptyIncomplete": "No figures for this period yet — the figures run up to {date}",

  "phone.available": "Practice available",
  "phone.unavailable": "Practice unavailable",
  "phone.unavailable.duration": "Unavailable for: {duration}",
  "phone.unavailable.duration.change": "Click to modify time frame",
  "phone.unavailable.apply": "Set assistant to unavailable",
  "phone.unavailable.info":
    "Patients calling will hear that the assistant is currently unavailable, and will be asked to try again later. Patients currently in the queue will be disconnected!\n\nCallback appointments cannot be scheduled for the time frame, but previously scheduled appointments will be kept.",
  "phone.callList": "Call list",
  "phone.callList.info": "Select a call to accept",
  "phone.callList.empty": "Waiting for calls",
  "phone.callList.empty.info": "No incoming or scheduled calls.",
  "phone.callList.unknown": "Unknown caller",
  "phone.callList.archive": "Archive",
  "phone.callList.archive.info": "Only archived calls from the last 30 days are visible",

  "phone.incoming": "Incoming call",
  "phone.incoming.ended": "Ended call",
  "phone.incoming.ended.info": "It's not possible to connect to the caller anymore",
  "phone.incoming.ended.short": "Ended",
  "phone.incoming.endedWithoutAnswer": "Hung up before the call was answered (wait time: {waitingTime})",
  "phone.callback.completed": "Call completed",
  "phone.incoming.automaticRinging": "Calls are redirected to your device automatically",

  "phone.eventLog.showMore": "+ Expand {count} more",
  "phone.eventLog.device": "Someone via device {deviceName}",
  "phone.eventLog.outgoingCall": "Outgoing call",
  "phone.eventLog.incomingCall": "Incoming call",
  "phone.eventLog.answered": "Answered: {duration}",
  "phone.eventLog.unanswered": "Unanswered",
  "phone.eventLog.callInProgress": "Call in progress",
  "phone.eventLog.scheduledCallback": "Planned at {time}",
  "phone.eventLog.addComment": "Type a comment",

  "phone.scheduled": "Scheduled",
  "phone.scheduledAt": "Scheduled at {time}",
  "phone.connectedToMe": "On the phone",
  "phone.connectedToCallGroup": "To call group",
  "phone.connectedToColleague": "Picked up by colleague",

  "phone.reasonToCall.regular": "Patient",
  "phone.reasonToCall.consultation": "Colleague",
  "phone.reasonToCall.emergency": "Emergency",

  "phone.callFilter.show": "Show",
  "phone.callFilter.all": "All",
  "phone.callFilter.callback": "Callback",
  "phone.callFilter.queued": "Queued",

  "phone.timePast": "{time} {type, select, queue {on hold} other {overdue}}",
  "phone.timeFuture": "Call in {time}",
  "phone.caller": "Caller",
  "phone.pickup": "Pick up",
  "phone.call": "Start call",

  "phone.connectingDevices.connecting": "Connecting to {deviceName}",
  "phone.connectingDevices.connected": "Connected to {deviceName}",

  "phone.archive": "Close & archive",
  "phone.archive.disabled": "Archiving is not possible for an active call",
  "phone.unarchive": "Undo archiving",

  "phone.econsultLink.send": "Ask patient for photo",
  "phone.econsultLink.send.info":
    "Sends the patient an SMS with a link to share a photo or message without logging in. You will get a portal link to view the message.",
  "phone.econsultLink.sent": "E-consult link sent via SMS",
  "phone.econsultLink.open": "Open e-consult",

  "phone.eventLog.econsultLinkSent": "Link sent",
  "phone.eventLog.econsultPatientAuthor": "Caller",
  "phone.eventLog.econsultAttachment": "[Image]",
  "phone.eventLog.econsultAttachmentFile": "[File]",
  "phone.call.patientMessagesCount": "{count, plural, one {# message from caller} other {# messages from caller}}",

  "phone.transcription.loading": "Call is being transcribed",
  "phone.transcription.completed": "See SOEP message",
  "phone.transcription.noMessages": "No SOEP message available",
  "phone.transcription.unavailable": "Call transcription unavailable",
  "phone.transcription.sidebar.title": "Save report",
  "phone.transcription.sidebar.subTitle": "Review created report",
  "phone.transcription.sidebar.messageTitle": "SOEP message",

  "phone.archive.search.placeholder": "Search phone number",

  "portalDetail.internalNotes.title": "Notes",
  "portalDetail.internalNotes.placeholder": "Say something...",
  "portalDetail.assignedTo.saved": "Assigned to {assignedTo}",
  "portalDetail.assignedToNone.saved": "Not assigned",
  "portalDetail.assignedTo": "Assigned to",
  "portalDetail.assignedTo.tooltip": "Assign people",
  "portalDetail.unassigned": "Unassigned",

  "statusFilter.econsults.open": "Open digital consults",
  "statusFilter.econsults.econsult": "E-consults",
  "statusFilter.econsults.appointment": "Video appointment",
  "statusFilter.econsults.walkInConsult": "Walk-in consults",
  "statusFilter.econsults.stayHomeAdvice": "Home care advices",
  "statusFilter.econsults.outOfScope": "Not applicable",
  "statusFilter.econsults.phoneConsult": "Phone call",
  "statusFilter.econsults.closed": "Closed digital consults",
  "statusFilter.econsults.all": "All digital consults",
  "statusFilter.inbox.all": "All items",
  "assignedFilter.all": "Independent of assignment",
  "assignedFilter.me": "Assigned to me",
  "assignedFilter.unassigned": "Unassigned",

  "practiceFilter.all": "All practices",

  "sidenav.expand": "Expand navigation menu",
  "sidenav.collapse": "Collapse navigation menu",
  "sidenav.edit": "Edit navigation menu",
  "sidenav.save": "Save changes",
  "sidenav.practice.settings": "Settings",
  "sidenav.practice.appointment.list": "Appointments",
  "sidenav.practice.econsult.list": "Digital consults",
  "sidenav.practice.medication.request.list": "Medication requests",
  "sidenav.practice.new.patient.list": "Registrations",
  "sidenav.practice.patient.list": "Patients",
  "sidenav.inbox": "Inbox",
  "sidenav.phone": "Phone calls",
  "sidenav.admin": "Admin",
  "sidenav.admin.users": "Users",
  "sidenav.admin.patientAccounts": "Patient accounts",
  "sidenav.admin.practices": "Practices",
  "sidenav.admin.careGroups": "Care groups",
  "sidenav.admin.authorizationClaims": "Authorization claims",
  "sidenav.admin.auditTrails": "Audit trails",
  "sidenav.admin.maintenanceBanners": "Announcement banners",
  "sidenav.careGroup.overview": "Practice insights",
  "sidenav.careGroup.settings": "Care group settings",
  "userAccountMenu.open": "Open user account menu",
  "userAccountMenu.as": "Signed in as",
  "userAccountMenu.myAccount": "My account",
  "userAccountMenu.signout": "Sign Out",

  practice: "Practice",
  "practice.create.success": "Practice created",
  "practice.select.title": "Practice configuration",
  "practice.name": "Name",
  "practice.name.placeholder": "Fill in name",
  "practice.slug": "Web address",
  "practice.slug.placeholder": "Fill in the domain name text",
  "practice.select.slugNotFound": "Practice {slug} not found, or you don't have access.",
  "practice.select.noAccess":
    "You are not an administrator of any practice. Please ask an administrator to get access.",
  "practice.accessExpired.title": "Access expired",
  "practice.accessExpired.description":
    "Your access to practice {practice} has expired. Claim access again to continue configuring it.",
  "practice.accessExpired.backToPractices": "Back to the practices overview",
  "practice.accessExpired.backToPortal": "Back to the inbox",
  "practice.page.general": "Practice",
  "practice.page.general.features": "Features",
  "practice.page.logo": "Logo",
  "practice.page.his": "HIS connection",
  "practice.page.pages": "Site pages",
  "practice.page.pharmacies": "Pharmacies",
  "practice.page.authorizations": "Colleagues",
  "practice.page.agendas": "Agendas",
  "practice.page.agendaBlocks": "Agenda blocks",
  "practice.page.walkInConsult": "Digital walk-in consult",
  "practice.page.phoneSystem": "Phone system",
  "practice.page.labels": "Labels",
  "practice.page.triageOutcome": "Outcome questionnaire",
  "practice.page.triageOutcomes": "Follow-up questionnaire",
  "practice.page.responseTemplates": "Response templates",
  "practice.page.communicationTemplates": "Communication templates",
  "practice.page.notifications": "Notifications",
  "practice.page.registrations": "Registrations",
  "practice.page.digid": "DigiD",
  "practice.group.general": "General",
  "practice.group.notificationsAndCommunication": "Notifications & Communication",
  "practice.group.registration": "Registration",
  "practice.group.portal": "Portal",
  "practice.configuration.slug": "Web address",
  "practice.configuration.slug.fullAddress": "The new web address will be: ",

  "practice.configuration.emailAndWebsite.title": "Email & Website",
  "practice.configuration.fromEmail": "Sender email address",
  "practice.configuration.fromEmail.noAccess":
    "To change your sender email address, please contact support@mijnpraktijk.nl",
  "practice.configuration.fromEmail.admin": "Before changing the sender email address, see",
  "practice.configuration.replyToEmail": "Reply email address",
  "practice.configuration.replyToEmail.info":
    "If patients try to answer an email from MijnPraktijk, it is useful that the reply goes directly to a practice email address.",
  "practice.configuration.agbCode": "AGB code",
  "practice.configuration.nameAndAddress.title": "Name & address",
  "practice.configuration.automaticActions.title": "Automatic actions",
  "practice.configuration.address.segment.agb": "AGB address",
  "practice.configuration.address.segment.custom": "Custom address",
  "practice.configuration.address.agbReadonly": "Address according to the AGB registry",
  "practice.configuration.address.noAgb": "No address is known in the AGB registry for this practice.",
  "practice.configuration.zorgMail": "Zorgmail ID",
  "practice.configuration.phoneNumbers.title": "Phone numbers",
  "practice.configuration.phoneNumber": "Practice phone number",
  "practice.configuration.emergencyPhoneNumber":
    "Emergency phone number (leave empty when the same as regular phone number)",
  "practice.configuration.hapPhoneNumber": "After-hours general practice center phone number",
  "practice.configuration.externalWebsiteUrl": "External website URL",
  "practice.configuration.externalWebsiteUrl.validation": "Please enter a valid url",
  "practice.configuration.journalClosesConsult": "Sending journal item closes digital consult automatically",
  "practice.configuration.journalClosesConsult.info":
    "In a digital consult, sending a new HIS journal item to the HIS will close the digital consult. This means the patient can no longer respond to the digital consult and video will be ended.",
  "practice.configuration.analyticsPermission": "Save anonymous data for quality improvement",
  "practice.configuration.analyticsPermission.info":
    "To improve quality and efficiency of care. Saved data is anonymous and cannot be traced back to individual patients. The statistics will also give insight into what your practice can learn from others. And the other way around!",
  "practice.configuration.includeInSearch.header": "nederland.zoekeenhuisartspraktijk.nl",
  "practice.configuration.includeInSearch": "Show practice at nederland.zoekeenhuisartspraktijk.nl",
  "practice.configuration.includeInSearch.info":
    "Here we refer patients to who are looking for a GP. If you turn this off, you will be hidden from this overview page. You will still be findable via your practice page and the overview of your care group.",

  "practice.pharmacies.title": "Pharmacy list",
  "practice.pharmacies.allowCustom": "Allow patients to enter custom pharmacies",
  "practice.pharmacies.agbCode": "AGB code",
  "practice.pharmacies.addItem": "Add pharmacy",
  "practice.pharmacies.empty.title": "No pharmacies",
  "practice.pharmacies.empty.description": "There are no pharmacies yet. Use the button below to create one.",

  "practice.walkInConsult.intro":
    "During walk-in consulting hours, patients can start an e-consult and wait in the virtual waiting room. You can see the patients waiting in the e-consults overview.",
  "practice.walkInConsult.addItem": "Add time slot",
  "practice.walkInConsult.empty.title": "No time slots",
  "practice.walkInConsult.empty.description": "There are no time slots yet. Use the button below to create one.",

  "practice.phone.init.title": "Phone system not enabled yet",
  "practice.phone.init.info": "Please contact {email} for more information.",
  "practice.phone.init.switchInfo": "Turn off if you do not want defaults",
  "practice.phone.init.templates": "Add default phone menus",
  "practice.phone.init.schedule": "Initialise daily schedules",
  "practice.phone.init.button": "Initialise",

  "practice.phone.menuAudio.default": "Default text message",
  "practice.phone.menuAudio.text": "Write text message",
  "practice.phone.menuAudio.text.info":
    "English texts can be placed between [en] and [/en]. It's possible to use {link}.",
  "practice.phone.menuAudio.text.info.link": "SSML tags",
  "practice.phone.menuAudio.file": "Upload audio file",
  "practice.phone.menuAudio.file.missing": "Missing audio file",
  "practice.phone.menuAudio.file.loading": "Loading...",
  "practice.phone.menuAudio.play": "Play",
  "practice.phone.menuAudio.stop": "Stop",

  "practice.phone.general": "General phone settings",
  "practice.phone.general.incomingPhoneNumbers": "Incoming phone numbers",
  "practice.phone.general.incomingPhoneNumbers.add": "Phone number",
  "practice.phone.general.incomingPhoneNumbers.remove": "Remove phone number",
  "practice.phone.general.incomingPhoneNumbers.admin.info": "See {link} on how to set up phone numbers",
  "practice.phone.general.incomingPhoneNumbers.admin.link": "documentation",
  "practice.phone.general.menuOptionEnglish": "English menu option",
  "practice.phone.general.transcribe": "Write an automatic SOEP summary after each phone call",
  "practice.phone.general.allowPrescription": "Allow prescription requests",
  "practice.phone.general.prescriptionNlMenuAudio": "Prescription menu Dutch",
  "practice.phone.general.prescriptionEnMenuAudio": "Prescription menu English",

  "practice.phone.devices": "Devices",
  "practice.phone.devices.info":
    "Incoming phone calls can be routed to devices. Choose which devices you want to receive phone calls on.",
  "practice.phone.devices.addItem": "Add device",
  "practice.phone.devices.empty.title": "No devices",
  "practice.phone.devices.empty.description": "There are no devices yet. Use the button below to create one.",
  "practice.phone.devices.confirmRemove.title": "Delete device",
  "practice.phone.devices.confirmRemove.content": "Are you sure you want to delete device {name}?",
  "practice.phone.devices.name": "Name",
  "practice.phone.devices.phoneNumber": "Phone number",
  "practice.phone.devices.timeout": "Max seconds ringing",
  "practice.phone.devices.timeout.info":
    "To prevent the call going to voicemail, this value should be a bit less than when voicemail would answer.",
  "practice.phone.devices.moreThan0.validation": "Please enter a number greater than 0",
  "practice.phone.devices.adminOnly": "Contact support@mijnpraktijk.nl to have this setting changed.",
  "practice.phone.devices.auth.label": "Univerge Blue account",
  "practice.phone.devices.auth.status.connected": "Connected",
  "practice.phone.devices.auth.status.disconnected": "Not connected",
  "practice.phone.devices.auth.connect": "Connect",
  "practice.phone.devices.auth.reconnect": "Reconnect",
  "practice.phone.devices.auth.disconnect": "Disconnect",
  "practice.phone.devices.auth.connect.error": 'Device "{deviceName}" couldn\'t be connected',
  "practice.phone.devices.auth.disconnect.success": 'Device "{deviceName}" has been disconnected',
  "practice.phone.devices.auth.verify": "Check account",
  "practice.phone.devices.auth.verify.title": "Linked Univerge Blue account",
  "practice.phone.devices.auth.verify.question": "Does this information look correct?",
  "practice.phone.devices.auth.verify.mijnpraktijk": "Device in MijnPraktijk",
  "practice.phone.devices.auth.verify.elevate": "Devices of the Univerge Blue account",
  "practice.phone.devices.auth.verify.deviceId": "Device ID",
  "practice.phone.devices.auth.verify.deviceType": "Device type",
  "practice.phone.devices.auth.verify.empty": "No devices found for this account.",
  "practice.phone.devices.auth.verify.error": "The devices of this Univerge Blue account couldn't be loaded.",
  "practice.phone.devices.auth.verify.confirm": "Yes, that's correct",
  "practice.phone.devices.auth.verify.reject": "No, disconnect",
  "practice.phone.devices.preventImmediateAnswer": "Automatic voicemail detection",
  "practice.phone.devices.preventImmediateAnswer.info":
    "This setting should only be enabled for personal cell phones with voicemail! Yealink devices will not work as expected. Instead, you should disable voicemail on the device. If you check this option and the call is picked up within one second, the system will assume it was answered by a voicemail and hang up immediately.",
  "practice.phone.devices.useDesktopClient": "Answer calls in the Univerge Blue desktop client",
  "practice.phone.devices.useDesktopClient.info":
    "Enable this if the assistant has the Univerge Blue Connect desktop client installed. The system will then attempt to automatically answer incoming calls in the desktop client.",
  "practice.phone.devices.ringRegularCalls": "Automatic ringing for regular calls, after",
  "practice.phone.devices.ringConsultationCalls": "Automatic ringing for colleague calls, after",
  "practice.phone.devices.ringEmergencyCalls": "Automatic ringing for emergency calls, after",
  "practice.phone.devices.ringDelaySeconds": "seconds",

  "practice.phone.templates": "Phone menus",
  "practice.phone.templates.info":
    "In a phone menu you can save different phone settings. For example: there might be a phone menu for when the assistant is on a break, or a phone menu for regular morning hours. On the phone schedule you can configure when these phone menus are active.",
  "practice.phone.templates.addItem": "Add phone menu",
  "practice.phone.templates.empty.title": "No phone menus",
  "practice.phone.templates.empty.description": "There are no phone menus yet. Use the button below to create one.",
  "practice.phone.templates.confirmRemove.title": "Delete phone menu",
  "practice.phone.templates.confirmRemove.content":
    'Are you sure you want to delete phone menu "{name}"? {isSaved, select, true {Any schedules using this phone menu will also be deleted!} other {}}',
  "practice.phone.templates.closed.info":
    "This default phone menu cannot be deleted and is used when the schedule is empty",
  "practice.phone.templates.assistantUnavailable.info":
    "This default phone menu cannot be deleted and is used when the assistant is set to temporarily unavailable",
  "practice.phone.templates.name": "Name",
  "practice.phone.templates.colour": "Colour",
  "practice.phone.templates.practiceStatus": "Practice is",
  "practice.phone.templates.practiceOpen": "Open",
  "practice.phone.templates.practiceClosed": "Closed",
  "practice.phone.templates.allowQueueing": "Allow queueing",
  "practice.phone.templates.allowCallback": "Allow callback scheduling",
  "practice.phone.templates.allowConsultation": "Allow colleague calls",
  "practice.phone.templates.scheduleCallbacksPerHour": "Schedule callbacks per hour",
  "practice.phone.templates.scheduleCallbacksPerHour.validation": "Please enter a number greater than 0",
  "practice.phone.templates.welcomeMenuAudio": "Welcome menu Dutch",
  "practice.phone.templates.englishMenuAudio": "Welcome menu English",
  "practice.phone.templates.menuAudioChangeWarning":
    "You've altered this phone menu's settings. Ensure you also update the welcome menu (below), or use the default text message.",

  "practice.phone.schedule": "Schedule",
  "practice.phone.schedule.add": "Time slot",
  "practice.phone.schedule.event.add": "Add time slot",
  "practice.phone.schedule.event.edit": "Edit time slot",
  "practice.phone.schedule.template": "Phone menu",
  "practice.phone.schedule.template.placeholder": "Choose a phone menu",
  "practice.phone.schedule.template.new": "New phone menu",
  "practice.phone.schedule.type": "Schedule",
  "practice.phone.schedule.type.oneTime": "One-time",
  "practice.phone.schedule.type.oneTime.info": "This event will occur only once, overriding the repeating schedule.",
  "practice.phone.schedule.type.repeating": "Repeats weekly",
  "practice.phone.schedule.type.repeating.info":
    "Recurs on the selected day. One-time events will always override this schedule.",
  "practice.phone.schedule.days": "On which days?",
  "practice.phone.schedule.days.hint.single": "Pick one or more days. Multiple days stay linked together afterwards.",
  "practice.phone.schedule.days.hint.linked":
    "These {count} days belong together — a change applies to all of them. Turning a day off removes that time slot from the schedule.",
  "practice.phone.schedule.delete": "Delete time slot",
  "practice.phone.schedule.delete.confirm": "Delete",
  "practice.phone.schedule.delete.confirm.title": "Delete time slot?",
  "practice.phone.schedule.delete.confirm.body": "This time slot is removed from the schedule.",
  "practice.phone.schedule.scope.title": "This time slot is scheduled every {weekday}",
  "practice.phone.schedule.scope.question": "What do you want to change?",
  "practice.phone.schedule.scope.once": "This week only",
  "practice.phone.schedule.scope.once.info": "Applies in the week of {weekStart}. The other weeks stay as they are.",
  "practice.phone.schedule.scope.weekly": "Every week",
  "practice.phone.schedule.scope.weekly.info": "From this week on, including all following weeks.",
  "practice.phone.schedule.scope.convert.weekly": "Stop repeating",
  "practice.phone.schedule.scope.convert.weekly.info":
    "The weekly time slot is removed from every week; only this one-time slot remains.",
  "practice.phone.schedule.scope.delete.question": "What do you want to delete?",
  "practice.phone.schedule.scope.delete.once.info":
    "Only in the week of {weekStart}. The other weeks stay as they are.",
  "practice.phone.schedule.scope.delete.weekly.info": "The time slot is removed from every week.",
  "practice.phone.schedule.override.thisWeek": "Adjusted this week",
  "practice.phone.schedule.legend.oneTime": "One-time time slot — sits on top of the weekly schedule",

  "practice.phone.phoneNumberIncorrect": "Phone number must be 10 digits or start with +",
  "practice.labels.intro": "Labels are displayed for all items and can be used in filtering.",
  "practice.labels.tooWhite": "This color is too white to be displayed correctly",
  "practice.labels.addItem": "Add label",
  "practice.labels.empty.title": "No labels",
  "practice.labels.empty.description": "There are no labels yet. Use the button below to create one.",

  "practice.his.intro": "By connecting the HIS to MijnPraktijk we can exchange data.",
  "practice.his.his": "Select your HIS",
  "practice.his.disabled": "Disabled",
  "practice.his.connectionViaApi.header": "Connection via API",
  "practice.his.connectionViaApi.info":
    "An API connection provides the most reliable connection. This is therefore enabled by default.",
  "practice.his.bricksApiTarget": "Bricks Tetra API target",
  "practice.his.bricksRegistrationAgb": "Bricks Tetra AGB code used for registrations",
  "practice.his.sandayOrganizationId": "Sanday Organization ID",
  "practice.his.microHisDatabaseId": "MicroHIS Database ID",
  "practice.his.microHisPraktijkCode": "MicroHIS praktijkcode (optional)",
  "practice.his.microHisPraktijkCode.info":
    "Only needed when Dedalus identifies your practice by praktijkcode instead of AGB code, e.g. in a demo database. When filled in, MicroHIS uses it instead of the AGB code.",
  "practice.his.omniHisApiTarget": "OmniHis API target",
  "practice.his.appointmentCancelMinMinutes.1": "Appointments can be cancelled online, until",
  "practice.his.appointmentCancelMinMinutes.2": "before the start of the appointment",
  "practice.his.appointmentCancelMinMinutes.minutes": "Minutes",
  "practice.his.appointmentCancelMinMinutes.hours": "Hours",
  "practice.his.appointmentCancelMinMinutes.days": "Days",
  "practice.his.appointmentCancelMinMinutes.weeks": "Weeks",
  "practice.his.testConnection.btn": "Test connection via API",
  "practice.his.testConnection.success": "Connection successful",
  "practice.his.testConnection.error": "Connection failed. The HIS is not responding, or the API code is incorrect.",
  "practice.his.connectionViaRpa.header": "Additional connection (via RPA)",
  "practice.his.connectionViaRpa.info.disconnected":
    "The button below will take you to the site of our partner AutoMedic, where you can activate RPA.",
  "practice.his.connectionViaRpa.info.connected": "A connection has been made with our partner AutoMedic for RPA.",
  "practice.his.rpaModal.title": "Additional connection (via RPA)",
  "practice.his.rpaModal.text":
    "We offer an additional connection via an RPA (Robotic Process Automation). This is a kind of robot that clicks for you. This allows certain data to be transferred automatically and quickly. Without the RPA, this would be manual work.",
  "practice.his.autoMedic.label": "Connection status",
  "practice.his.autoMedic.status.disconnected": "No connection",
  "practice.his.autoMedic.status.connected": "Connected",
  "practice.his.autoMedic.btn.connect": "Create connection via RPA",
  "practice.his.autoMedic.btn.disconnect": "Disconnect via RPA",
  "practice.his.autoMedic.connect.success": "Successfully connected to AutoMedic",
  "practice.his.autoMedic.disconnect.success": "Successfully disconnected from AutoMedic",
  "practice.his.autoMedic.saveChanges": "Save the changes first, before connecting to AutoMedic",
  "practice.his.capabilities.header": "This is what we exchange automatically with {his}",
  "practice.his.capabilities.info": "For each feature you can see who it is for. Other data you transfer yourself.",
  "practice.his.capabilities.api.disconnected":
    "The connection with {his} is currently off. As soon as you connect, we will exchange the data below automatically.",
  "practice.his.capabilities.rpa.disconnected":
    "Available, but currently off. With an RPA connection we also exchange this:",
  "practice.his.capabilities.group.patients": "For your patients",
  "practice.his.capabilities.group.practice": "For the practice",
  "practice.his.capabilities.core": "Book an appointment online",
  "practice.his.capabilities.appointmentList": "See booked appointments",
  "practice.his.capabilities.appointmentCancel": "Cancel an appointment",
  "practice.his.capabilities.prescribedMedication": "See prescribed medication",
  "practice.his.capabilities.medicationRequest": "Request a repeat prescription",
  "practice.his.capabilities.medicalResults": "View results and letters",
  "practice.his.capabilities.allergies": "View allergies",
  "practice.his.capabilities.episodes": "View episodes",
  "practice.his.capabilities.loginDigid": "Log in with DigiD",
  "practice.his.capabilities.loginPassword": "Log in with email and password",
  "practice.his.capabilities.registration": "Register a new patient",
  "practice.his.capabilities.patientLookup": "Look up a patient in the HIS",
  "practice.his.capabilities.agendaBlocks": "Fetch agenda blocks from the HIS",
  "practice.his.capabilities.addFileToPatient": "Add a file or image to the patient record",
  "practice.his.capabilities.sendJournal": "Send a journal entry to the HIS",
  "practice.his.capabilities.charge": "Declare a consultation",
  "practice.his.capabilities.deepLinks": "Open the patient record directly in the HIS",
  "practice.his.noConnection.header": "We do not have a connection with {his} yet",
  "practice.his.noConnection.body":
    "You can use MijnPraktijk as usual, but data is not exchanged automatically with {his}. You transfer it yourself.",
  "practice.his.noConnection.noted":
    "We have noted that your practice uses {his}, so we can help you in time once a connection is available.",

  "practice.pages.addItem": "Add page",
  "practice.pages.empty.title": "No site pages",
  "practice.pages.empty.description": "There are no site pages yet. Use the button below to create one.",
  "practice.pages.intro":
    "Site pages are displayed to patients in order. New pages can only be moved once they are saved",
  "practice.pages.noRecord": "There are currently no site pages. You can create one with the 'Add page' button.",
  "practice.pages.tab.sitePages": "Site pages",
  "practice.pages.tab.website": "Website",
  "practice.pages.confirmRemove.title": "Delete page",
  "practice.pages.confirmRemove.content": "Are you sure you want to delete page {pageSlug}?",
  "practice.pages.confirmRemove.success": "Page is deleted",
  "practice.pages.slug": "Web address",
  "practice.pages.slug.fullAddress": "The new web address will be: ",
  "practice.pages.slug.warning":
    "WARNING: changing the web address can result in patients not being able to find the page anymore.",
  "practice.pages.slug.validation": "Web address can only contain a-z (so no capitals) and 0-9 (numbers)",
  "practice.pages.slug.validationForbidden": "This web address is not allowed",
  "practice.pages.poster": "Poster with QR code",
  "practice.pages.poster.download": "Download poster (PDF)",
  "practice.pages.poster.info":
    "An A4 poster with the QR code and web address of this page. Print it for the waiting room or the front desk.",
  "practice.pages.poster.unsaved": "Save the page first, so the poster gets the right web address.",
  "practice.pages.module": "Type",
  "practice.pages.module.static": "Static page",
  "practice.pages.module.appointmentCreation": "Create appointment",
  "practice.pages.module.appointmentList": "List appointments",
  "practice.pages.module.econsult": "E-consult",
  "practice.pages.module.walkInConsult": "Digital walk-in consult",
  "practice.pages.module.medicationRequest": "Medication request",
  "practice.pages.module.registrationRequest": "Registration",
  "practice.pages.module.myMedicalFile": "Medical file",
  "practice.pages.module.incorrect": "This type is not possible",
  "practice.pages.overview": "Tile on website",
  "practice.pages.overview.hintForRegistrations":
    "Is the site page open for registrations? Then people who have the web address can register. Even if the tile is hidden.",
  "practice.pages.overview.nl": "Overview page NL",
  "practice.pages.overview.en": "Overview page EN",
  "practice.pages.overviewIcon.label": "Overview page icon",
  "practice.pages.overviewIcon.edit": "Edit",
  "practice.pages.overviewIcon.title": "Overview page icon of {slug}",
  "practice.pages.labels.registration": "Add labels to registrations made via this page",
  "practice.pages.labels.registration.description":
    "In the overview of new registrations, you can filter by these labels.",
  "practice.pages.introPage": "Show introduction page",
  "practice.pages.introPage.hintForRegistrations": "This page is never shown when registrations are closed.",
  "practice.pages.moduleText": "Show blue information panel at the top of the page",
  "practice.pages.staticText": "Text on page",
  "practice.pages.prep": "Consult preparation",
  "practice.pages.prep.disabled": "Off",
  "practice.pages.prep.urgentOnly": "Call the practice in an emergency; don't suggest alternative contact methods.",
  "practice.pages.prep.displayAdvise":
    "Call the practice in an emergency; suggest alternative contact methods. If digital contact is not recommended, still offer e-consult option.",
  "practice.pages.prep.forceAdvise":
    "Call the practice in an emergency; suggest alternative contact methods. If digital contact is not recommended, only allow face-to-face appointment.",
  "practice.pages.plato.administrative": "Only administrative questionnaires",
  "practice.pages.plato.administrative.info":
    "Administrative questionnaires are for non-medical requests by patients. Enabling this option will disable the medical questionnaires for this site page and show the administrative questionnaires instead.",
  "practice.pages.agendas": "Agendas",
  "practice.pages.agendas.all": "All agendas",
  "practice.pages.agendas.info": "Which agendas can the patient select? Leave empty for {link}.",
  "practice.pages.agendas.prepInfo":
    "In case an appointment is made after consult preparation, which agendas can the patient select? Leave empty for {link}.",
  "practice.pages.agendas.link": "all agendas",
  "practice.pages.agendaBlocks": "Agenda blocks",
  "practice.pages.agendaBlocks.info": "Which agenda blocks can the patient select? Leave empty for {link}.",
  "practice.pages.agendaBlocks.prepInfo":
    "In case an appointment is made after consult preparation, which agenda blocks can the patient select? Leave empty for {link}.",
  "practice.pages.agendaBlocks.link": "all agenda blocks",
  "practice.pages.agendaBlocks.all": "agenda blocks",
  "practice.pages.prepAlternativeLabels": "Consult preparation alternative labels",
  "practice.pages.prepAlternativeLabels.info":
    "In case an appointment is made after the consult preparation, which labels should be applied?",
  "practice.pages.registration.toggle": "Open for registrations",
  "practice.pages.registration.limit.before": "Close registrations automatically after",
  "practice.pages.registration.limit.after": "registrations",
  "practice.pages.registration.limit.info": "This was originally set to {limit}.",
  "practice.pages.registration.limitReached":
    "<p>Registrations are closed because the counter is at 0.</p><p>The counter will automatically increase if you reject a registration.</p><p>Or if a patient has started a registration, but does not complete it within 24 hours.</p>",
  "practice.pages.registration.limitDialog.title": "Close registrations automatically",
  "practice.pages.registration.limitDialog.text":
    "<p>The maximum number of new registrations was set to <b>{limit}</b> on {date}.</p><p>Since then, <b>{registered}</b> patients have registered. So there are still <b>{remaining}</b> spots available.</p><p>Registrations are counted as soon as they come in. If a registration is rejected, a spot will become available again. Patients leaving the practice are not included in this count.</p>",
  "practice.pages.registration.closedText": "Text for closed registration",
  "practice.pages.registration.settingsLink": "General registration settings",

  "practice.pages.questionnaire.toggle": "Add extra questions",
  "practice.pages.questionnaire.addQuestion": "Add question",
  "practice.pages.questionnaire.required": "Required",
  "practice.pages.questionnaire.requiredToggle": "Make this question required",
  "practice.pages.questionnaire.questionType": "The patient answers via",
  "practice.pages.questionnaire.questionTitle": "Question title",
  "practice.pages.questionnaire.questionTitle.locale": "Question ({locale})",
  "practice.pages.questionnaire.questionTitle.placeholder.NL": "Example: Heeft u nog een vraag voor ons?",
  "practice.pages.questionnaire.questionTitle.placeholder.EN": "Example: Do you have a question for us?",
  "practice.pages.questionnaire.description": "Description",
  "practice.pages.questionnaire.description.locale": "Description ({locale})",
  "practice.pages.questionnaire.description.placeholder.NL":
    "Example: Of iets wat u wilt delen? Met betrekking tot de inschrijving.",
  "practice.pages.questionnaire.description.placeholder.EN":
    "Example: Or something you would like to share? In context of the registration.",
  "practice.pages.questionnaire.questionLabel.locale": "Label above the textarea ({locale})",
  "practice.pages.questionnaire.questionLabel.placeholder.NL": "Example: Vraag of mededeling",
  "practice.pages.questionnaire.questionLabel.placeholder.EN": "Example: Question or statement",
  "practice.pages.questionnaire.questionPlaceholder.locale": "Placeholder text ({locale})",
  "practice.pages.questionnaire.questionPlaceholder.placeholder.NL":
    "Example: Ik zou me alleen willen inschrijven als mijn man en kinderen hier ook terecht kunnen. Kan dat?",
  "practice.pages.questionnaire.questionPlaceholder.placeholder.EN":
    "Example: I only want to register here if my kids and husband can register here as well. Is that possible?",
  "practice.pages.questionnaire.options": "Answer options",
  "practice.pages.questionnaire.addOption": "Add option",
  "practice.pages.questionnaire.editDialog.title": "Question {index}",
  "practice.pages.questionnaire.untitledQuestion": "Question {number}",
  "practice.pages.questionnaire.editQuestion": "Edit question",
  "practice.pages.questionnaire.deleteQuestion": "Delete question",
  "practice.pages.questionnaire.deleteOption": "Delete option",
  "practice.pages.questionnaire.option.fallback": "Option {number}",
  "practice.pages.questionnaire.type.open_ended": "Open text field",
  "practice.pages.questionnaire.type.single_select": "Single choice",
  "practice.pages.questionnaire.type.multi_select": "Multiple choice",
  "practice.pages.questionnaire.validation.required": "This field is required",
  "practice.pages.questionnaire.import.button": "Import from file",
  "practice.pages.questionnaire.import.title": "Import questions from a file",
  "practice.pages.questionnaire.import.intro":
    "Already have your questions in a document? Import a .docx or .txt file and we convert it automatically. You then step through the questions one by one to review them.\n\nTip: use a vertical bar to put both languages on one line, for example 'Rookt u? | Do you smoke?'.",
  "practice.pages.questionnaire.import.label": "Drop a file here or click to choose",
  "practice.pages.questionnaire.import.parsing": "Processing…",
  "practice.pages.questionnaire.import.success": "{count, plural, one {# question added} other {# questions added}}",
  "practice.pages.questionnaire.import.error": "The document could not be processed.",
  "practice.pages.questionnaire.import.warningsTitle": "Some parts could not be fully interpreted:",
  "practice.pages.questionnaire.import.warning.unsupportedFileType": 'Unsupported file type "{contentType}".',
  "practice.pages.questionnaire.import.warning.orphanOptionsSkipped":
    "A list of options was found before any question and was skipped.",
  "practice.pages.questionnaire.import.warning.bareLinesAddedAsOptions":
    'Added {count, plural, one {# line} other {# lines}} without a bullet under "{title}" as options — please verify.',
  "practice.pages.questionnaire.import.warning.unreadableDocument":
    "The file could not be read; it may be corrupt or incomplete.",
  "practice.pages.questionnaire.import.warning.sectionWithoutTitle": "Skipped a section without a question title.",
  "practice.pages.questionnaire.import.warning.choiceQuestionWithoutOptions":
    '"{title}" looks like a choice question but no options were found — add them in the builder.',
  "practice.pages.questionnaire.import.warning.optionsIgnoredForOpenQuestion":
    'Options under "{title}" were ignored because it is an open question.',
  "practice.pages.questionnaire.import.warning.ignoredNonQuestionTable":
    "Skipped a table that could not be read as questions.",
  "practice.pages.questionnaire.review.title": "Review imported questions",
  "practice.pages.questionnaire.review.progress": "Question {current} of {total}",
  "practice.pages.questionnaire.review.documentWarningsTitle": "Some parts could not be fully interpreted:",
  "practice.pages.questionnaire.review.previous": "Previous",
  "practice.pages.questionnaire.review.stop": "Stop",
  "practice.pages.questionnaire.review.next": "Save and continue",
  "practice.pages.questionnaire.review.finish": "Finish",
  "practice.pages.questionnaire.review.suggestTranslations": "Suggest translations",
  "practice.pages.questionnaire.review.suggestTranslations.failed":
    "Could not suggest translations. Please try again or enter the text manually.",

  "practice.agendas.intro":
    "Schedules define when patients can make appointments. Each schedule is linked to a schedule in the HIS.",
  "practice.agendas.addItem": "Add agenda",
  "practice.agendas.empty.title": "No agendas",
  "practice.agendas.empty.description": "There are no agendas yet. Use the button below to create one.",
  "practice.agendas.name": "Name shown to patient",
  "practice.agendas.hisName": "HIS name",
  "practice.agendas.hisName.unknown": "This HIS name doesn't exist (any more)",
  "practice.agendas.address": "Address",
  "practice.agendas.address.info": "Address for face-to-face appointments.",
  "practice.agendas.roles.title": "Agenda belongs to roles",
  "practice.agendas.roles.info":
    "Show this agenda when the outcome is one of the following types of health care providers",
  "practice.agendas.roles.tip":
    "When a patient wants to make an appointment, we ask preparatory questions. These questions determine, among other things, which type of healthcare provider is best suited to help.",
  "practice.agendas.types": "Types of allowed appointments",
  "practice.agendas.emptyRolesError": "Each agenda must include at least one health professional role",
  "practice.agendas.faceToFaceAppointmentEnabled": "Face-to-face appointments",
  "practice.agendas.phoneAppointmentEnabled": "Phone appointments",
  "practice.agendas.videoAppointmentEnabled": "Video appointments",
  "practice.agendas.hidden": "Only show this agenda if it is enabled on the site page.",
  "practice.agendas.confirmRemove.title": "Delete agenda",
  "practice.agendas.confirmRemove.content": "Are you sure you want to delete agenda {agendaName}?",
  "practice.agendas.confirmRemove.success": "Agenda is deleted",

  "practice.agendaBlocks.addItem": "Add agenda block",
  "practice.agendaBlocks.empty.title": "No agenda blocks",
  "practice.agendaBlocks.empty.description": "There are no agenda blocks yet. Use the button below to create one.",
  "practice.agendaBlocks.intro":
    "Agendas blocks are used in the HIS to categorise certain appointment types. This is often displayed in the HIS with a certain color. Limit here which agenda blocks can be used by patients to schedule an appointment.",
  "practice.agendaBlocks.hisBlockName": "HIS block name",
  "practice.agendaBlocks.hisBlockName.unknown": "This HIS block name doesn't exist (any more)",
  "practice.agendaBlocks.confirmRemove.title": "Delete agenda block",
  "practice.agendaBlocks.confirmRemove.content": "Are you sure you want to delete agenda block {name}?",
  "practice.agendaBlocks.confirmRemove.success": "Agenda block is deleted",

  "practice.responseTemplates.addItem": "Add response template",
  "practice.responseTemplates.empty.title": "No response templates",
  "practice.responseTemplates.empty.description":
    "There are no response templates yet. Use the button below to create one.",
  "practice.responseTemplates.empty.defaultTitle": "No default templates",
  "practice.responseTemplates.empty.defaultDescription": "There are no default templates available.",
  "practice.responseTemplates.intro": "Response templates are frequently used responses for e-consults.",
  "practice.responseTemplates.name": "Name",
  "practice.responseTemplates.text": "Text",
  "practice.responseTemplates.suggestedText":
    "<p>Hello {firstName},</p><p>{currentText}</p><p>Best regards,<br />{yourName}</p>",
  "practice.responseTemplates.age": "Age",
  "practice.responseTemplates.ageFrom": "From age",
  "practice.responseTemplates.ageTo": "To age",
  "practice.responseTemplates.gender": "Sex",
  "practice.responseTemplates.defaultsPlaceholder": "Show MijnPraktijk templates",
  "practice.responseTemplates.locale": "Language",
  "practice.responseTemplates.treatmentArea": "Treatment area",
  "practice.responseTemplates.plan": "Plan for HIS journal",
  "practice.responseTemplates.triageSymptomLabel": "Outcome labels",
  "practice.responseTemplates.addTriageSymptomLabel": "Add outcome labels",
  "practice.responseTemplates.suggestions": "Use a template as a starting point for your answer:",
  "practice.responseTemplates.showAll": "All templates",
  "practice.responseTemplates.search": "Search response template",
  "practice.responseTemplates.confirmRemove.title": "Delete response template",
  "practice.responseTemplates.confirmRemove.content":
    "Are you sure you want to delete response template {templateName}?",
  "practice.responseTemplates.confirmRemove.success": "Response template is deleted",

  "practice.communicationTemplates.intro": "Automated email or SMS messages to the patient contain a standard text.",
  "practice.communicationTemplate.list": "Communication templates",
  "practice.communicationTemplate.type.email": "Email",
  "practice.communicationTemplate.type.sms": "SMS",
  "practice.communicationTemplate.group.general": "General",
  "practice.communicationTemplate.group.appointments": "Appointments",
  "practice.communicationTemplate.group.econsults": "E-consults",
  "practice.communicationTemplate.group.walkInConsults": "Walk-in consults",
  "practice.communicationTemplate.group.medicationRequests": "Medication requests",
  "practice.communicationTemplate.group.registration": "Registration",
  "practice.communicationTemplate.group.portal": "Portal",
  "practice.communicationTemplate.group.pageInvitation": "Personal invitation (registration or appointment)",
  "practice.communicationTemplate.group.support": "Patient support",
  "practice.communicationTemplate.group.system": "System user",
  "practice.communicationTemplate.group.other": "Other",
  "practice.communicationTemplate.enabled": "Sending enabled",
  "practice.communicationTemplate.isDefault": "Use default text from MijnPraktijk",
  "practice.communicationTemplate.nl-NL.subject": "Dutch subject",
  "practice.communicationTemplate.nl-NL.text": "Dutch text",
  "practice.communicationTemplate.en-GB.subject": "English subject",
  "practice.communicationTemplate.en-GB.text": "English text",

  "practice.notifications.intro":
    "For different events you can specify which notifications should be sent. This could for example be used to inform assistants of new medication requests, or doctors of new e-consults.",
  "practice.notifications.addItem": "Add notification",
  "practice.notifications.empty.title": "No notifications",
  "practice.notifications.empty.description": "There are no notifications yet. Use the button below to create one.",
  "practice.notifications.modal.title": "Notification {number}",
  "practice.notifications.confirmRemove.title": "Delete notification",
  "practice.notifications.confirmRemove.content": "Are you sure you want to delete this notification?",
  "practice.notifications.confirmRemove.success": "Notification is deleted",
  "practice.notifications.trigger.type.label": "Send a message when",
  "practice.notifications.trigger.type.placeholder": "Select a reason",
  "practice.notifications.trigger.type.labelsAdded": "Label(s) have been added",
  "practice.notifications.trigger.type.labelsRemoved": "Label(s) have been removed",
  "practice.notifications.trigger.type.assignedUsers": "Colleague(s) have been assigned",
  "practice.notifications.trigger.type.unassignedUsers": "Assigned colleague(s) have been removed",
  "practice.notifications.trigger.type.itemEvents": "Something is new",
  "practice.notifications.trigger.type.statusChange": "There is a status change",
  "practice.notifications.trigger.type.phoneStatus": "Phone system problems",
  "practice.notifications.trigger.type.registrationStatus": "Registrations opened/closed",
  "practice.notifications.trigger.labels": "Label(s)",
  "practice.notifications.trigger.users.label": "Colleague(s)",
  "practice.notifications.trigger.users.all": "Leave empty for all colleagues",
  "practice.notifications.trigger.itemEvents.label": "Event(s)",
  "practice.notifications.trigger.itemEvents.placeholder": "Select events",
  "practice.notifications.trigger.itemEvents.newFaceToFaceAppointment": "New face-to-face appointment",
  "practice.notifications.trigger.itemEvents.newPhoneAppointment": "New phone appointment",
  "practice.notifications.trigger.itemEvents.newVideoAppointment": "New video appointment",
  "practice.notifications.trigger.itemEvents.newWalkInConsult": "New walk-in consult appointment",
  "practice.notifications.trigger.itemEvents.newEconsult": "New e-consult",
  "practice.notifications.trigger.itemEvents.newPatientMessageEconsult":
    "New patient response in e-consult (after 5 minutes unseen)",
  "practice.notifications.trigger.itemEvents.newMedicationRequest": "New medication request",
  "practice.notifications.trigger.itemEvents.newRegistrationRequest": "New registration request",
  "practice.notifications.trigger.itemEvents.newPhoneMedicationRecording": "New phone medication prescription",
  "practice.notifications.trigger.statusChange.label": "Status change(s)",
  "practice.notifications.trigger.statusChange.placeholder": "Select statuses",
  "practice.notifications.trigger.multipleIsOr":
    "When multiple are selected, one of them can trigger the notification.",
  "practice.notifications.itemStatus.appointmentCreated": "Appointment created",
  "practice.notifications.itemStatus.appointmentRemoved": "Appointment removed",
  "practice.notifications.itemStatus.econsultOpen": "Digital consult open",
  "practice.notifications.itemStatus.econsultClosed": "Digital consult closed",
  "practice.notifications.itemStatus.econsultUnanswered": "Digital consult unanswered",
  "practice.notifications.itemStatus.econsultAnswered": "Digital consult answered",
  "practice.notifications.itemStatus.medicationRequestUndecided": "Medication request to be processed",
  "practice.notifications.itemStatus.medicationRequestApproved": "Medication request approved",
  "practice.notifications.itemStatus.medicationRequestRejected": "Medication request rejected",
  "practice.notifications.itemStatus.medicationRequestSentToHis": "Medication request sent to HIS",
  "practice.notifications.itemStatus.registrationRequestUndecided": "Registration request to be processed",
  "practice.notifications.itemStatus.registrationRequestAccepted": "Registration request accepted",
  "practice.notifications.itemStatus.registrationRequestRejected": "Registration request rejected",
  "practice.notifications.itemStatus.registrationRequestCompleted": "Registration request completed",
  "practice.notifications.itemStatus.registrationRequestProcessing": "Registration request processing",
  "practice.notifications.registrationEvent.label": "Event(s)",
  "practice.notifications.registrationEvent.openedAutomatic": "Registrations opened automatically",
  "practice.notifications.registrationEvent.closedAutomatic": "Registrations closed automatically",
  "practice.notifications.registrationEvent.openedManual": "Registrations opened manually",
  "practice.notifications.registrationEvent.closedManual": "Registrations closed manually",
  "practice.notifications.delay.delayedValue.label": "Send directly",
  "practice.notifications.delay.delayedFactor.label": "Execute after",
  "practice.notifications.delay.delayedFactor.minutes": "Minutes",
  "practice.notifications.delay.delayedFactor.hours": "Hours",
  "practice.notifications.delay.delayedFactor.days": "Days",
  "practice.notifications.delay.delayedFactor.workdays": "Working days",
  "practice.notifications.delay.delayedFactor.weeks": "Weeks",
  "practice.notifications.delay.value.label": "Amount",
  "practice.notifications.delay.unit.label": "Unit",
  "practice.notifications.condition.type.label": "AND if",
  "practice.notifications.condition.type.placeholder": "Select a condition",
  "practice.notifications.condition.type.labels": "Label(s)",
  "practice.notifications.condition.type.assignedUsers": "Assigned colleague(s)",
  "practice.notifications.condition.type.itemTypes": "Item type(s)",
  "practice.notifications.condition.type.itemStatus": "Item status(ses)",
  "practice.notifications.condition.labels.label": "Contains label(s)",
  "practice.notifications.condition.assigned.label": "Assigned user(s)",
  "practice.notifications.condition.itemTypes.label": "Item type(s)",
  "practice.notifications.condition.itemTypes.faceToFaceAppointment": "Face-to-face appointment",
  "practice.notifications.condition.itemTypes.phoneAppointment": "Phone appointment",
  "practice.notifications.condition.itemTypes.videoAppointment": "Video appointment",
  "practice.notifications.condition.itemTypes.econsult": "E-consult",
  "practice.notifications.condition.itemTypes.walkInConsult": "Walk-in consult",
  "practice.notifications.condition.itemTypes.medicationRequest": "Medication request",
  "practice.notifications.condition.itemTypes.registrationRequest": "Registration request",
  "practice.notifications.condition.itemStatus.label": "Item status(ses)",
  "practice.notifications.condition.multipleIsOr":
    "When multiple are selected, one of them can be applicable (i.e. OR). Press the plus button when all should be applicable (AND).",
  "practice.notifications.target.type.label": "Send notification to",
  "practice.notifications.target.type.placeholder": "Select a recipient type",
  "practice.notifications.target.type.emailAddress": "Email address",
  "practice.notifications.target.type.teamsUrl": "Teams connector",
  "practice.notifications.target.email.info":
    "It is possible to use multiple email addresses, by separating them with a comma (e.g. a@b.com,c@d.com).",
  "practice.notifications.target.teamsUrl.label": "Teams webhook URL",
  "practice.notifications.target.teamsUrl.info": "See the {linkTeams} or {linkOutlook} on how to set up notifications.",
  "practice.notifications.target.teamsUrl.linkTeams": "Teams documentation",
  "practice.notifications.target.teamsUrl.linkOutlook": "Outlook groups documentation",
  "practice.notifications.validation.selectAtLeastOne": "Select at least one option",
  "practice.notifications.validation.delayBothOrNone": "Fill in both the delay amount and the delay unit",
  "practice.notifications.validation.error": "Some required fields are missing. Check the opened notifications.",

  "practice.registrations.postalCodeRegex.title": "Postcode requirements for patient registration",
  "practice.registrations.postalCodeRegex.label": "Postcode regex",
  "practice.registrations.postalCodeRegex.info": "This needs to be a valid regular expression.",
  "practice.registrations.postalCodeRegex.infoForPractice":
    "The postcode area where the patient can register can be configured. Contact support@mijnpraktijk.nl, and mention the postcode area with numbers and letters.\nWe will configure the postcode requirements for you.",
  "practice.registrations.postalCodeRegex.validation": "Regex is invalid",
  "practice.registrations.postalCodeRegex.empty":
    "No postcode area has been configured. Patients can register to the practice from anywhere, which might not be intentional.",
  "practice.registrations.postalCodeRegex.invalidPattern": "This regex will accept all postcodes!",
  "practice.registrations.postalCodeRegex.tooManyMatches":
    "Your regex matches {count} postcodes. This may be too broad and could indicate an error in your pattern.",
  "practice.registrations.postalCodeRegex.showMap": "Show on map",
  "practice.registrations.postalCodeRegex.map.title": "Postcode coverage area",
  "practice.registrations.postalCodeRegex.map.infoTitle": "Approximate coverage",
  "practice.registrations.postalCodeRegex.map.infoText":
    "This map shows {count} matching postcodes grouped by PC4 areas (4-digit codes). Use this visualization to catch obvious errors in your regex pattern, not to verify coverage at the address level.",
  "practice.registrations.postalCodeRegex.map.noMatch": "No matches found",
  "practice.registrations.postalCodeRegex.map.empty": "No postcodes match this regex pattern",
  "practice.registrations.postalCodeRegex.map.error":
    "The postcode regex is invalid and cannot be displayed on the map",

  "postalCodeArea.mode.pc4": "postcode-4",
  "postalCodeArea.mode.pc6": "postcode-6",
  "postalCodeArea.regex.label": "Postcode-6 areas",
  "postalCodeArea.regex.info": "This needs to be a valid regular expression.",
  "postalCodeArea.regex.validation": "Regex is invalid",
  "postalCodeArea.regex.tooManyMatches":
    "Your regex matches {count} postcodes. This may be too broad and could indicate an error in your pattern.",
  "postalCodeArea.regex.showMap": "Show on map",
  "postalCodeArea.regex.map.title": "Postcode coverage area",
  "postalCodeArea.regex.map.infoTitle": "Approximate coverage",
  "postalCodeArea.regex.map.infoText":
    "This map shows {count} matching postcodes grouped by PC4 areas (4-digit codes). Use this visualization to catch obvious errors in your regex pattern, not to verify coverage at the address level.",
  "postalCodeArea.regex.map.noMatch": "No matches found",
  "postalCodeArea.regex.map.empty": "No postcodes match this regex pattern",
  "postalCodeArea.regex.map.error": "The postcode regex is invalid and cannot be displayed on the map",
  "postalCodeArea.p4.areas.title": "Postcode-4 areas",
  "postalCodeArea.p4.add": "Add postcode",
  "postalCodeArea.p4.remove": "Remove",
  "postalCodeArea.p4.placeholder": "1234",
  "postalCodeArea.p4.to.label": "up to and including",
  "postalCodeArea.p4.validation.fourDigits": "Enter exactly four digits",
  "postalCodeArea.p4.validation.toGreaterThanFrom": "The end must be greater than or equal to the start",
  "postalCodeArea.p4.map.error": "The postcode area cannot be displayed on the map",

  "practice.registrations.intro":
    "The settings above are general settings for registrations. Below you can edit the registration page itself. Here you can, among other things:",
  "practice.registrations.intro.openOrClosed": "Open or close registrations",
  "practice.registrations.intro.customQuestions": "Add your own questions",
  "practice.registrations.conditions": "Conditions for registration",
  "practice.registrations.idCheck.checkbox": "Digital ID check",
  "practice.registrations.idCheckMandatory.checkbox": "Require patients to try the digital ID check",
  "practice.registrations.idCheckMandatory.checkbox.info":
    "If the digital ID check fails, people can still complete the registration",
  "practice.registrations.allowLandlinePhoneNumbers.checkbox": "Accept Dutch landline numbers",
  "practice.registrations.allowForeignPhoneNumbers.checkbox": "Accept foreign telephone numbers",
  "practice.registrations.previousGpLetter.checkbox":
    "Send a letter to the previous GP on behalf of the patient. If the patient consents to transfer of their medical files, this is also stated in the letter.",
  "practice.registrations.previousGpLetter.subText":
    "The letter is automatically sent to the previous GP after you accept the patient to your practice.",
  "practice.registrations.showLspConsent.checkbox": "Show LSP consent question to patients",
  "practice.registrations.showLspConsent.link.href": "https://www.mijnmitz.nl/en",
  "practice.registrations.showLspConsent.link.title": "mijnmitz.nl",
  "practice.registrations.showLspConsent.info":
    "Disable this if your practice is affiliated with Mitz. Patients will see a link to {link} to manage their consent themselves.",
  "practice.registrations.afterAcceptance.label": "Actions after the patient is accepted",
  "practice.registrations.vecozoCov.checkbox": "Verification of health insurance information (COV check)",
  "practice.registrations.vecozoIon.checkbox": "Register patient via ION (registration by name)",
  "practice.registrations.vecozoIon.checkbox.subText":
    "Only if this option is checked will we fetch the previous GP of a patient.",
  "practice.registrations.vecozo.info":
    "To do this, you must first complete a consent form with Vecozo. {link1} and click 'Toestemmingsverklaring aan derden' and follow the link to log in. {link2} shows you how to proceed with the settings from there.",
  "practice.registrations.vecozo.info.link1": "Click here",
  "practice.registrations.vecozo.info.link2": "This film",
  "practice.registrations.sbvz.infoLink": "What is checked?",
  "practice.registrations.sbvz.info.intro":
    "At SBV-Z (the Sectorale Berichten Voorziening in de Zorg) we request the data the municipality has on record for the patient. We compare it with what the patient filled in themselves:",
  "practice.registrations.sbvz.info.point1": "Name, date of birth and citizen service number (BSN)",
  "practice.registrations.sbvz.info.point2": "The address as registered with the municipality",
  "practice.registrations.sbvz.info.point3":
    "Whether the provided identity document is valid and not reported missing or stolen",
  "practice.registrations.sbvz.info.note":
    "You will see the result of the check with the registration. Something doesn't match? You can still accept the patient, but verify the details yourself at the desk.",
  "practice.registrations.registrationAgb.label": "AGB code",
  "practice.registrations.registrationAgb.intro": "Choose from multiple practices after registration",
  "practice.registrations.registrationAgb.default": "If this is turned off, the AGB code {agbCode} will always be used",
  "practice.registrations.registrationAgb.agbCode": "AGB code",
  "practice.registrations.registrationAgb.name": "Name",
  "practice.registrations.registrationAgb.defaultValue": "Default practice",
  "practice.registrations.registrationAgb.validation": "AGB code must be digits only",
  "practice.registrations.registrationAgb.invalidLength": "AGB-code must be 8 numbers",
  "practice.registrations.registrationAgb.agbCode.required": "AGB code is required",
  "practice.registrations.communicationTemplates.title": "Customize emails to patients",
  "practice.registrations.communicationTemplates.description":
    "Go to {link} to edit the texts of the automated messages (such as emails and SMS).",
  "practice.registrations.communicationTemplates.link": "Communication templates",
  "practice.registrations.pages.title": "The registration page itself",
  "practice.registrations.pages.empty": "Create a site page of the 'Registration' type to allow patients to sign up",
  "practice.registrations.pages.add": "Add registration page",
  "practice.registrations.pages.edit": "Edit",
  "practice.registrations.pages.note":
    '(The website will only display a registration button when there is a registration site page and both "Open for registrations" and "Tile on website" are enabled)',

  "practice.feedback.dialog.title": "What do you need to reopen?",
  "practice.feedback.dialog.altTitle":
    "Your practice will close automatically after {limit} registrations. What do you need to reopen?",
  "practice.feedback.dialog.description": "On behalf of the care group, we are identifying where the bottlenecks are",

  "practice.feedback.dialog.skip": "Skip",
  "practice.feedback.dialog.submit": "Send",
  "practice.feedback.category.housing": "Housing",
  "practice.feedback.category.staff": "Staff (GPs)",
  "practice.feedback.category.assistants": "Staff (assistants/ supporting staff/ managing staff)",
  "practice.feedback.category.other": "Other",
  "practice.feedback.description.placeholder": "Fill in what you need",

  "practice.uzi.title": "UZI certificate",
  "practice.uzi.intro": "One certificate per practice. Used for DigiD login and the SBV-Z check.",
  "practice.uzi.publicCert": "Public file (public key)",
  "practice.uzi.publicCertInfo": "Example: praktijk_public.crt.pem",
  "practice.uzi.privateKey": "Secret file (private key)",
  "practice.uzi.privateKeyInfo": "Example: praktijk_private.key.pem",
  "practice.uzi.howToRequest": "How do I request a UZI certificate?",
  "practice.uzi.certPresent": "UZI certificate present",
  "practice.uzi.certPresentMeta": "Uploaded on {uploadedAt} · valid until {validUntil}",
  "practice.uzi.certPresentMetaSbvz":
    "Uploaded on {uploadedAt} · valid until {validUntil} · also used for the SBV-Z check",
  "practice.uzi.replace": "Replace certificate",
  "practice.uzi.upload": "Upload certificate",
  "practice.uzi.expiresSoon": "Certificate expires in {days, plural, one {# day} other {# days}}",
  "practice.uzi.expired": "Certificate has expired — upload a new certificate",
  "practice.uzi.replaceConfirm.title": "Replace certificate?",
  "practice.uzi.replaceConfirm.intro": "This UZI certificate is currently used by:",
  "practice.uzi.replaceConfirm.usage.digid": "DigiD login for patients",
  "practice.uzi.replaceConfirm.usage.sbvz": "SBV-Z check for registrations",
  "practice.uzi.replaceConfirm.note":
    "After replacing, these services immediately use the new certificate. The old certificate keeps working until the new one has been verified.",
  "practice.uzi.replaceConfirm.warning": "Upload the wrong certificate? Then these services may stop working.",
  "practice.uzi.replaceConfirm.confirm": "Yes, replace",
  "practice.uzi.howTo.intro":
    "Your practice probably already has a UZI server certificate — for example for the LSP. You need two files: the public file and the private file.",
  "practice.uzi.howTo.existing.title": "Your practice already has a certificate",
  "practice.uzi.howTo.existing.body":
    "The files are usually not inside your HIS itself: they were placed on the server during installation. Ask the party that supplies and manages your HIS — for example Medicom, Promedico, CGM or Tetra, or your own IT administrator — for a copy of the public file (.crt or .pem) and the private file (.key). Mention that you need them for MijnPraktijk.",
  "practice.uzi.howTo.existing.note":
    "Not sure whether your practice has a certificate? Your HIS supplier can check this at the UZI register using your practice's subscriber number (URA).",
  "practice.uzi.howTo.new.title": "No certificate yet? How to request one",
  "practice.uzi.howTo.new.leadTime": "Expect a lead time of several weeks.",
  "practice.uzi.howTo.new.step1":
    "Register your practice as a subscriber with the UZI register. You will receive a subscriber number (URA).",
  "practice.uzi.howTo.new.step2":
    "Ask the party that supplies your HIS to create the technical request file. You can also authorise them to handle the entire request for you.",
  "practice.uzi.howTo.new.step3":
    "Fill in the request form on uziregister.nl. Make sure you enter the correct domain name.",
  "practice.uzi.howTo.new.step4":
    "After the identity check you will receive the certificate by email. Upload the public and the private file here.",
  "practice.uzi.howTo.validity":
    "A certificate is usually valid for three years. Request a new certificate in time, so DigiD login and the SBV-Z check keep working.",
  "practice.uzi.howTo.more": "More information: {link}",
  "practice.uzi.howTo.more.link": "uziregister.nl/servercertificaat",

  "practice.digid.coupling": "DigiD connection",
  "practice.digid.adminOnly": "admin only",
  "practice.digid.entityId": "Entity ID",
  "practice.digid.entityIdPlaceholder": "Add the entity ID here",
  "practice.digid.entityIdInfo": "Example: urn:nl-eid-gdi:1.0:DV:00000009000213560000:entities:9652",
  "practice.digid.serviceId": "Service ID",
  "practice.digid.serviceIdPlaceholder": "Add the service ID here",
  "practice.digid.serviceIdInfo": "Example: 44f9c16d-99cd-47c2-abf1-f8b1857a4a7f",
  "practice.digid.status.hasPublicKey.success": "Public key uploaded",
  "practice.digid.status.hasPublicKey.error": "Public key has not been uploaded",
  "practice.digid.status.hasPrivateKey.success": "Secret key uploaded",
  "practice.digid.status.hasPrivateKey.error": "Secret key has not been uploaded",
  "practice.digid.status.isRequested.success": "DigiD request submitted",
  "practice.digid.status.isRequested.error": "Request for DigiD has not been submitted by MijnPraktijk.nl",
  "practice.digid.status.isProcessed.success": "Request processed by DigiD",
  "practice.digid.status.isProcessed.error":
    "Request has not been processed by DigiD. It can take a couple of days before it's activated",
  "practice.digid.status.operational.success": "DigiD is active",
  "practice.digid.status.operational.error": "DigiD disabled",
  "practice.digid.disable": "Disable DigiD",
  "practice.digid.disable.description":
    "Are you sure you want to disable DigiD? After this you need to upload your UZI certificate again and wait for the approval.",
  "practice.digid.disable.confirm": "Yes, disable DigiD",

  "practiceAuthorization.remove": "Delete authorization",
  "practiceAuthorization.remove.confirmation":
    "The user’s account will remain, but they will no longer have access to this practice’s data.\n\nYou can always add the colleague again using the ‘+ {addButtonLabel}’ button.",
  "practiceAuthorization.reactivate": "Reactivate",
  "practiceAuthorization.confirmation.title": "Do the right people still have access to {practiceName}?",
  "practiceAuthorization.confirmation.title.noConfigurationRights":
    "Access rights must be validated within {days} {days, plural, =1 {day} other {days}} for {practiceName}",
  "practiceAuthorization.confirmation.title.required.noConfigurationRights":
    "An administrator has to check the access rights for {practiceName}",
  "practiceAuthorization.confirmation.explanation":
    "These people can access patient data in MijnPraktijk. Every {months} months you must confirm whether these users should still have access.",
  "practiceAuthorization.confirmation.explanation.noConfigurationRights":
    "Ask an administrator to log in and confirm user access to {practiceName}.",
  "practiceAuthorization.confirmation.explanation.noConfigurationRights.warning":
    "You will lose access to MijnPraktijk when this doesn't happen in time.",
  "practiceAuthorization.confirmation.required": "User access must be validated before you can continue.",
  "practiceAuthorization.confirmation.confirm": "Yes, these access rights are correct",
  "practiceAuthorization.confirmation.close": "Close",
  "practiceAuthorization.confirmation.signout": "Sign Out",
  "practiceAuthorization.confirmation.postpone": "Postpone",
  "practiceAuthorization.confirmation.postpone.title": "You still have {days} {days, plural, =1 {day} other {days}}",
  "practiceAuthorization.confirmation.postpone.part1":
    "User access rights must be confirmed every {months} months. Otherwise, users will loose access to MijnPraktijk.",
  "practiceAuthorization.confirmation.postpone.part2": "We will remind you again next time you log in.",
  "practiceAuthorization.confirmation.postpone.postpone": "Postpone again",
  "practiceAuthorization.confirmation.postpone.back": "Back to checking",

  "practice.communicationTemplate.template.contact": "Contact or emergency",
  "practice.communicationTemplate.template.walkInConsultNotAvailable": "Walk-in consult - not available",
  "practice.communicationTemplate.template.walkInConsultQueueFull": "Walk-in consult - queue full",
  "practice.communicationTemplate.template.privacyStatement": "Privacy statement",
  "practice.communicationTemplate.template.emailAppointmentF2f": "Appointments - face-to-face",
  "practice.communicationTemplate.template.emailAppointmentPhone": "Appointments - phone",
  "practice.communicationTemplate.template.emailAppointmentVideo": "Appointments - video",
  "practice.communicationTemplate.template.emailAppointmentDeleted": "Appointments - deleted",
  "practice.communicationTemplate.template.emailAppointmentReminderVideo": "Appointments - video reminder",
  "practice.communicationTemplate.template.emailAppointmentReminderOther": "Appointments - other reminder",
  "practice.communicationTemplate.template.emailEconsultConfirmation": "E-consult - confirmation",
  "practice.communicationTemplate.template.emailEconsultReply": "E-consult - response",
  "practice.communicationTemplate.template.emailEconsultNew": "E-consult - new message",
  "practice.communicationTemplate.template.emailWalkInConsultConfirmation": "Walk-in consult - confirmation",
  "practice.communicationTemplate.template.emailMedicationConfirmation": "Medication - confirmation",
  "practice.communicationTemplate.template.emailMedicationApproved": "Medication - approved",
  "practice.communicationTemplate.template.emailMedicationRejected": "Medication - rejected",
  "practice.communicationTemplate.template.emailRegistrationVerify": "Registration - verification",
  "practice.communicationTemplate.template.emailRegistrationPreviousGpLetterReminder":
    "Registration - reminder signature previous GP letter",
  "practice.communicationTemplate.template.emailRegistrationPreviousGpLetterChanged":
    "Registration - previous GP changed, (re)sign letter",
  "practice.communicationTemplate.template.emailRegistrationConfirmation": "Registration - confirmation",
  "practice.communicationTemplate.template.emailRegistrationAccepted": "Registration - accepted",
  "practice.communicationTemplate.template.emailRegistrationRejected": "Registration - rejected",
  "practice.communicationTemplate.template.emailRegistrationCompleted": "Registration - completed",
  "practice.communicationTemplate.template.emailPatientWelcome": "Patient first time using the system",
  "practice.communicationTemplate.template.emailPageInvitation": "Page invitation",
  "practice.communicationTemplate.template.smsVideoAppointmentReminder": "SMS video appointment reminder",
  "practice.communicationTemplate.template.smsWalkInConsultAboutToStart": "SMS walk-in consult about to start",
  "practice.communicationTemplate.template.smsAnonymousEconsult": "SMS e-consult link after phone contact",
  "practice.communicationTemplate.template.econsultAnonymousGreeting": "E-consult chat message after phone contact",
  "practice.communicationTemplate.template.authNewAccount": "System user - new account",
  "practice.communicationTemplate.template.authPasswordReset": "System user - password reset request",
  "practice.communicationTemplate.template.authPasswordResetSharedEmail":
    "System user - password reset request (shared email)",
  "practice.communicationTemplate.template.authForgotUsername": "System user - forgot username",
  "practice.communicationTemplate.template.authPasswordChanged": "System user - password changed",
  "practice.communicationTemplate.template.authEmailChanged": "System user - email address changed",
  "practice.communicationTemplate.template.authDeactivation": "System user - account deactivated",
  "practice.communicationTemplate.template.authDeactivationHeadsUp": "System user - deactivation warning",
  "practice.communicationTemplate.template.authReactivation": "System user - account reactivated",
  "practice.communicationTemplate.template.authTotpReset": "System user - MFA reset",
  "practice.communicationTemplate.template.authMfa": "System user - 2FA code",
  "practice.communicationTemplate.template.authAccessRightsConfirmHeadsUp":
    "System user - access rights review reminder",
  "practice.communicationTemplate.template.authAccessRightsConfirmBlocked":
    "System user - access blocked (access rights)",
  "practice.communicationTemplate.template.authAccessRightsConfirmed": "System user - access rights reviewed",
  "practice.communicationTemplate.template.postalCodeWaitingListEmailVerification": "Waiting list - email verification",
  "practice.communicationTemplate.template.postalCodeWaitingListConfirmation":
    "Waiting list - confirmation of interest",
  "practice.communicationTemplate.template.practiceOpenNotification": "Waiting list - practice open for registration",
  "practice.communicationTemplate.template.notificationTarget": "Notification target",
  "practice.communicationTemplate.template.emailError": "Email error",
  "practice.communicationTemplate.template.digidRequested": "DigiD - requested",
  "practice.communicationTemplate.template.digidUpdated": "DigiD - updated",
  "practice.communicationTemplate.template.digidRemoved": "DigiD - removed",
  "practice.communicationTemplate.template.openClosedPracticeReminder": "Reminder to open closed practice",
  "practice.communicationTemplate.template.userInvitationInviteNew": "System user - invitation (new user)",
  "practice.communicationTemplate.template.userInvitationInviteExisting": "System user - invitation (existing user)",
  "practice.communicationTemplate.template.userInvitationInviteBare": "System user - invitation (no practice)",
  "practice.communicationTemplate.template.userInvitationWelcome": "System user - welcome",
  "practice.communicationTemplate.template.patientSwapInvitation": "Swap patients - transfer invitation",
  "practice.communicationTemplate.template.verify": "Patient account - email verification",
  "practice.communicationTemplate.template.mfa": "Patient account - MFA code",
  "practice.communicationTemplate.template.mfaResetSupportRequest": "Login Support - MFA code",
  "practice.communicationTemplate.template.passwordResetRequest": "Patient account - password reset request",
  "practice.communicationTemplate.template.passwordResetCompleted": "Patient account - password changed",
  "responseTemplates.type.practice": "Practice responses",
  "responseTemplates.type.default": "Default replies",

  "configuration.configurationAccess": "Administrator",
  "configuration.slug.warning": "WARNING: Changing the slug may result in patients not being able to find the website!",
  "configuration.slug.validation": "Slug may only contain letters, numbers and dashes",
  "configuration.slug.validationForbidden": "This slug is not allowed",

  "account.nav.myProfile": "My profile",
  "account.nav.changePassword": "Change password",
  "account.nav.2fa": "Two factor authentication",
  "account.changePassword.title": "Change Password",
  "account.changePassword.current": "Current Password",
  "account.changePassword.new": "New Password",
  "account.changePassword.new.placeholder": "Type a new password",
  "account.changePassword.confirm": "Confirm Password",
  "account.changePassword.btn": "Change password",
  "account.changePassword.confirm.validation.error": "Please confirm your password",
  "account.changePassword.notMatch.validation.error": "Passwords don't match",
  "account.changePassword.success": "Password is changed",
  "account.changePassword.passwordPolicy.minCharacters": `Password needs to have at least ${MIN_PASSWORD_LENGTH} characters`,
  "account.changePassword.passwordPolicy.letter": "Password needs to have at least one letter (a-z)",
  "account.changePassword.passwordPolicy.digit": "Password needs to have at least one digit (0-9)",
  "account.changePassword.passwordPolicy.specialCharacter":
    "Password needs to have at least one special character (e.g. !@#$%^&*)",

  "account.totp.title": "Two factor authentication (2FA)",
  "account.totp.btn": "Enable 2FA",
  "account.totp.intro1": "1. Open an authenticator app",
  "account.totp.intro1.info": "For example Microsoft Authenticator or Google Authenticator.",
  "account.totp.intro2": "2. Scan the QR code using the app",
  "account.totp.intro2.info": "With your authenticator app",
  "account.totp.qrcode.alt": "QR Code for 2FA",
  "account.totp.secret": "Then enter this code in your authenticator app:",
  "account.totp.secret.btn": "Can't scan the QR code?",
  "account.totp.success": "Two factor authentication has been enabled and must be used the next time logging in.",
  "account.totp.secret.tip":
    "Does the scanning itself work, but does the app tell you that your account already exists? In that case, remove your existing yet inactive old account from the Authenticator app.",
  "account.totp.alreadyEnabled1": "Two factor authentication has been enabled.",
  "account.totp.alreadyEnabled2":
    "It's not directly possible to disable this or change to another app. To be able to do this, contact support@mijnpraktijk.nl",

  "admin.users.tab.accounts": "Accounts",
  "admin.users.tab.invitations": "Invitations",
  "admin.users.edit": "Edit user",
  "admin.users.practices": "Practices",
  "admin.users.careGroups": "Care groups",
  "admin.users.isAdmin": "Is admin",
  "admin.users.blocked": "Blocked",
  "admin.users.blocked.reason": "This account is blocked. You can change this with the 'edit user' action",
  "admin.users.reactivate": "Reactivate account",
  "admin.users.mfa": "MFA",
  "admin.users.mfa.reset": "Reset MFA",
  "admin.users.mfa.reset.title": "Have you verified the identity of the person?",
  "admin.users.mfa.reset.confirm":
    "Call the practice using the phone number known to us. <b>Not</b> the phone number provided by the person requesting a reset. Only proceed if the practice confirms asking for a reset.",

  "admin.patientAccounts.email": "Email",
  "admin.patientAccounts.status": "Status",
  "admin.patientAccounts.practice": "Practice",
  "admin.patientAccounts.withDigiD": "DigiD",
  "admin.patientAccounts.portalAccountId": "Portal account ID",
  "hisPatientLinkStatus.pending": "Pending",
  "hisPatientLinkStatus.accepted": "Accepted",
  "hisPatientLinkStatus.rejected": "Rejected",
  "hisPatientLinkStatus.inactive": "Inactive",
  "hisPatientLinkStatus.notFound": "Not found",
  "hisPatientLinkStatus.duplicate": "Duplicate",
  "hisPatientLinkStatus.misconfigured": "Misconfigured",

  "admin.practices.slug": "Slug",
  "admin.practices.his": "HIS",
  "admin.practices.searchFacet.careGroupName": "Care group name",
  "admin.practices.searchFacet.fromEmail": "From email",
  "admin.practices.searchFacet.agbCode": "AGB code",
  "admin.practices.claimAccess": "Claim access",
  "admin.practices.claimAccessTitle": "Claim access for practice {practice}",
  "admin.practices.adminClaimReason": "Reason",
  "admin.practices.claimAccess.verify.title": "Confirm your identity",
  "admin.practices.claimAccess.verify.description": "Enter the 6-digit code from your authenticator app",
  "admin.practices.claimAccess.verify.label": "Verification code",
  "admin.practices.claimAccess.verify.continue": "Continue",
  "admin.practices.revokeAccess": "Revoke access",
  "admin.practices.delete.title": "Delete practice {practice}",
  "admin.practices.delete.confirmation":
    "This will remove the practice and all the associated patient records. The data cannot be recovered! Are you sure you want to remove this practice entirely?",
  "admin.practices.delete.success": "Practice deleted",

  "admin.authorizationClaims": "Authorization claims",
  "admin.authorizationClaims.untilDate": "Until date",
  "admin.authorizationClaims.user": "User",
  "admin.authorizationClaims.reason": "Reason",
  "admin.authorizationClaims.active": "Active",
  "admin.authorizationClaims.inactive": "Inactive",
  "admin.auditTrails.date": "Date",
  "admin.auditTrails.action": "Action",
  "admin.auditTrails.eventType": "Type",
  "admin.auditTrails.message": "Message",
  "admin.auditTrails.notSuccessful": "Not successful",
  "admin.auditTrails.entity": "Entity",
  "admin.auditTrails.user": "User",
  "admin.auditTrails.person": "Person",
  "admin.auditTrails.practice": "Practice",
  "admin.auditTrails.searchPerson": "Search person",
  "admin.auditTrails.searchUser": "Search user",
  "admin.auditTrails.action.C": "Create",
  "admin.auditTrails.action.R": "Read",
  "admin.auditTrails.action.U": "Update",
  "admin.auditTrails.action.D": "Delete",
  "admin.auditTrails.action.E": "Execute",
  "admin.auditTrails.entity.registrationRequest": "Registration request",
  "admin.auditTrails.userPracticeAccess.none": "No access",
  "admin.auditTrails.userPracticeAccess.regularAccess": "Regular access",
  "admin.auditTrails.userPracticeAccess.configurationAccess": "Administrator",

  "admin.text.edit": "Edit",
  "admin.editModeEnabled": "Edit mode enabled",
  "admin.editModeDisabled": "Edit mode disabled",
  "admin.editModeHelperText": "Only admins can do this. Changes are only possible when enabled.",

  "general.policy": "Privacy & Policy",
  "general.terms": "Terms & Conditions",

  "fileModal.close.btn": "Close",
  "fileModal.save.btn": "Save",
  "fileModal.save.success": "The file has been saved; see your downloads folder.",

  "print.sensitiveData":
    "This printed document from MijnPraktijk may contain personal health information about patients. Please handle this with care and share information only with appropriate healthcare providers.",

  "helpWidget.help": "Help",
  "helpWidget.title": "What do you have a question about?",
  "helpWidget.technical": "Something on this site is not working",
  "helpWidget.medical": "Medical question",
  "helpWidget.other": "Other question or issue",
  "helpWidget.manuals": "Look at our manuals",

  "portal.changeNotInHis": "This change will not be processed in the HIS.",
  "portal.entity.notFound.title": "Not found",
  "portal.entity.notFound.description":
    "This page is no longer accessible. It may have been deleted, or you may no longer have access.",
  "button.previous": "Previous",
  "button.next": "Next",
  "button.send": "Send",
  "button.sending": "Sending...",
  "button.call": "Call me now",
  "button.save": "Save",
  "button.cancel": "Cancel",
  "button.download": "Download",
  "button.continue": "Continue",
  "button.continueAnyway": "Proceed anyway",
  "button.back": "Back",
  "button.close": "Close",
  "button.clear": "Clear",
  "button.confirm": "Confirm",

  "filePreview.invalid": "This file appears to be invalid or corrupt and cannot be displayed.",
  "filePreview.pdfError": "PDF error: {error}",
  "filePreview.page": "Page",

  livePreview: "Live preview",

  "search.placeholder": "Search",
  "search.searching": "Searching...",
  "search.noResults": "No results",
  "search.agb.noResults": "No results. Try an address or city name",
  "search.placeholderHint": "Type to search",
  "search.remainingResults": "And {remaining} more results",

  "login.loggedOut": "You've been logged out. Enter your credentials to log in again.",
  "login.title": "Want to know more about MijnPraktijk.nl?",
  "login.privacyPolicy": "Our privacy policy (in Dutch)",
  "login.text.1": "About MijnPraktijk.nl",
  "login.text.2": ", or visit {link}",
  "login.asProfessional": "Log in as healthcare professional",
  "login.submit": "Log in",
  "login.email": "Email",
  "login.email.placeholder": "Enter your email",
  "login.username": "Username",
  "login.username.placeholder": "Enter your username or email",
  "login.password": "Password",
  "login.password.placeholder": "Enter your password",
  "login.storeCredentials": "Stay signed in for a maximum of 7 days",
  "login.storeCredentials.info":
    "Without this option your session ends when you close the browser. Do not use this on shared or public computers.",
  "login.forgotpassword": "Forgot Password?",
  "login.canNotLogin": "I can’t sign in",
  "login.forgot": "Forgot?",

  // Login support flow
  "loginSupport.deactivated.title": "Account deactivated?",
  "loginSupport.deactivated.description": "This happens when you haven’t signed in for a while.",
  "loginSupport.deactivated.action": "Reactivate account",
  "loginSupport.deactivated.detail":
    "<strong>Ask an administrator</strong> (usually the practice manager) to reactivate your account. Are you the only administrator? Then please contact our support.",
  "loginSupport.forgotPassword.title": "Forgot your password?",
  "loginSupport.forgotPassword.description":
    "Enter your username or email address. We will send instructions to your email to reset your password.",
  "loginSupport.forgotPassword.conditions":
    "<ul><li><strong>If</strong> the email address is known to us</li><li><strong>If</strong> the account is still active</li></ul>",
  "loginSupport.forgotPassword.action": "Reset password",
  "loginSupport.forgotPassword.success":
    "If the account exists, an email has been sent with a link to reset your password.",
  "loginSupport.forgotPassword.submit": "Send reset link",
  "loginSupport.forgotUsername.title": "Forgot your username?",
  "loginSupport.forgotUsername.description":
    "Enter your email address. If it is known to us, we will email you your username.",
  "loginSupport.forgotUsername.conditions":
    "<ul><li><strong>If</strong> the email address is known to us</li><li><strong>If</strong> the account is still active</li></ul>",
  "loginSupport.forgotUsername.action": "Request username",
  "loginSupport.forgotUsername.email": "Email address",
  "loginSupport.forgotUsername.success":
    "If the account exists, an email has been sent containing your username or usernames.",
  "loginSupport.forgotUsername.submit": "Request",
  "loginSupport.mfaReset.title": "Reset two-step verification",
  "loginSupport.mfaReset.description":
    "Maybe because you are signing in from a new device, or because you are using a new authenticator app",
  "loginSupport.mfaReset.action": "Reset",
  "loginSupport.mfaReset.email": "Email address (of the relevant account)",
  "loginSupport.mfaReset.practice": "To which practice are you trying to sign in?",
  "loginSupport.mfaReset.additionalInfo": "Is there anything else you would like to add?",
  "loginSupport.mfaReset.success": "Your request has been submitted. We will contact you as soon as possible.",
  "loginSupport.mfaReset.submit": "Contact support to reset it",
  "loginSupport.mfaReset.explanation":
    "<ol><li><strong>Support will call you</strong><p>On the phone number known to us, or of the practice to which you have access. This is to verify it is indeed you who asked for a reset.</p></li><li><strong>Did it succeed?</strong><p>Then support will reset your account, which allows you to reset your two-step verification.</p></li></ol>",
  "loginSupport.other.title": "Something else?",
  "loginSupport.other.description": "We’re happy to help.",
  "forgotPassword.email.placeholder": "Email address",
  "loginSupport.other.link": "Contact support",
  "login.validation.passwordInvalid": "Please input your password",
  "login.totp.message": "Enter the 6-digit code from your authenticator app",
  "login.totp.label": "6-digit code from your authenticator app",
  "login.mfa.message": "Enter the 6-digit code sent to your email address",
  "login.mfa.label": "6-digit code from your email",
  "login.error.credentials": "Signing in failed. Check your username and password.",
  "login.error.credentials.usernameNotEmail":
    "Signing in failed. Check your password and your <b>username</b>. It may be different from your email address.",
  "login.error.totp": "Incorrect token",
  "login.error.mfa": "Incorrect email code",
  "login.error.mfaMaxAttempts":
    "Too many incorrect codes. Please re-enter your password to receive a new code by email.",
  "login.error.invalidCredentials": "Invalid credentials",
  "login.error.tooManyAttempts":
    "Login temporarily unavailable. There were too many failed login attempts. Please wait 5 minutes and try again.",
  "login.error.tokenExpired": "The token expired. Log in again to retrieve a new one.",
  "login.error.duplicateEmail":
    "There are multiple accounts linked to this email due to a technical issue. Please contact support@mijnpraktijk.nl to have this resolved. In case you and other family member share the same email address, please let us know.",
  "login.error.inactive":
    "Your account is deactivated due to inactivity. Ask an administrator in your practice to reactivate your account.",
  "login.sensitiveDataNotification":
    "MijnPraktijk contains personal health information about patients. Please handle this with care and share information only with appropriate healthcare providers.",
  "login.mijnpraktijk": "Log in using MijnPraktijk",
  "login.digid": "Log in using DigiD",
  "login.digid.cancelled": "Logging in with DigiD was cancelled.",
  "login.digid.minimumAuthenticationLevelNotReached":
    "Logging in for this organisation was unsuccessful. The security level of your DigiD account is insufficient. You can link your phone number via Mijn DigiD to improve this.",
  "login.digid.unknownError":
    "Logging in for this organisation was unsuccessful. Please try again later. Still having issues? Log in at Mijn DigiD. This way, you check if your DigiD is working properly. There may be a problem with the organisation you are logging in to.",
  "login.digid.patientNotFound": "Logging in with DigiD was successful, but your BSN is not yet known to us.",
  "login.digid.patientNotFound.otherMethod": "Log in via 'Log in using MijnPraktijk' and fill in your BSN.",
  "login.digid.patientNotFound.register": "Not a patient yet? Register {link}.",
  "login.digid.patientNotFound.register.link": "here",
  "login.digid.patientNotFound.contact": "Still not working? Please contact your practice.",

  "forgotPassword.email.label": "Your email address",
  "forgotPassword.identifier": "Username or email",
  "forgotPassword.identifier.placeholder": "Enter your username or email",
  "forgotPassword.message.success": "Reset password link has been sent to your email address {email}.",
  "forgotPassword.message.checkEmail": "Please check your email to continue.",
  "forgotPassword.title": "Forgot password?",
  "forgotPassword.text":
    "Enter your username or email address. We will send instructions to your email to reset your password.",
  "forgotPassword.rememberPassword": "Remembered your password?",
  "forgotPassword.backToLogin": "Back to log in",
  "forgotPassword.success.title": "Check your inbox",
  "forgotPassword.success.text":
    "If an account matches, we've sent instructions to reset your password. A link is only valid for 30 minutes, so do not wait too long.",
  "resetPassword.reset": "Reset",
  "resetPassword.message.success": "Your password has been updated",
  "resetPassword.message.successLogin": "Log in with your new password.",
  "resetPassword.title": "Reset password",
  "resetPassword.requestNew": "Please request a new password reset.",
  "resetPassword.error.duplicatePassword":
    "You have already used this password before. Please choose a different password.",

  "confirmAccess.success.title": "Thanks for confirming your access to MijnPraktijk!",
  "confirmAccess.success.description": "You can close this page",
  "confirmAccess.error.title": "Something went wrong",
  "confirmAccess.error.description": "We could not confirm your access. Please log in again.",

  "person.contactInfo": "Contact information",
  "person.gender": "Gender",
  "person.gender.info": "As shown in your passport",
  "person.gender.male": "Male",
  "person.gender.female": "Female",
  "person.gender.unknown": "N/A",
  "person.firstName": "First name",
  "person.affix": "Prefix last name",
  "person.lastName": "Last name",
  "person.dateOfBirth": "Date of birth",
  "person.dateOfBirth.placeholder": "DD-MM-YYYY",
  "person.dateOfBirth.validation": "Please enter a valid date of birth",
  "person.placeOfBirth": "Place of birth",
  "person.language": "Registration language",

  "person.bsn": "Citizen service number (BSN)",
  "person.bsn.validation": "BSN number is incorrect",
  "person.bsn.required": "Citizen service number (BSN) is required",
  "person.phone": "Phone number",
  "person.mobilePhone": "Mobile phone number",
  "person.mobilePhone.validation": "Please enter a valid mobile phone number",
  "person.mobilePhone.validationNL":
    "Dutch mobile number must {requiresPrefix, select, true {start with 6 and } other {}}have 9 digits",
  "person.mobilePhone.validationServiceNumber.disabled": "Service numbers are not allowed",
  "person.mobilePhone.validationServiceNumber.invalid": "Please enter a valid service number",
  "person.mobilePhone.disabledForeignPhoneNumbers":
    "We will use this number to contact you. We only accept Dutch numbers.",
  "person.address": "Postal address",
  "person.street": "Street",
  "person.houseNumber": "House number",
  "person.houseNumber.withoutAddition": "(without addition)",
  "person.houseNumber.placeholder": "For example: 42",
  "person.houseNumber.validation": "Please enter a valid house number",
  "person.houseNumberAddition": "House number addition",
  "person.houseNumberAddition.info": "For example, A, II, bis, etc. Can be left empty if not applicable.",
  "person.houseNumberAddition.placeholder": "Addition",
  "person.houseNumberFull": "House number (with addition)",
  "person.houseNumberFull.placeholder": "For example: 123A",
  "person.houseNumberFull.validation": "Please enter a valid house number",
  "person.houseNumberFull.required": "House number (with addition) is required",

  // We use the Dutch sounding 'postcode' for English as well
  // https://mijnpraktijknl.atlassian.net/wiki/spaces/IT/pages/95579457519618/Persona+MijnPraktijk.nl#Patient-side
  "person.postalCode": "Postcode",

  "person.postalCode.placeholder": "For example: 1234 AB",
  "person.postalCode.validation": "Please enter a valid postcode",
  "person.postalCode.partialValidation": "Enter a complete postcode with digits and letters",
  "person.postalCode.postalCodeRegex":
    "This practice only accepts registrations from specific postcode areas. Your postcode is outside this area, so unfortunately you cannot register here. If you believe an exception may be possible, please contact the practice.",
  "person.city": "City",
  "person.autoFillAfterPostalCode": "Automatically filled after entering postcode and house number",
  "person.postalCodeHouseNumberNotFound": "Combination of postcode and house number not found",
  "person.email": "Email address",

  "plato.model.search": "What is your medical complaint or question?",
  "plato.model.search.placeholder": "For example: 'skin complaints' or 'vaccination'",
  "plato.model.search.results": "Complaints",
  "plato.model.search.active": "Search results",
  "plato.model.noResults.tip1": "Other search term",
  "plato.model.noResults.tip1.info": "For example 'headache' instead of 'migraine'.",
  "plato.model.noResults.tip2": "Shorter word",
  "plato.model.noResults.tip2.info": "For example 'urine' instead of 'urine problem'.",
  "plato.model.noResults.tip3": "Check spelling",
  "plato.model.cannotFind": "I cannot find my complaint",
  "plato.model.cannotFind.title": "No search results",
  "plato.model.cannotFind.altTitle": "Can't find what you're looking for?",
  "plato.model.cannotFind.info": "Maybe this helps:",
  "plato.model.cannotFind.alternative": "Still no success?",
  "plato.model.cannotFind.alternative.link": "View the general questionnaire",
  "plato.model.cannotFind.alternative.title": "Please note: Choosing the right complaint helps us help you better.",
  "plato.model.cannotFind.alternative.notice": "This allows us to ask more targeted questions.",
  "plato.model.emergency": "Is it life-threatening? Call 112",
  "plato.model.emergency.practice": "Is it life-threatening? Call 112 or our emergency number: {phoneNumber}.",
  "plato.model.complaints.mostChosen": "Most chosen complaints",
  "plato.administrative.title": "Questions",
  "plato.administrative.placeholder": "For example: 'reschedule' or 'vaccination'",
  "plato.administrative.unavailable": "No actions found",
  "plato.administrative.all": "All actions",
  "plato.localeChange.close": "Close",
  "readAloud.label": "Read aloud",
  "readAloud.pause": "Pause",
  "readAloud.previous": "Previous",
  "readAloud.next": "Next",

  "plato.localeChange.confirm": "Start over",
  "plato.localeChange.info":
    'Questionnaire has already been started in {oldLanguage}. Click the "Start over" button to start over in {newLanguage}.',
  "plato.advice": "Our recommendation",
  "plato.advice.contact": "Phone number: {phoneNumber}",
  "plato.advice.contact.immediate": "Call immediately",
  "plato.advice.contact.immediate.desc":
    "Based on your answers, we advise you not to wait. Call the practice immediately.",
  "plato.advice.contact.soon": "Call our practice",
  "plato.advice.contact.soon.desc": "Based on your answers, we advise you to call us.",
  "plato.advice.contact.button": "Call",
  "plato.advice.contact.page": "<contactLink>Get in touch</contactLink> with the practice",
  "plato.advice.contact.page.button": "Get in touch",
  "plato.advice.hap.contact": "Contact your after-hours general practice center (Dutch: huisartsenpost).",
  "plato.advice.hap.immediate": "Call the general practice center immediately",
  "plato.advice.hap.immediate.desc":
    "Based on your answers, we advise you not to wait. Call the after-hours general practice center (Dutch: huisartsenpost) immediately.",
  "plato.advice.hap.soon": "Call the after-hours general practice center",
  "plato.advice.hap.soon.desc":
    "Based on your answers, it is possible that your health concern needs to be assessed today. The after-hours general practice center (Dutch: huisartsenpost) will ask you questions to determine what needs to be done regarding your health concern.",
  "plato.advice.hap.within.24h": "Call the after-hours general practice center",
  "plato.advice.hap.within.24h.desc":
    "Based on your answers, it is possible that your health concern needs to be assessed within 24 hours. The after-hours general practice center (Dutch: huisartsenpost) will ask you questions to determine what needs to be done regarding your health concern.",
  "plato.advice.homeAdvice.expand": "Read the home care advice",
  "plato.advice.appointment.expand": "Make an appointment {start, select, true {now} other {}}",
  "plato.advice.faceToFace": "Visit the practice {sameDay, select, true {today} other {within {within}}}",
  "plato.advice.within.24h": "24 hours",
  "plato.advice.within.3d": "3 days",
  "plato.advice.within.5d": "5 days",
  "plato.advice.within.2w": "2 weeks",
  "plato.advice.econsult": "Send a message",
  "plato.advice.econsult.desc":
    "We include your answers in the message. You will receive an email when we have responded.",
  "plato.advice.econsult.expand": "{start, select, true {Send message} other {Continue}}",
  "plato.advice.faceToFace.expand": "Schedule an appointment at the practice",
  "plato.advice.homeAdvice": "Instructions",
  "plato.advice.homeAdvice.desc": "Read what you can do yourself and what to watch out for.",
  "plato.advice.other.desc":
    "Please make an appointment {sameDay, select, true {today} other {within the next {within}}}.",
  "plato.advice.other.adviceType.homeAdvice": "Home care advice",
  "plato.advice.other.adviceType.video": "Video appointment",
  "plato.advice.other.adviceType.phone": "Phone appointment",
  "plato.advice.other.adviceType.econsult": "Online consultation",
  "plato.advice.other.adviceType.faceToFace": "Appointment",
  "plato.advice.other.adviceType.walkInConsult": "Walk-in consultation",
  "plato.advice.otherOptions": "Other options",
  "plato.advice.otherOptions.desc": "Would you prefer a different option? Choose from:",
  "plato.advice.otherOptions.followUp.faceToFace": "Schedule an appointment at the practice",
  "plato.advice.otherOptions.followUp.video": "Schedule a video appointment",
  "plato.advice.otherOptions.followUp.phone": "Schedule a phone appointment",
  "plato.advice.otherOptions.followUp.econsult": "Start an online consultation",
  "plato.advice.otherOptions.followUp.walkInConsult": "Visit the walk-in consultation",
  "plato.advice.emergencyDisclaimer.title": "Is it life-threatening?",
  "plato.advice.emergencyDisclaimer.prose": "<ul><li>Call 112</li><li>Or our emergency number: {phoneNumber}</li></ul>",

  "plato.advice.changeDisclaimer.title": "Are your symptoms getting worse or different?",
  "plato.advice.changeDisclaimer.prose": "<ul><li>Then please contact us again.</li></ul>",

  "plato.advice.moreInfo": "More information",
  "plato.advice.closingSoon.title": "The practice is closing soon",
  "plato.advice.closingSoon.text1":
    "We do not handle patient messages outside of office hours (evenings, nights, weekends).",
  "plato.advice.closingSoon.text2": "To make sure your question gets answered in time, please call the practice.",

  "plato.urgency.placeholder": "Select urgency",

  "plato.scale.1":
    "<p><b>1. Mild pain</b></p><p>The pain is barely noticeable and I can still do everything I normally do.</p>",
  "plato.scale.2":
    "<p><b>2. Mild pain</b></p><p>The pain is noticeable, but I can still do everything I normally do.</p>",
  "plato.scale.3":
    "<p><b>3. Mild pain</b></p><p>The pain is very noticeable, but I can still do everything I normally do.</p>",
  "plato.scale.4": "<p><b>4. Moderate pain</b></p><p>The pain distracts me, I notice it during the things I do.</p>",
  "plato.scale.5":
    "<p><b>5. Moderate pain</b></p><p>The pain distracts me a lot, causing me to be unable to do some things.</p>",
  "plato.scale.6":
    "<p><b>6. Moderate pain</b></p><p>The pain distracts me a lot, causing me to be unable to do several things.</p>",
  "plato.scale.7": "<p><b>7. Severe pain</b></p><p>I can hardly do anything I normally do.</p>",
  "plato.scale.8": "<p><b>8. Severe pain</b></p><p>I can do nothing I normally do or it is very difficult.</p>",
  "plato.scale.9": "<p><b>9. Severe pain</b></p><p>I can do nothing I normally do.</p>",
  "plato.scale.10":
    "<p><b>10. Worst pain ever</b></p><p>I can do nothing I normally do. The pain overshadows everything.</p>",

  "registrationRequests.title": "Registration requests",
  "registrationRequests.sameAddress.title": "Same address",
  "registrationRequests.bulkDelete.count":
    "{count, plural, =1 {# registration selected} other {# registrations selected}}",
  "registrationRequests.bulkDelete.deselect": "Clear selection",
  "registrationRequests.bulkDelete.modal.title": "Delete registration requests",
  "registrationRequests.bulkDelete.modal.body":
    "{count, plural, =1 {# registration request} other {# registration requests}} will be permanently deleted. Personal data for rejected and undecided registrations will be anonymised. This action cannot be undone.",
  "registrationRequests.bulkDelete.modal.hisCheckbox":
    "I confirm that all relevant patient data has been transferred to the HIS",
  "registrationRequest.edit": "Edit",
  "registrationRequest.person.title": "Personal information",
  "registrationRequest.person.age": "Age",
  "registrationRequest.person.years": "years",
  "registrationRequest.checklist.title": "Checklist",
  "registrationRequest.process.title": "Registration",
  "registrationRequest.process.placeOfBirth": "Place of birth",
  "registrationRequest.previousGp.label": "Previous GP",
  "registrationRequest.previousGp.requestApproval": "Permission file transfer",
  "registrationRequest.previousGp.requestApproval.passant": "Not asked (passant)",
  "registrationRequest.previousGp.patientCannotFind": "Patient couldn't find previous GP",
  "registrationRequest.previousGp.patientHadNone": "Patient never had a GP in the Netherlands",
  "registrationRequest.previousGp.source.ion": "Automatically retrieved via ION foundation",
  "registrationRequest.previousGp.source.patient": "Filled in by patient",
  "registrationRequest.previousGp.ion": "Adjusted following ION registration",
  "registrationRequest.previousGp.ion.unknown": "Could not determine previous GP",
  "registrationRequest.previousGp.ion.disabled":
    "The previous GP could not be looked up because 'Register patient via ION' was disabled in the settings.",
  "registrationRequest.previousGp.fetchedAfterAcceptance":
    "Will be fetched automatically after the patient is accepted",

  "registrationRequest.previousGp.covAlert.disabled.title":
    "Adjust your settings to receive the previous GP from the ION Foundation",
  "registrationRequest.previousGp.covAlert.disabled": `After the registration in MijnPraktijk has been accepted, you can receive the name of the previous GP practice from the ION Foundation.

To do this, go to: settings > Registrations > Register patient via ION, and enable it.`,

  "registrationRequest.previousGp.covAlert.alreadyPatient.title":
    "ION indicates that this person is already a patient at your practice",
  "registrationRequest.previousGp.covAlert.alreadyPatient": `This may be because:

The patient has registered a second time, or...
You registered this person with ION first (e.g. via your HIS) and only then accepted them in MijnPraktijk.

Make sure you accept the registration in MijnPraktijk first, and only then add them in the HIS and ION.

This way, the correct previous GP will automatically receive a deregistration request and a request for record transfer.`,

  "registrationRequest.previousGp.covAlert.unauthorized.title":
    "Grant MijnPraktijk permission in Vecozo to receive the previous GP from the ION Foundation",
  "registrationRequest.previousGp.covAlert.unauthorized": `Have you done this before? The permission may have since expired.

{permissionLink} on 'Declaration of consent to third parties' and follow the link to log in.

{videoLink} shows how to continue with the setup from here.`,

  "registrationRequest.previousGp.covAlert.unauthorized.vecozoLink.permissions": "Click here",
  "registrationRequest.previousGp.covAlert.unauthorized.vecozoLink.video": "This video",

  "registrationRequest.previousGp.covAlert.notFoundCov.title": "No previous GP known at the ION Foundation",
  "registrationRequest.previousGp.covAlert.notFoundCov":
    "This occurs, among other things, when a patient is not insured in the Netherlands.",
  "registrationRequest.previousGpMessage.label": "Signed letter sent to previous GP",

  "registrationRequest.previousGpMessageLetter.null": "Disabled",
  "registrationRequest.previousGpMessageLetter.disabled": "Disabled",
  "registrationRequest.previousGpMessageLetter.ionDisabled":
    "Cannot send letter: ION is required to find the previous GP",
  "registrationRequest.previousGpMessageLetter.ok": "Letter sent",
  "registrationRequest.previousGpMessageLetter.differentIonGpWaitingForPatient":
    "ION indicated different previous GP than patient specified",
  "registrationRequest.previousGpMessageLetter.noPreviousGp": "Letter not sent: No previous GP found via ION",
  "registrationRequest.previousGpMessageLetter.notSigned": "Letter not sent",
  "registrationRequest.previousGpMessageLetter.noZorgMailAccount":
    "Cannot send letter: The previous GP has no ZorgMail",
  "registrationRequest.previousGpMessageLetter.zorgMailBounce":
    "Letter delivery failed {retrying, select, true {(Retrying...)} other {(Bounced)}}",
  "registrationRequest.previousGpMessageLetter.error": "Letter not sent: Unknown error",
  "registrationRequest.previousGpMessageLetter.error.expired": "Previous GP did not approve the request in time",
  "registrationRequest.previousGpMessageLetter.error.sendFailed": "Sending failed",
  "registrationRequest.previousGpMessageLetter.pending": "Awaiting approval",
  "registrationRequest.previousGpMessageLetter.pending.expired": "Request expired",
  "registrationRequest.previousGpMessageLetter.pending.sendFailed": "Sending failed",
  "registrationRequest.previousGpMessageLetter.notApproved": "Letter not sent: The patient does not approve",

  "registrationRequest.previousGpMessageResult.null": "Disabled",
  "registrationRequest.previousGpMessageResult.disabled": "Disabled",
  "registrationRequest.previousGpMessageResult.ionDisabled":
    "The previous GP could not be looked up because ION was disabled",
  "registrationRequest.previousGpMessageResult.ok": "Previous GP found",
  "registrationRequest.previousGpMessageResult.differentIonGpWaitingForPatient":
    "ION indicated different previous GP than patient specified",
  "registrationRequest.previousGpMessageResult.noPreviousGp": "No previous GP known at ION",
  "registrationRequest.previousGpMessageResult.notSigned": "Letter not sent",
  "registrationRequest.previousGpMessageResult.noZorgMailAccount": "GP does not have a ZorgMail account",
  "registrationRequest.previousGpMessageResult.zorgMailBounce": "ZorgMail couldn't be delivered (Bounced)",
  "registrationRequest.previousGpMessageResult.error":
    "Something went wrong when retrieving the previous GP via ION (unknown error)",
  "registrationRequest.previousGpMessageResult.error.expired": "Previous GP did not approve the request in time",
  "registrationRequest.previousGpMessageResult.error.sendFailed": "Sending failed",
  "registrationRequest.previousGpMessageResult.pending": "Awaiting approval",
  "registrationRequest.previousGpMessageResult.pending.expired": "Request expired",
  "registrationRequest.previousGpMessageResult.pending.sendFailed": "Sending failed",
  "registrationRequest.previousGpMessageResult.notApproved": "The patient does not approve",

  "registrationRequest.insurance.insurer": "Insurer",
  "registrationRequest.insurance.notAsked.passant": "Not asked (passant without BSN)",
  "registrationRequest.pharmacy.notAsked": "Not asked",
  "registrationRequest.insurance.name": "Name health insurer",
  "registrationRequest.insurance.number": "Account number",
  "registrationRequest.rejectionReason": "Rejection reason",
  "registrationRequest.patientType.label": "Patient type",
  "registrationRequest.patientType.regular": "Regular patient",
  "registrationRequest.patientType.passant": "Passant",
  "registrationRequest.passant.alert":
    "Passant: this person stays registered with their own GP. Accepting does not register them in ION; the registration is sent to the HIS, mark the patient as a passant there.",
  "registrationRequest.passant.accept.title": "Accept passant",
  "registrationRequest.passant.accept.description":
    "This person is a passant and stays registered with their own GP. MijnPraktijk does not register the patient in ION. The registration is sent to the HIS: mark the patient as a passant there yourself, otherwise the HIS will still send an ION registration message.",
  "registrationRequest.passant.accept.confirm": "Accept",
  "registrationRequest.passant.accept.hisInstruction.default":
    "Mark this patient as a passant in the HIS so that no ION registration takes place.",
  "registrationRequest.passant.accept.hisInstruction.medicom": "In Medicom, tick 'incidenteel' on the patient.",
  "registrationRequest.passant.accept.hisInstruction.promedico":
    "In Promedico, change the status from 'Vaste patiënt' to Passant (or Waarneming / Tijdelijk, depending on the situation).",
  "registrationRequest.rejectionReason.title": "Rejection reason",
  "registrationRequest.rejectionReason.reason": "Reason",
  "registrationRequest.rejectionReason.prompt": "Enter a rejection reason",
  "registrationRequest.rejectionReason.reason.info":
    "Please note that the reason for rejection will be sent to the patient by email. Don't mention any medical information.",
  "registrationRequest.rejectionReason.placeholder": "Optional rejection reason",
  "registrationRequest.rejectionReason.reject": "Reject",
  "registrationRequest.agbCode.title": "AGB code for registration",
  "registrationRequest.agbCode.description1": "To accept this patient, you need to select an AGB code.",
  "registrationRequest.agbCode.description2": "Which practice will this patient be registered to?",
  "registrationRequest.agbCode.placeholder": "Select practice",
  "registrationRequest.agbCode.confirm": "Select",
  "registrationRequest.hisKnown.title": "Patient already known in HIS",
  "registrationRequest.hisKnown.description":
    "This patient is already known in the HIS. When accepted, the HIS might have a duplicate patient.",
  "registrationRequest.hisKnown.areYouSure": "Are you sure you want to accept this patient?",
  "registrationRequest.hisKnown.confirm": "Accept",
  "registrationRequest.vecozoCov.label": "COV insurance check",
  "registrationRequest.pharmacyAgb": "AGB code: {agbCode}",
  "registrationRequest.vecozoCovResult.disabled": "Not performed",
  "registrationRequest.vecozoCovResult.noBsn": "Not performed: no BSN",
  "registrationRequest.vecozoCovResult.ok": "COV check approved",
  "registrationRequest.vecozoCovResult.ok.subText": "Patient is insured in the Netherlands.",
  "registrationRequest.vecozoCovResult.inactiveInsurance": "Patient has no active insurance in the Netherlands",
  "registrationRequest.vecozoCovResult.bsnUnknown": "Something is wrong — check manually",
  "registrationRequest.vecozoCovResult.bsnUnknown.subText": "The patient's BSN is unknown at Vecozo.",
  "registrationRequest.vecozoCovResult.bsnIncorrect": "Something is wrong — check manually",
  "registrationRequest.vecozoCovResult.bsnIncorrect.subText": "The patient's BSN is not a valid BSN number.",
  "registrationRequest.vecozoCovResult.combinationBsnDateOfBirthUnknown": "Combination BSN and date of birth unknown",
  "registrationRequest.vecozoCovResult.multiplePersonsFound": "Multiple persons found",
  "registrationRequest.vecozoCovResult.notFound": "Person not found",
  "registrationRequest.vecozoCovResult.error": "General error",
  "registrationRequest.vecozoCov.checkedAt": "Checked via Vecozo {date}",
  "registrationRequest.vecozoCov.disabledAlert.title":
    "Adjust your settings to receive the COV insurance check from Vecozo.",
  "registrationRequest.vecozoCov.disabledAlert":
    "To do this, go to: settings > Registrations > Actions after the patient is accepted",
  "registrationRequest.vecozoCov.retrySuccess": "COV check retried successfully",
  "registrationRequest.resendToHis": "Resend to HIS",
  "registrationRequest.resendToHis.success": "Registration resent to HIS",
  "registrationRequest.resendToAutoMedic": "Resend to AutoMedic",
  "registrationRequest.resendToAutoMedic.success": "Registration resent to AutoMedic",
  "registrationRequest.vecozoIon.label": "ION registration",
  "registrationRequest.vecozoIon.previousGp": "Previous GP",
  "registrationRequest.vecozoIon.performedAfterAcceptance": "Will be performed after the patient is accepted",
  "registrationRequest.vecozoIon.notPerformed.passant": "Will not be performed (passant)",
  "registrationRequest.vecozoIon.notPerformed.passant.subText":
    "On acceptance the ION registration is deliberately skipped because this is a passant: the person stays registered with their own GP. The rest of the registration proceeds as for a regular patient.",
  "registrationRequest.vecozoIon.ok": "The patient is registered via ION. Previous GP: {previousGp}.",
  "registrationRequest.vecozoIon.error": "The patient isn't registered via ION: {errorMessage}.",
  "registrationRequest.vecozoIon.passant": "ION registration skipped: passant.",
  "registrationRequest.vecozoIonResult.disabled": "ION not enabled",
  "registrationRequest.vecozoIonResult.ok": "ION registered",
  "registrationRequest.vecozoIonResult.unauthorized": "No permission for ION",
  "registrationRequest.vecozoIonResult.notFoundCov": "No COV found",
  "registrationRequest.vecozoIonResult.died": "Patient died",
  "registrationRequest.vecozoIonResult.alreadyPatient": "Already registered as patient",
  "registrationRequest.vecozoIonResult.invalidBsn": "Invalid BSN",
  "registrationRequest.vecozoIonResult.error": "General error",
  "registrationRequest.vecozoIonResult.passant": "Skipped (passant)",
  "registrationRequest.vecozoIonResult.passant.subText":
    "The ION registration was deliberately skipped because this is a passant: the person stays registered with their own GP. The rest of the registration proceeds as for a regular patient.",
  "registrationRequest.error.bsnAlreadyExists":
    "The patient already exists in the HIS. Search the HIS (including inactive patients) and set the patient back to active.",
  "registrationRequest.error.bsnInvalid":
    "Failed to send to the HIS. Check if the citizen service number (BSN) is valid.",
  "registrationRequest.error.hisFailed":
    "Failed to register the patient in the HIS. Please try again or register the patient manually.",
  "registrationRequest.idCheck.label": "ID check",
  "registrationRequest.idCheck.document": "Document",
  "registrationRequest.idCheck.disabledAlert.title": "Change your settings to receive the iDenfy ID check.",
  "registrationRequest.idCheck.disabledAlert":
    "Go to: settings > Registrations > Conditions for registration > Digital ID check",

  "registrationRequest.idCheckResult.pending": "ID check still in progress — do not accept yet",
  "registrationRequest.idCheckResult.pending.description":
    "The patient scanned their ID online. The automatic check of the ID has not finished yet. This can take up to 30 minutes.",
  "registrationRequest.idCheckResult.disabled": "This practice has the digital ID check switched off — check manually",
  "registrationRequest.idCheckResult.ok": "ID approved",
  "registrationRequest.idCheckResult.ok.description":
    "The patient scanned their ID online. It was checked automatically and approved.",
  "registrationRequest.idCheckResult.patientSkipped": "ID check skipped — check manually",
  "registrationRequest.idCheckResult.patientSkipped.description": "The patient skipped the ID check themselves.",
  "registrationRequest.idCheckResult.idInvalid": "ID check failed — check manually",
  "registrationRequest.idCheckResult.idInvalid.description":
    "The patient scanned their ID online. It was checked automatically, but something went wrong.",
  "registrationRequest.idCheckResult.verificationInconsistency": "Facial verification incorrect",
  "registrationRequest.idCheckResult.error": "ID check failed — check manually",
  "registrationRequest.idCheckResult.error.description":
    "The patient started the online ID check but did not complete it.",

  // Edited by GP/Assistant. `user.edited` covers a plain edit to the patient's
  // details and `system.updated` an event written by the system (which has no
  // actor): neither carries a check outcome, so both deliberately have no
  // `.description` sub-line.
  "registrationRequest.idCheckTimeline.user.edited": "By {actor}",
  "registrationRequest.idCheckTimeline.system.updated": "Updated automatically",
  "registrationRequest.idCheckTimeline.manualVerification.action": "Adjust",
  "registrationRequest.idCheckTimeline.manualVerification.placeholder": "Note",
  "registrationRequest.idCheckTimeline.manualVerification.submit": "Confirm",

  // Every timeline event title reuses the same five outcome texts as the
  // `idCheckResult` fallback copy (IT-5028), so the assistant reads the same
  // wording whichever render path a registration takes. The `.description`
  // sub-line below each title explains how that outcome came about; the UI
  // derives it by appending `.description` to the title key, so a title without
  // one simply shows no sub-line.

  // A status a colleague set by hand at the desk. A rejection here does not say
  // "check manually" — a person has just checked it by hand.
  "registrationRequest.idCheckTimeline.manualVerification.ok": "ID approved",
  "registrationRequest.idCheckTimeline.manualVerification.ok.description": "Checked manually by {actor}.",
  "registrationRequest.idCheckTimeline.manualVerification.pending": "ID check still in progress — do not accept yet",
  "registrationRequest.idCheckTimeline.manualVerification.pending.description": "Set to pending by {actor}.",
  "registrationRequest.idCheckTimeline.manualVerification.idInvalid": "ID rejected — do not accept",
  "registrationRequest.idCheckTimeline.manualVerification.idInvalid.description": "Rejected manually by {actor}.",

  // Translations for `providerAutomatic` (initial ID check, automated with AI/OCR).
  "registrationRequest.idCheckTimeline.providerAutomatic.pending": "ID check still in progress — do not accept yet",
  "registrationRequest.idCheckTimeline.providerAutomatic.pending.description":
    "The patient scanned their ID online. The automatic check of the ID has not finished yet. This can take up to 30 minutes.",
  "registrationRequest.idCheckTimeline.providerAutomatic.ok": "ID approved",
  "registrationRequest.idCheckTimeline.providerAutomatic.ok.description":
    "The patient scanned their ID online. It was checked automatically and approved.",
  "registrationRequest.idCheckTimeline.providerAutomatic.idInvalid": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerAutomatic.idInvalid.description":
    "The patient scanned their ID online. It was checked automatically, but something went wrong.",
  "registrationRequest.idCheckTimeline.providerAutomatic.notDone": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerAutomatic.notDone.description":
    "The patient started the online ID check but did not complete it.",
  "registrationRequest.idCheckTimeline.providerAutomatic.verificationInconsistency": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerAutomatic.verificationInconsistency.description":
    "The patient scanned their ID online. The check returned a conflicting result.",
  "registrationRequest.idCheckTimeline.providerAutomatic.unknownError": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerAutomatic.unknownError.description":
    "Something went wrong while checking the ID.",
  "registrationRequest.idCheckTimeline.providerAutomatic.failed": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerAutomatic.failed.description":
    "The patient scanned their ID online. It was checked automatically, but something went wrong.",

  // Translations for `providerManual` (human-in-the-loop verification)
  "registrationRequest.idCheckTimeline.providerManual.ok": "ID approved",
  "registrationRequest.idCheckTimeline.providerManual.ok.description":
    "The patient scanned their ID online. It was checked manually and approved.",
  "registrationRequest.idCheckTimeline.providerManual.idInvalid": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerManual.idInvalid.description":
    "The patient scanned their ID online. It was checked manually, but something went wrong.",
  "registrationRequest.idCheckTimeline.providerManual.notDone": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerManual.notDone.description":
    "The patient started the online ID check but did not complete it.",
  "registrationRequest.idCheckTimeline.providerManual.verificationInconsistency": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerManual.verificationInconsistency.description":
    "The patient scanned their ID online. The check returned a conflicting result.",
  "registrationRequest.idCheckTimeline.providerManual.unknownError": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerManual.unknownError.description":
    "Something went wrong while checking the ID.",
  "registrationRequest.idCheckTimeline.providerManual.failed": "ID check failed — check manually",
  "registrationRequest.idCheckTimeline.providerManual.failed.description":
    "The patient scanned their ID online. It was checked manually, but something went wrong.",

  // iDenfy rejection reason codes (shown under a rejected verdict). Codes not
  // listed here fall back to the raw provider string in the UI.
  "registrationRequest.idCheckTimeline.reason.DOC_INFO_MISMATCH": "Document details do not match",
  "registrationRequest.idCheckTimeline.reason.DOC_NOT_FOUND": "No document found",
  "registrationRequest.idCheckTimeline.reason.DOC_NOT_FULLY_VISIBLE": "Document not fully visible",
  "registrationRequest.idCheckTimeline.reason.DOC_FACE_NOT_FOUND": "No face found on document",
  "registrationRequest.idCheckTimeline.reason.DOC_TOO_BLURRY": "Document too blurry",
  "registrationRequest.idCheckTimeline.reason.DOC_GLARED": "Too much glare on document",
  "registrationRequest.idCheckTimeline.reason.DOC_FACE_GLARED": "Too much glare on face in document",
  "registrationRequest.idCheckTimeline.reason.MRZ_NOT_FOUND": "MRZ code not found",
  "registrationRequest.idCheckTimeline.reason.MRZ_OCR_READING_ERROR": "MRZ code unreadable",
  "registrationRequest.idCheckTimeline.reason.BARCODE_NOT_FOUND": "Barcode not found",
  "registrationRequest.idCheckTimeline.reason.DOC_EXPIRED": "Document expired",
  "registrationRequest.idCheckTimeline.reason.DOC_NOT_SUPPORTED": "Document type not supported",
  "registrationRequest.idCheckTimeline.reason.COUNTRY_NOT_SUPPORTED": "Country not supported",
  "registrationRequest.idCheckTimeline.reason.COUNTRY_MISMATCH": "Country does not match",
  "registrationRequest.idCheckTimeline.reason.DOC_TYPE_MISMATCH": "Document type does not match",
  "registrationRequest.idCheckTimeline.reason.DOC_SUBTYPE_MISMATCH": "Document subtype does not match",
  "registrationRequest.idCheckTimeline.reason.DOC_SIDE_MISMATCH": "Wrong side of document",
  "registrationRequest.idCheckTimeline.reason.DOC_DAMAGED": "Document damaged",
  "registrationRequest.idCheckTimeline.reason.DOC_FAKE": "Document possibly forged",
  "registrationRequest.idCheckTimeline.reason.DOC_PERSONAL_CODE_INVALID": "Personal code invalid",
  "registrationRequest.idCheckTimeline.reason.DOC_NOT_ALLOWED": "Document not allowed",
  "registrationRequest.idCheckTimeline.reason.DOC_NAME_ERROR": "Error in first name on document",
  "registrationRequest.idCheckTimeline.reason.DOC_SURNAME_ERROR": "Error in surname on document",
  "registrationRequest.idCheckTimeline.reason.DOC_EXPIRY_ERROR": "Error in expiry date on document",
  "registrationRequest.idCheckTimeline.reason.DOC_DOB_ERROR": "Error in date of birth on document",
  "registrationRequest.idCheckTimeline.reason.DOC_PERSONAL_NUMBER_ERROR": "Error in personal number on document",
  "registrationRequest.idCheckTimeline.reason.DOC_NUMBER_ERROR": "Error in document number",
  "registrationRequest.idCheckTimeline.reason.DOC_DATE_OF_ISSUE_ERROR": "Error in date of issue on document",
  "registrationRequest.idCheckTimeline.reason.DOC_SEX_ERROR": "Error in sex on document",
  "registrationRequest.idCheckTimeline.reason.DOC_NATIONALITY_ERROR": "Error in nationality on document",
  "registrationRequest.idCheckTimeline.reason.DOC_ERROR": "Error during document check",
  "registrationRequest.idCheckTimeline.reason.AUTO_UNVERIFIABLE": "Could not be verified automatically",
  "registrationRequest.idCheckTimeline.reason.DOC_SPOOF_DETECTED": "Possible document spoofing detected",
  "registrationRequest.idCheckTimeline.reason.MRZ_INVALID": "MRZ code invalid",
  "registrationRequest.idCheckTimeline.reason.NFC_INIT_FAILED": "NFC read could not start",
  "registrationRequest.idCheckTimeline.reason.NFC_FAILED": "NFC read failed",
  "registrationRequest.idCheckTimeline.reason.NFC_TIMEOUT": "NFC read timed out",
  "registrationRequest.idCheckTimeline.reason.FACE_MISMATCH": "Face does not match",
  "registrationRequest.idCheckTimeline.reason.NO_FACE_FOUND": "No face found",
  "registrationRequest.idCheckTimeline.reason.TOO_MANY_FACES": "Too many faces detected",
  "registrationRequest.idCheckTimeline.reason.FACE_TOO_BLURRY": "Face too blurry",
  "registrationRequest.idCheckTimeline.reason.FACE_GLARED": "Too much glare on face",
  "registrationRequest.idCheckTimeline.reason.FACE_UNCERTAIN": "Face uncertain",
  "registrationRequest.idCheckTimeline.reason.FACE_ERROR": "Error during face check",
  "registrationRequest.idCheckTimeline.reason.FAKE_FACE": "Possible fake face detected",
  "registrationRequest.idCheckTimeline.reason.FACE_SUSPECTED": "Face flagged as suspicious",
  "registrationRequest.idCheckTimeline.reason.FACE_BLACKLISTED": "Face on blocklist",
  "registrationRequest.idCheckTimeline.reason.DOC_FACE_BLACKLISTED": "Document face on blocklist",
  "registrationRequest.idCheckTimeline.reason.DOC_MOBILE_PHOTO": "Photo of a screen detected",
  "registrationRequest.idCheckTimeline.reason.DEV_TOOLS_OPENED": "Developer tools opened",
  "registrationRequest.idCheckTimeline.reason.DOC_PRINT_SPOOFED": "Printed document detected",
  "registrationRequest.idCheckTimeline.reason.FAKE_PHOTO": "Possible fake photo",
  "registrationRequest.idCheckTimeline.reason.AML_SUSPECTION": "AML check flagged",
  "registrationRequest.idCheckTimeline.reason.AML_FAILED": "AML check failed",
  "registrationRequest.idCheckTimeline.reason.LID_SUSPECTION": "Lost/stolen document check flagged",
  "registrationRequest.idCheckTimeline.reason.LID_FAILED": "Lost/stolen document check failed",
  "registrationRequest.idCheckTimeline.reason.SANCTIONS_SUSPECTION": "Sanctions check flagged",
  "registrationRequest.idCheckTimeline.reason.SANCTIONS_FAILED": "Sanctions check failed",
  "registrationRequest.idCheckTimeline.reason.RC_FAILED": "Check failed",

  // Translations for patient actions
  "registrationRequest.idCheckTimeline.patient.submitted": "Patient completed the registration",
  "registrationRequest.idCheckTimeline.patient.skipped": "ID check skipped — check manually",
  "registrationRequest.idCheckTimeline.patient.skipped.description": "The patient skipped the ID check themselves.",

  "registrationRequest.bsn.label": "BSN check",
  "registrationRequest.bsn.ok": "BSN automatically extracted from document",
  "registrationRequest.bsn.ok.tooltip": "BSN automatically extracted from ID document, via online ID check",
  "registrationRequest.bsn.mismatch": "BSN not filled in",
  "registrationRequest.bsn.mismatch.tooltip": "BSN in the ID document differs from the entered BSN",
  "registrationRequest.bsn.nocheck": "BSN not checked: manually entered by patient",
  "registrationRequest.bsn.nocheck.tooltip": "No automatic BSN check: patient entered BSN manually",

  "registrationRequest.bsnTimeline.fieldEdit": "BSN updated",
  "registrationRequest.bsnTimeline.changedFrom": "Previously: {value}",
  "registrationRequest.bsnTimeline.editedBy": "By {actor}",
  "registrationRequest.bsnTimeline.tooltip.fieldEdit": "BSN updated manually",
  "registrationRequest.bsnTimeline.tooltip.bsnCheck": "Manually confirmed",

  "registrationRequest.file.view": "View",
  "registrationRequest.pdfFile.label": "PDF file registration download",
  "registrationRequest.pdfFile.fileName": "Registration {name}.pdf",

  "registrationRequest.previousGpLetter.label": "Signed previous GP letter",
  "registrationRequest.previousGpLetter.label.v2": "Letter for previous GP",
  "registrationRequest.previousGpLetter.letter": "Signed letter.pdf",
  "registrationRequest.previousGpLetter.letter.v2": "Letter to previous GP.pdf",
  "registrationRequest.previousGpLetter.letterOrIon.disabled":
    "No letter has been generated. During registration, both 'Register patient via ION' and 'Send a letter to the previous GP on behalf of the patient' must be enabled.",
  "registrationRequest.previousGpLetter.featureDisabled":
    "Sending a letter to the previous GP is disabled for this practice.",
  "registrationRequest.previousGpLetter.passant": "Skipped (passant)",
  "registrationRequest.previousGpLetter.passant.subText":
    "A passant keeps their own GP: there is no letter and the patient was not asked for a previous GP or for permission to transfer the file.",
  "registrationRequest.previousGpLetter.proof": "Proof document.pdf",
  "registrationRequest.previousGpLetter.generatedAfterAcceptance":
    "The letter is only sent once you accept the patient. You can download it to send it separately if needed.",
  "registrationRequest.previousGpLetter.pending": "Waiting for result...",

  "registrationRequest.noPreviousGp": "No previous GP",
  "registrationRequest.notYet": "Not yet happened",
  "registrationRequest.failed": "Failed",
  "registrationRequest.lspApproval.label": "LSP permission",
  "registrationRequest.lspApproval.yes": "Yes",
  "registrationRequest.lspApproval.no": "No permission from patient",
  "registrationRequest.lspApproval.mitz": "Patient uses Mitz",
  "registrationRequest.guardianConsent.label": "Consent of those with parental authority",
  "registrationRequest.guardianConsent.yes": "Confirmed by the person who registered",
  "registrationRequest.guardianConsent.no": "Not ticked",
  "registrationRequest.childConsent.label": "Child agrees to the registration",
  "registrationRequest.childConsent.yes": "Confirmed by the person who registered",
  "registrationRequest.childConsent.no": "Not ticked",
  "registrationRequest.mijnGezondheidNetApproval": "MGN permission",
  "registrationRequest.autoMedic.label": "Sent to AutoMedic (RPA)",
  "registrationRequest.autoMedic.notYet": "Not yet sent to AutoMedic",
  "registrationRequest.autoMedic.created": "Not yet executed in AutoMedic",
  "registrationRequest.autoMedic.failed": "Error",
  "registrationRequest.autoMedic.sendFailed": "Sending to AutoMedic failed",
  "registrationRequest.autoMedic.success": "Executed in AutoMedic",
  "registrationRequest.autoMedic.failed.title": "Error executing in AutoMedic (RPA)",
  "registrationRequest.autoMedic.failed.info1": "Error message: {errorMessage}",
  "registrationRequest.autoMedic.failed.info2": "Click in AutoMedic on the button 'Execute' to try again.",
  "registrationRequest.autoMedic.created.title": "Data is in the queue of Automedic (RPA), but has not been executed",
  "registrationRequest.autoMedic.created.info": "To put the data in the HIS, click in AutoMedic on 'Execute'.",
  "registrationRequest.customQuestions.title": "Custom questions",
  "registrationRequest.customQuestions.noAnswer": "No answer entered",
  "registrationRequest.update.success": "The patient details has been successfully updated",
  "registrationRequest.sentToHisStatus.notSent": "Not sent yet",
  "registrationRequest.sentToHisStatus.sent": "Sent",
  "registrationRequest.sentToHisStatus.processed": "Sent & processed",
  "registrationRequest.sentToHisStatus.error": "Error",

  // RPA (AutoMedic) practices get their own vocabulary: for them the status
  // tracks the robot's queue and run, not a HIS API call.
  "registrationRequest.rpaSentToHisStatus.notSent": "Not sent yet",
  "registrationRequest.rpaSentToHisStatus.sent": "In RPA queue",
  "registrationRequest.rpaSentToHisStatus.processed": "Entered in HIS by RPA",
  "registrationRequest.rpaSentToHisStatus.error": "Error",

  "about.title": "About MijnPraktijk.nl",
  "about.text":
    '<p>MijnPraktijk.nl is part of Buurtdokters. On <a href="https://www.buurtdokters.net/contact">https://www.buurtdokters.net/contact</a> you can find our contact details.</p><h3>Complaints</h3><p>If you have complaints about the MijnPraktijk portal or Buurtdokters, please contact us via the help button or by sending an email to support@mijnpraktijk.nl</p><p>What we promise:</p><ul><li>We will respond to your message within 3 business days.</li><li>We will treat your complaint confidentially and will not share your personal data with third parties without your consent, unless necessary to comply with a legal obligation.</li><li>We will keep you informed of the progress of handling your complaint.</li></ul><p>We only process complaints that are about the MijnPraktijk portal or Buurtdokters. If you have complaints about your GP or GP practice, please contact your GP or the Stichting Klachten en Geschillen Eerstelijnszorg (SKGE) via <a href="https://www.skge.nl">https://www.skge.nl</a></p><h3>Coordinated Vulnerability Disclosure (CVD)</h3><p>We consider the security of our systems a top priority. But no matter how much effort we put into system security, there can still be vulnerabilities present.</p><p>If you discover a vulnerability, we would like to know about it so we can take steps to address it as quickly as possible. We would like to ask you to help us better protect our clients and our systems.</p><p>Please do the following:</p><ul><li>Email your findings to security@mijnpraktijk.nl. Preferably encrypt your findings using our <a href="https://portal.mijnpraktijk.nl/files/pgp-security-mijnpraktijk.asc">PGP key</a> to prevent this critical information from falling into the wrong hands;</li><li>Do not take advantage of the vulnerability or problem you have discovered, for example by downloading more data than necessary to demonstrate the vulnerability or deleting or modifying other people\'s data;</li><li>Do not reveal the problem to others until it has been resolved and delete all obtained confidential data;</li><li>Do not use attacks on physical security, social engineering, distributed denial of service, spam or applications of third parties; and</li><li>Do provide sufficient information to reproduce the problem, so we will be able to resolve it as quickly as possible. Usually, the IP address or the URL of the affected system and a description of the vulnerability will be sufficient, but complex vulnerabilities may require further explanation.</li></ul><p>What we promise:</p><ul><li>We will respond to your report within 3 business days with our evaluation of the report and an expected resolution date;</li><li>If you have followed the instructions above, we will not take any legal action against you in regard to the report;</li><li>We will handle your report with strict confidentiality, and not pass on your personal details to third parties without your permission, unless legally required. Reporting under a pseudonym is possible;</li><li>We will keep you informed of the progress towards resolving the problem;</li><li>In the public information concerning the problem reported, we will give your name as the discoverer of the problem (unless you desire otherwise); and</li><li>As a token of our gratitude for your assistance, we offer a reward for every report of a security problem that was not yet known to us. The amount of the reward will be determined based on the severity of the leak and the quality of the report. The minimum reward will be a &euro;50 gift certificate.</li></ul><p>We strive to resolve all problems as quickly as possible, and we would like to play an active role in the ultimate publication on the problem after it is resolved.</p><h3>Subprocessors</h3><table class="sub-processors"><thead><tr><th>Type of Personal Data</th><th>Purpose of Processing</th><th>Subprocessors</th></tr></thead><tbody><tr><td>Data of users of the MijnPraktijk, including user actions performed (viewing records, etc.)</td><td>Identification and authentication of users of the MijnPraktijk<br><br>Logging of actions</td><td>AWS (Germany / EU)</td></tr><tr><td>Data of patients, including health-related data</td><td>Medical record keeping</td><td>AWS (Germany / EU)</td></tr><tr><td>Identity</td><td>Verification of the patient\'s identity during registration</td><td>iDenfy (Amazon AWS Europe (Dublin, Ireland))</td></tr><tr><td>Various rules, patient and user identification, and other digital posts</td><td>Communication between MijnPraktijk and HIS</td><td>Enovation (NL)</td></tr><tr><td>Data of users of MijnPraktijk<br><br>Change requests and reported incidents or helpdesk questions may contain personal data of users and/or patients</td><td>Providing support to users</td><td>Atlassian (EU)<br><br>Office 365 / Outlook (EU)</td></tr><tr><td>Personal details of patients and content of letters</td><td>Sending letters to patients</td><td>Postbode.nu (EU)</td></tr><tr><td>Contact information of individuals subscribing to a digital newsletter and/or \'status updates\'</td><td>Email newsletters<br><br>Providing updates to users in case of (partial) MijnPraktijk outages</td><td>Mailchimp (USA)</td></tr><tr><td>User actions in the portal</td><td>Improving the quality and functionality of MijnPraktijk</td><td>AWS (Germany / EU)</td></tr></tbody></table><h3>Certification</h3><p>Our organisation is ISO 27001 and NEN 7510 certified. Press the image to see the certificate.</p><p class="certificationWrapper"><a href="/certification/iso27001.pdf"><img alt="ISO 27001" class="certificationImage" src="/certification/iso27001.png"></a><a href="/certification/nen7510.pdf"><img alt="NEN 7510" class="certificationImage" src="/certification/nen7510.png"></a></p>',

  "hisUnknown.title": "Patient unknown in HIS",
  "hisUnknown.description":
    "This patient is unknown in the HIS. When approved, the patient will receive an email that as of now it's possible to use the website. Because the patient is unknown in the HIS, this won't work.",
  "hisUnknown.areYouSure": "Are you sure you want to approve this patient?",
  "hisUnknown.confirm": "Approve",

  "public.steps.welcome": "Welcome",
  "public.steps.welcome.placeholder": "Welcome. Press next to get started.",
  "public.steps.general": "General information",
  "public.steps.personal": "Personal information",
  "public.steps.previousGpLetter": "Previous GP",
  "public.steps.dateAndTime": "Date and time",
  "public.steps.type": "Type",
  "public.steps.econsult": "Send message",
  "public.steps.appointments": "Make an appointment",
  "public.steps.appointmentList": "Appointment overview",
  "public.steps.medication": "Repeat medication request",
  "public.steps.treatmentAreas": "Intro",
  "public.steps.questionnaire": "Questions",
  "public.steps.questionnaire.withCount": "Max {remaining} remaining",
  "public.steps.advice": "Choose appointment type",
  "public.steps.myMedicalFile": "My medical file",
  "public.steps.done": "Done",

  "public.registration.preparation": "Before we start...",
  "public.registration.preparation.tip1": "Keep your proof of identity at hand",
  "public.registration.preparation.tip1.example": "This can be your passport or driving license, for example",
  "public.registration.preparation.tip2": "The registration process takes about 10 minutes",

  "public.registration.start": "Welcome",
  "public.registration.start.language": "Language preference",
  "public.registration.start.language.info":
    "Would you prefer to use the application and receive follow-up emails in Dutch or English?",
  "public.registration.start.birthDate": "When were you born?",
  "public.registration.start.address": "Do you live in our postcode area?",
  "public.registration.start.address.passant":
    "No Dutch address? Enter postcode {postalCode} and house number {houseNumber}, and fill in your address abroad as street and city.",

  "public.registration.start.invalid.age.title": "Online registration is available from the age of {age}",
  "public.registration.start.invalid.age.description":
    "Are you under {age} years old? If so, we ask you to identify yourself at the desk, along with a parent or carer.",

  "public.registration.start.idCheckAge.title": "The online ID check may not work for young children",
  "public.registration.start.idCheckAge.description": `For children under 5, the online ID check may sometimes not work, because their face is still changing quickly.

    If the online identification does not succeed after a few attempts, you can still complete the registration.

    {contactLink} to arrange how you can complete the identification afterwards.`,
  "public.registration.start.idCheckAge.contactLink": "Contact the practice",

  "public.registration.start.invalid.postalCode.title": "Your postcode is outside our postcode area",
  "public.registration.start.invalid.postalCode.description":
    "Therefore, you cannot complete the online registration.\n\nFind a GP nearer to where you live, or contact the practice to find a solution together.",

  "public.registration.email": "Confirm email",
  "public.registration.email.description":
    "We will use your email address for purposes such as appointment confirmations and logging in.",
  "public.registration.email.info": "We will send you an email to confirm the email address",
  "public.registration.email.verified":
    "An email has been sent to {email}.\nClick on the link in this email to continue your registration.",

  "public.registration.emailExpired": "This link is not valid",
  "public.registration.emailExpired.alert": "This registration link is no longer active or is not recognized.",
  "public.registration.emailExpired.description":
    "This can happen if the link has expired (links are valid for 24 hours) or was not copied correctly. Start a new registration to receive a new link.",
  "public.registration.emailExpired.tryAgain": "Register again",

  "public.registration.emailPending": "Registration in progress",
  "public.registration.emailPending.alert": "A registration has already been started with this email address.",
  "public.registration.emailPending.description":
    "First use the link you received in your email to complete your registration, before you can start a new one with the same email address.",
  "public.registration.emailPending.resend": "Resend email",
  "public.registration.emailPending.startOver": "Start over",

  "public.registration.step.existingPatient": "Already registered",
  "public.registration.step.existingPatient.alert": "You are already registered at this practice",
  "public.registration.step.existingPatient.description":
    "Your details are already in our system. Already have an account? Log in via MijnPraktijk.\nNeed help? Contact the practice.",
  "public.registration.step.existingPatient.back": "Back to the practice overview",

  // ID check - step based
  "public.registration.idCheck": "Scan ID",
  "public.registration.idCheck.preparation.why1":
    "We must verify your identity by law. This can be done here, online, by making a scan of your identity document",
  "public.registration.idCheck.preparation.why1.example": "Such as your passport, ID card, or driver’s license",
  "public.registration.idCheck.preparation.why2": "This also avoids filling in personal details manually",
  "public.registration.idCheck.preparation.why2.example":
    "When we scan your identity document, we extract personal information such as your name and date of birth. Which you then don't have to fill in manually.",
  "public.registration.idCheck.preparation.personalData": "What do we do with your personal data?",
  "public.registration.idCheck.preparation.privacy": "If you continue, you agree to the {link}.",
  "public.registration.idCheck.preparation.privacy.link": "privacy conditions of our supplier iDenfy",
  "public.registration.idCheck.preparation.more":
    "Read more about why we check your identity on the website of the {link}.",
  "public.registration.idCheck.preparation.more.link": "National Government",
  "public.registration.idCheck.preparation.skip": "Skip ID scan",
  "public.registration.idCheck.preparation.continue": "Next",

  "public.registration.idCheck.awaitingManualReview": "Your ID is being manually verified. This can take a few minutes",
  "public.registration.idCheck.maxRetriesReached": "Verification is taking longer than expected.",
  "public.registration.idCheck.refreshToContinue": "Refresh the page to continue.",

  "public.registration.idCheck.startFailed.alert": "Starting the identity check failed.",
  "public.registration.idCheck.startFailed.description": "This is most likely a temporary problem. Please try again.",
  "public.registration.idCheck.startFailed.retry": "Try again",

  "public.registration.submitFailed.alert": "Your information could not be sent.",
  "public.registration.submitFailed.description":
    "This is most likely a temporary connection problem. Please try again.",
  "public.registration.submitFailed.retry": "Try again",

  "public.registration.personalInfo": "Personal information",
  "public.registration.personalInfo.bsn.optional": "No Dutch BSN? Leave this field empty.",
  "public.registration.personalInfo.readonlyFromIdCheck":
    "This data was retrieved from the ID check. It will be verified manually, so you only need to contact the practice if your name is clearly wrong.",
  "public.registration.insurance": "Insurance & Pharmacy",
  "public.registration.insurance.found":
    "The health insurance and policy number below have been found automatically. If this is incorrect, please contact your health insurance provider directly.",
  "public.registration.insurance.notFound":
    "Your health insurance could not be found automatically. Either you are not insured in the Netherlands, or something has gone wrong. If it is the latter, please check with your health insurance provider to see where the issue lies.",

  "public.registration.pharmacy": "To which pharmacy may the GP send your prescriptions?",
  "public.registration.pharmacy.instructions":
    "You can choose a pharmacy yourself. If the field below offers limited options, these are the pharmacies that your GP practice works with.",
  "public.registration.pharmacy.description":
    "The first time you collect medication, the pharmacy may ask for identification. Visit {link} to find pharmacies near you.",
  "public.registration.pharmacy.custom": "Other pharmacy",
  "public.registration.pharmacy.custom.label": "Other pharmacy name",

  "public.registration.previousGP": "Previous GP",
  "public.registration.custom": "Extra questions",
  "public.registration.permissions": "Lastly",
  "public.registration.permissions.title": "Lastly...",

  "public.registration.permissions.checkbox.1.title":
    "I want a MijnGezondheid.net account for contact with the general practitioner.",
  "public.registration.permissions.checkbox.1.href": "https://home.mijngezondheid.net/en/terms-and-conditions",
  "public.registration.permissions.checkbox.1.description": "See the {link} for more information.", // The {link} refers to the 'href' above
  "public.registration.permissions.checkbox.1.description.link": "conditions of MijnGezondheid.net",
  "public.registration.permissions.checkbox.2.title": "I have filled in this form truthfully",
  "public.registration.permissions.guardianConsent":
    "Everyone with custody or legal guardianship of this child agrees to this registration",
  "public.registration.permissions.guardianConsent.info":
    "For a child under 16, everyone with custody or legal guardianship must agree to the registration with a new GP",
  "public.registration.permissions.childConsent": "The child that is being registered agrees to this registration",
  "public.registration.permissions.childConsent.info":
    "From the age of 12 a child has a say in their own registration with a new GP",

  "public.registration.end": "Lastly",
  "public.registration.end.title": "Success! We have received your registration request.",
  "public.registration.end.description":
    "You will receive an email when your registration is complete. This may take a few days to a few weeks.",
  "public.registration.end.callPractice": "Do you need care sooner? Please call the practice: {phoneNumber}",
  "public.registration.end.goToPractice": "To the practice's website",
  "public.registration.end.failed.alert": "Something went wrong while processing your registration.",
  "public.registration.end.failed.description":
    "Your data has not been lost. Please try again. If the problem persists, contact the practice.",
  "public.registration.end.failed.retry": "Try again",

  "public.registration.sessionTimeout.warning.title": "Your session is about to expire",
  "public.registration.sessionTimeout.warning.description":
    "Your session will expire in {minutes} {minutes, plural, one {minute} other {minutes}} to protect your data. Continue your registration to extend the session.",
  "public.registration.sessionTimeout.continue": "Continue registration",
  "public.registration.sessionTimeout.timeRemaining": "Time remaining",

  "public.prep.approvalStep.title": "Questionnaire consent",
  "public.prep.approvalStep.description":
    "In order to better assess your complaints, we would like to ask you some questions. We need your permission for this. Consent is not required.",
  "public.prep.approvalStep.approved": "I agree to the conditions above",

  "public.prep.questionnaire.required": "This question is required and must be filled in.",

  "public.prep.completeStep.title": "End of questionnaire",
  "public.prep.completeStep.description.econsultDigitalPossibleOrWalkInConsult":
    "Given the complaints, it's possible to send a message to the practice. You can also choose to have a digital walk-in consult.",
  "public.prep.completeStep.description.econsultDigitalPossible":
    "Given the complaints, it's possible to send a message to the practice.",
  "public.prep.completeStep.description.appointmentDigitalPossibleConsiderEconsultOrWalkInConsult":
    "You can choose to schedule an appointment, but it's also possible to send a message to the practice or have a digital walk-in consult.",
  "public.prep.completeStep.description.appointmentDigitalPossibleConsiderEconsult":
    "You can choose to schedule an appointment, but it's also possible to send a message to the practice.",
  "public.prep.completeStep.description.econsultDigitalNotPossible":
    "Given the complaints it's only possible to schedule a face-to-face appointment.",
  "public.prep.completeStep.description.econsultDigitalNotRecommendedNorWalkInConsult":
    "Given the complaints it's advised to schedule an appointment or use the digital walk-in consult.",
  "public.prep.completeStep.description.econsultDigitalNotRecommended":
    "Given the complaints it's advised to make an appointment.",
  "public.prep.completeStep.description.appointmentDigitalNotPossible":
    "You can now schedule an appointment. Given the complaints it's only possible to schedule a face-to-face appointment.",
  "public.prep.completeStep.description.appointmentDigitalNotRecommendedNorWalkInConsult":
    "You can now schedule an appointment. Given the complaints you're advised not to use the message option. You can use the digital walk-in consult.",
  "public.prep.completeStep.description.appointmentDigitalNotRecommended":
    "You can now schedule an appointment. Given the complaints it's advised not to use the message option.",
  "public.prep.completeStep.econsultOption.title": "Message",
  "public.prep.completeStep.econsultOption.description":
    "Send a message to the practice. General response within 1 working day.",
  "public.prep.completeStep.appointmentOption.title": "Appointment",
  "public.prep.completeStep.appointmentOption.description":
    "Schedule an appointment at the practice, or a phone/video call.",
  "public.prep.completeStep.walkInConsultOption.title": "Digital walk-in consult",
  "public.prep.completeStep.walkInConsultOption.description":
    "Virtual meeting, get help directly. {queueLength, plural, =0 {There are no patients} one {There is one patient} other {There are # patients}} waiting in line.",

  "public.prep.httpErrorRedirect":
    "There is an issue with the preparation questionnaire. You're redirected to an alternative option.",

  "public.appointment.main.header": "Schedule appointment",
  "public.appointment.noSlots.callPractice":
    "Unfortunately, there are no available times for scheduling an appointment through the website. Please call the practice at {phoneNumber} to make an appointment.",
  "public.appointment.noSlots.contactPractice":
    "Unfortunately, there are no available times for scheduling an appointment through the website. <contactLink>Get in touch</contactLink> with the practice to make an appointment.",
  "public.appointment.showMore": "+ {count, plural, one {# more} other {# more}}",
  "public.appointment.week.thisWeek": "This week",
  "public.appointment.week.future": "From next week",
  "public.appointment.thisWeek.empty": "Unfortunately, there are no more slots available this week.",
  "public.appointment.thisWeek.urgent": "Is it urgent? <contactLink>Get in touch</contactLink> with the practice.",
  "public.appointment.filter.caregiver.all": "All caregivers",
  "public.appointment.filter.caregiver.other":
    "{count, plural, =0 {No caregiver} one {One caregiver} other {# caregivers}}",
  "public.appointment.filter.appointmentTypes.all": "All appointment types",
  "public.appointment.filter.appointmentTypes.other":
    "{count, plural, =0 {No appointment types} one {One appointment type} other {# appointment types}}",
  "public.appointment.filter.days.all": "All days",
  "public.appointment.filter.days.other": "{count, plural, =0 {No days} one {One day} other {# days}}",
  "public.appointment.success": "Appointment scheduled",
  "public.appointment.success.agenda": "You have an appointment with",
  "public.appointment.success.type": "Appointment type",
  "public.appointment.success.time": "When",

  "public.appointment.urgency.warning.FOLLOW_UP_SAME_DAY": "Are you unable to make an appointment today?",
  "public.appointment.urgency.warning.FOLLOW_UP_WITHIN_24H": "Are you unable to make an appointment within 24 hours?",
  "public.appointment.urgency.warning.FOLLOW_UP_WITHIN_3D": "Are you unable to make an appointment within 3 days?",
  "public.appointment.urgency.warning.FOLLOW_UP_WITHIN_5D": "Are you unable to make an appointment within 5 days?",
  "public.appointment.urgency.warning.FOLLOW_UP_WITHIN_2W": "Are you unable to make an appointment within 2 weeks?",
  "public.appointment.urgency.warning.callPractice":
    "Call the practice {phoneNumber}. Do you think it can wait? Make an appointment below.",
  "public.appointment.urgency.warning.contactPractice":
    "<contactLink>Then get in touch</contactLink> with the practice. Do you think it can wait? Make your appointment below.",

  "public.appointmentList.main.header": "Scheduled appointments",
  "public.appointmentList.noAppointments":
    "There are no known appointments. It might be that some appointments are not displayed in this list.",
  "public.appointmentList.possibleMissingAppointments":
    "This list might not be complete; some appointments might be hidden.",
  "public.appointmentList.agenda": "With {agenda}",
  "public.appointmentList.cancel": "Cancel appointment",
  "public.appointmentList.cancel.title": "Do you want to cancel your appointment?",
  "public.appointmentList.cancel.info": "Are you sure you want to cancel the appointment on {date} at {time}?",
  "public.appointmentList.add": "Appointment",
  "public.appointmentList.period": "{from} to {to}",
  "public.appointmentList.calendar.add": "Add to calendar",
  "public.appointmentList.calendar.event.title": "Appointment at {practiceName}",
  "public.appointmentList.calendar.event.with": "Appointment with {agenda}",
  "public.appointmentList.calendar.event.description": "Appointment with {agenda} at {practiceName}",
  "public.appointmentList.calendar.other": "Other",

  "public.login.token.header": "Confirm it is you",
  "public.login.token.requestDescription": "Click the button to receive a verification code via SMS on your phone.",
  "public.login.token.requestButton": "Send code",
  "public.login.token.smsVerification":
    "A code has been sent to your phone number {maskedPhoneNumber}. Fill in the code below.",
  "public.login.token.retry.header": "No SMS received?",
  "public.login.token.retry": "We can send you a phone call with the confirmation code:",
  "public.login.token.incorrect": "If the phone number is incorrect, please contact the practice to change it.",
  "public.login.token.error.invalid": "The code you entered is incorrect. Please try again.",
  "public.login.token.error.expired": "The code has expired. Please request a new one.",
  "public.login.token.error.maxAttempts": "Too many attempts. Please request a new code.",
  "public.login.token.error.generic": "Something went wrong verifying the code. Please try again.",
  "public.login.token.error.noPhoneNumber": "We have no phone number on record for you. Please contact your practice.",
  "public.login.callVerification.nonDutchNumber":
    "You do not have a Dutch phone number and might not receive SMS. If so, try the voice call instead.",
  "public.login.callVerification.successful": "We called you with the code.",

  "public.econsult": "Online conversation with the doctor",
  "public.econsult.desc":
    "<ul><li>Safely send the doctor your message and any photos or files</li><li>The answers you gave will also be sent to the doctor</li><li>You will receive an e-mail when the doctor answered, usually within 3 days</li></ul>",
  "public.econsult.submit.subject": "Enter a subject for your message",
  "public.econsult.submit.files": "Upload any photos or files for the doctor",
  "public.econsult.submit.success": "We received your message",
  "public.econsult.submit.success.desc":
    "Your symptoms and any other information you added is sent to the doctor. You will receive an e-mail when the doctor answered, usually within 3 days.",
  "public.econsult.video.ended": "The meeting was ended. If this was an error, {link}.",
  "public.econsult.video.ended.link": "press here to restart",
  "public.econsult.video.practiceNotAvailable":
    "No one of the practice is currently available, but that probably won't take long. Please wait a minute.",
  "public.econsult.simple.practiceNotAvailable":
    "You can exchange text messages here. If the doctor (or assistant) is online at the same time, they can start a video connection.",
  "public.econsult.send": "We received your response. As soon as there is a reaction, you'll receive an email.",
  "public.econsult.send.phoneConsult":
    "We received your message. The assistant can see all messages during your phone call.",
  "public.econsult.attach.prominent": "Add a photo or file",
  "public.econsult.queue.index":
    "{queueIndex, plural, =0 {Your consult can start any moment now.} one {You are the next in line. At this moment only one other consult in progress, after which your consult will start.} other {There are # patients before you. We estimate this will take around {waitingMinutes} minutes.}} You can already send (a summary of) your question or issue as a message. This will help during the consult.",
  "public.econsult.queue.sms": "We will send an SMS when your consult is about to start.",
  "public.econsult.messages": "E-consult messages",
  "public.econsult.phoneConsult.title": "Phone consult details",
  "public.econsult.messages.closed": "This e-consult is closed, it is no longer possible to respond to it",
  "public.econsult.messages.placeholder": "Type your message here",

  "public.walkInConsult.continue": "You can now continue to the {link}.",
  "public.walkInConsult.link": "digital walk-in consult",

  "public.medication.main.header": "Repeat medication request",
  "public.medication.prescribed.noMedication":
    "There is no known chronic medication on file. It's only possible to request chronic medication that you're currently taking. Please contact the practice if this is incorrect.",
  "public.medication.prescribed.headsup":
    "Below you can see your chronic medication which is known to us and can be repeated. Please contact the practice if this is incomplete.",
  "public.medication.prescribed.drug": "Drug name",
  "public.medication.prescribed.strength": "Strength",
  "public.medication.prescribed.dosage": "Dosage",
  "public.medication.prescribed.amount": "Amount",
  "public.medication.prescribed.comment": "Comments",
  "public.medication.prescribed.noSelectionMade": "Select which medication you want to request",
  "public.medication.selfDefined.headsup":
    "Note: you can only order chronic medication that you already received before.",
  "public.medication.selfDefined.addMore": "More medication",
  "public.medication.selfDefined.add": "Add",
  "public.medication.selfDefined.addCancel": "Cancel",
  "public.medication.selfDefined.validation.drug": "Drug name is required",
  "public.medication.selfDefined.validation.strength": "Strength is required",
  "public.medication.selfDefined.validation.dosage": "Dosage is required",
  "public.medication.selfDefined.validation.amount": "Amount is required",
  "public.medication.selfDefined.placeholder.strength": "e.g. 5mg",
  "public.medication.selfDefined.placeholder.dosage": "e.g. 2 per day",
  "public.medication.selfDefined.placeholder.amount": "e.g. 30 tablets",
  "public.medication.success": "The request has been received",
  "public.medication.success.info":
    "We will arrange the repeat prescription and send it to your pharmacy. If there are remaining questions from our side, we will contact you.",

  "public.registration.previousGpLetter.title": "Unsubscribe from your previous GP",
  "public.registration.previousGpLetter":
    "If you are accepted as a patient with us, we can only complete your registration once you have been unsubscribed from your previous GP practice. Therefore, we will send a letter on your behalf.",
  "public.registration.previousGpLetter.question": "Can we also request your medical files?",
  "public.registration.previousGpLetter.yes": "Yes",
  "public.registration.previousGpLetter.no":
    "No, I will send a message to my previous GP myself for the transfer of my medical files",
  "public.registration.previousGpLetter.previewButton": "Preview letter",
  "public.registration.previousGpApprovalTerms":
    "I agree that my previous GP will be notified that I am leaving the practice.",
  "public.registration.previousGpApprovalTerms.info":
    "You can only be registered with one GP. Therefore, to continue with the registration, you must agree to the notice being sent.",
  "public.registration.previousGpLetter.noApprovalWarning.title":
    "Note: you have not given us permission to request your medical file",
  "public.registration.previousGpLetter.noApprovalWarning.listItem1":
    "If you do not give us permission, you must ensure that your previous GP sends your medical file to us.",
  "public.registration.previousGpLetter.noApprovalWarning.listItem2":
    "To provide you with better care, we need to be aware of your medical history.",

  "public.registration.previousGpSelection.title": "Previous GP",
  "public.registration.previousGpSelection.description":
    "In order for us to request your medical file from your previous GP practice, we need to know the name of that practice.",
  "public.registration.previousGpSelection.searchLabel": "Search for your previous GP practice",
  "public.registration.previousGpSelection.searchPlaceholder": "Search by practice name or address",
  "public.registration.previousGpSelection.cannotFind": "I cannot find my previous GP",
  "public.registration.previousGpSelection.noPreviousGp": "I have never had a GP in the Netherlands",

  "public.registration.process.accepted":
    "Your registration at the practice has been accepted. You have received more information by email.",
  "public.registration.process.rejected":
    "Your registration at the practice has been rejected. You have received more information about this by email. If you have any questions about the rejection, please contact the practice.",
  "public.registration.process.completed":
    "Your registration at the practice has been completed. You have finished the registration process.",
  "public.registration.main.header": "Register to our practice",
  "public.registration.existingPatient":
    "You are already registered in our practice. It's not possible to register again.",

  // ID check V1 bullets
  "public.registration.idCheck.introduction":
    "We must verify your identity by law. Identification can be done online (using your passport or driving licence, for example). You then have to enter less information.",
  "public.registration.idCheck.introduction.short": "In short:",
  "public.registration.idCheck.introduction.text1":
    "MijnPraktijk and the practice save the document number and the type of document.",
  "public.registration.idCheck.introduction.text2":
    "{provider} keeps a copy of your identity document and it will be removed after {days}.",
  "public.registration.idCheck.days": "1 day",
  "public.registration.idCheck.introduction.text3":
    "Employees of the practice only see whether your document has been verified and have {noAccess} to your identity documents.",
  "public.registration.idCheck.noAccess": "no access",

  "public.registration.idCheck.introduction.final":
    "By continuing you agree to the {link1}. Read more about why we ask this on the {link2} website.",
  "public.registration.idCheck.privacy.link": "privacy conditions of our provider iDenfy",
  "public.registration.idCheck.government.link": "Rijksoverheid",
  "public.registration.idCheck.agree": "I agree",
  "public.registration.idCheck.notAgree": "Skip ID scan",
  "public.registration.idCheck.notAgreeConfirm": "Yes, skip it",
  "public.registration.idCheck.cancelConfirm": "Yes, cancel",
  "public.registration.idCheck.cancel": "Cancel ID scan",
  "public.registration.idCheck.areYouSure": "Are you sure?",
  "public.registration.idCheck.areYouSureDescription":
    "The GP practice must verify your identity.{newLine}If you do not identify yourself online, you will need to visit the practice to show your identification. The practice can only accept your registration after this step has been completed.{newLine}Contact the practice at ({phoneNumber}) to find out how to best proceed with your registration.",
  "public.registration.idCheck.mandatory": "I cannot identify online",
  "public.registration.idCheck.mandatoryTitle": "We must verify your identity - online or in person",
  "public.registration.idCheck.mandatoryDescription":
    "The GP practice must verify your identity.{newLine}Would you like to register online? Then you cannot skip the online identification check at our practice.{newLine}Can you not identify yourself online, or do you not want to? Then you can register and identify yourself in person, at our practice.",
  "public.registration.idCheck.accept": "Scan my ID after all",
  "public.registration.idCheck.loading": "Loading...",
  "public.registration.idCheck.processing1": "Processing your information...",
  "public.registration.idCheck.processing2": "This may take some time. Please wait and don't close this window.",
  "public.registration.idCheck.verificationInconsistency": "Facial recognition didn't match the ID. Please try again.",
  "public.registration.lspApproval":
    "I give permission to share my file with other healthcare providers if they need it",
  "public.registration.lspApproval.explanation":
    "For example, when you need emergency care. See {link} for more information about 'Landelijk Schakelpunt'",
  "public.registration.lspApproval.href": "https://www.volgjezorg.nl/en/lsp",
  "public.registration.mitzInfo.title": "Consent for sharing your medical data",
  "public.registration.mitzInfo.link.href": "https://www.mijnmitz.nl/en",
  "public.registration.mitzInfo.link.title": "mijnmitz.nl",
  "public.registration.mitzInfo.body":
    "Do you want healthcare providers like the emergency post, pharmacy or specialist to have access to your medical data? You can manage this yourself via Mitz. Visit {link} to manage your consent.",
  "public.registration.mijnGezondheidNetApproval": "I want a MijnGezondheid.net account to contact the GP",
  "public.registration.mijnGezondheidNetApproval.explanation": "See the {link} for more information.",
  "public.registration.mijnGezondheidNetApproval.link": "terms and conditions of MijnGezondheid.net",
  "public.registration.terms": "I have filled out this form truthfully.",
  "public.registration.terms.validation.required": "You need to agree to this, in order to continue",
  "public.registration.pharmacy.label": "Pharmacy",
  "public.registration.pharmacy.info1":
    "You may choose any pharmacy. If the field below offers a limited selection, these are the pharmacies your new GP practice works with.",
  "public.registration.pharmacy.info2":
    "You can choose a pharmacy yourself. The first time you collect medication, the pharmacy may ask for identification. Visit {link} to find pharmacies near you.",
  "public.registration.pharmacy.required": "You must choose a pharmacy",
  "public.registration.customPharmacy.other": "Other pharmacy",
  "public.registration.customPharmacy.label": "Other pharmacy name",
  "public.registration.error.create":
    "We cannot start an ID check right now. You can try again, if the problem persists, please contact support@mijnpraktijk.nl",
  "public.registration.disabled.title": "We are closed for registrations",
  "public.registration.invitationInvalid.text":
    "The link may already have been used or has expired. Please contact the practice for a new invitation.",
  "public.overview.title.welcome": "Welcome to {practiceName}",
  "public.overview.pages.title": "How can we help you?",
  "public.notPatient.message":
    "We can not find you in our system. Please call us at {practicePhoneNumber} to discuss your personal details{hasRegistration, select, true { or {link} if you are not a patient with us yet} other {}}.",
  "public.notPatient.link": "register as a new patient",

  "public.medicalResults.main.header": "Medical files",
  "public.medicalResults.noResults":
    "There are no known medical files. It might be that some results are not displayed in this list.",
  "public.medicalResults.possibleMissingResults":
    "Results your GP has not released yet, or that were received more than 14 months ago, are not shown here.",
  "public.medicalResults.type.label": "Type",
  "public.medicalResults.showMore": "Show more",
  "public.medicalResults.showLess": "Show less",
  "public.medicalResults.remarks": "Remarks from GP",
  "public.medicalResults.letter.title": "Medical letters",
  "public.medicalResults.letter.introduction": "Below are two types of messages:",
  "public.medicalResults.letter.introduction.line1":
    "Letters of your medical situation sent by your GP and other care professionals",
  "public.medicalResults.letter.introduction.line2": "Referrals the GP has sent to a specialist",
  "public.medicalResults.letter.noText": "No text available",
  "public.medicalResults.medications.title": "Medication",
  "public.medicalResults.medications.introduction": "You can request repeat medication {link}.",
  "public.medicalResults.medications.introduction.link": "here",
  "public.medicalResults.medications.date": "Start and end",
  "public.medicalResults.medications.prescribed": "Prescribed medication",
  "public.medicalResults.medications.amount": "Amount",
  "public.medicalResults.medications.startDate": "Start date",
  "public.medicalResults.medications.endDate": "End date",
  "public.medicalResults.medications.dosage": "Dosage",
  "public.medicalResults.medications.repeatable": "Allowed to repeat",
  "public.medicalResults.showDetails": "Show details",
  "public.medicalResults.lab.title": "Test results",
  "public.medicalResults.lab.introduction": "The results displayed here, are from tests for which you visited your GP.",
  "public.medicalResults.lab.type": "Type",
  "public.medicalResults.lab.date": "Date",
  "public.medicalResults.lab.value": "Result",
  "public.medicalResults.lab.deviates": "Deviates",
  "public.medicalResults.lab.deviates.yes": "Yes",
  "public.medicalResults.lab.deviates.no": "No",
  "public.medicalResults.lab.deviates.unknown": "Unknown",
  "public.medicalResults.lab.remark": "Note",

  "public.allergy.title": "Allergies",
  "public.allergy.introduction": "Allergies or intolerance to medication",
  "public.allergy.substance": "Substance",
  "public.allergy.medicationAdministration": "Medication administration",
  "public.allergy.note": "Note",
  "public.allergy.details.author": "Allergy diagnosed by",
  "public.allergy.details.created": "Allergy diagnosed on",
  "public.allergy.details.updated": "Last contact with the substance",
  "public.allergy.details.medicationAdministration": "Medication administration",
  "public.allergy.details.status": "Status",
  "public.allergy.details.severity": "Severity",
  "public.allergy.details.comment": "Comment",

  "public.episodes.title": "Medical complaints",
  "public.episodes.complaint": "Complaint",
  "public.episodes.date": "Start and end",
  "public.episode.title.fallback": "Episode",
  "public.episode.start": "Date first contact",
  "public.episode.end": "Date last contact",

  "public.soep.subjective": "Subjective",
  "public.soep.objective": "Objective",
  "public.soep.evaluation": "Evaluation",
  "public.soep.plan": "Plan",

  "public.letters.title": "Title",
  "public.letters.date": "Date",
  "public.letters.sender": "Sender",
  "public.letters.receiver": "Receiver",

  "public.privacy.title": "Privacy statement",
  "public.privacy.title.named": "Privacy statement {name}",

  "public.login.info": "Log in to make an appointment, order medication, or to contact the practice",
  "public.login.notAvailable":
    "The patient portal is not available for {practiceName}. You can return to the overview via the button below:",
  "public.login.signup.title": "New to {practiceName}?",
  "public.login.alternatives": "or",
  "public.login.register.title": "Register as a new patient",
  "public.login.register.description": "If you are not a patient at {practiceName} yet",
  "public.login.createAccount.title": "Create an account",
  "public.login.createAccount.description": "If you are registered at {practiceName}, but don't have an account yet",
  "public.login.createAccount.info1":
    "Create an account at {practiceName} to make appointments, order medication, or contact the practice.",
  "public.login.createAccount.info2": "Please fill in the form below to create an account at {practiceName}.",
  "public.login.createAccount.email.info": "Make sure the email you enter matches the one registered at the practice.",
  "public.login.createAccount.email.alreadyInUse": "This email address is already in use",
  "public.login.createAccount.verification": "Click the link in your email",
  "public.login.createAccount.verification.info":
    "We’ve sent you an email.\nClick the link to create an account.\n\nIf you can’t find the email, check your spam folder. {link}",
  "public.login.createAccount.verification.info.link": "Click here to try again.",
  "public.login.createAccount.password": "Choose a strong password",
  "public.login.createAccount.password.placeholder": "Type a unique password",
  "public.login.createAccount.submit": "Sign up",
  "public.login.createAccount.unsuccessful":
    "We couldn’t create account for you because we couldn’t find your patient record. Please contact the practice.",
  "public.login.createAccount.accepted": "Your account has been created",
  "public.login.createAccount.accepted.link": "Click here to continue.",
  "public.login.createAccount.pending": "Thank you! We’re reviewing your information",
  "public.login.createAccount.pending.info":
    "We’ve received your information. The account needs to be activated by the practice, which is handled during office hours.\n\nYou will be able to log in once your account has been activated by your practice.\n\nIf you need to make an appointment or need help in the meantime, please contact the practice directly.",
  "public.login.createAccount.duplicate":
    "An account already exists with the provided information. {link} Forgot your password? Press 'Forgot Password?' in the login screen.",
  "public.login.createAccount.duplicate.link": "Click here to log in.",
  "public.login.passwordReset": "Password reset",
  "public.login.passwordReset.info": "Fill in your e-mail address. You’ll receive a link to set a new password.",
  "public.login.passwordReset.sent": "Click the link in your email",
  "public.login.passwordReset.sent.info":
    "If there is an account with this e-mail, we’ve sent you a link to set a new password.\n\nIf you can’t find the email, check your spam folder.",
  "public.login.passwordReset.newPassword": "Choose new password",
  "public.login.passwordReset.newPassword.info":
    "Your password must be at least {characterLength} characters, must contain at least one number and special character.",
  "public.login.passwordReset.newPassword.submit": "Save password",
  "public.login.passwordReset.successful": "Your password was updated",
  "public.login.passwordReset.successful.info": "Your new password is now active.\nYou can use it to {link}.",
  "public.login.passwordReset.successful.info.link": "log in to your account",
  "public.login.passwordReset.error.expired":
    "This link to reset your password has expired. You can press 'Forgot Password?' at the login screen to request a new password.",
  "public.login.passwordReset.error.incorrectCode": "The verification code entered is incorrect.",
  "public.login.passwordReset.error.tooManyAttempts":
    "You entered too many incorrect verification codes. Try again in 5 minutes. You will receive a new code then.",
  "public.login.passwordReset.error.duplicate":
    "You have already used this password before. Please choose a different password.",
  "public.login.createAccountAtPractice":
    "If you don't have an account yet, you can request one at front office at the practice.",
  "public.login.accountLinkStatus.pending": "You can't login yet",
  "public.login.accountLinkStatus.pending.info":
    "Your account hasn't been approved by the practice yet. This should take no longer than a few working days. If it takes longer, or if you need help, contact the practice.",
  "public.login.accountLinkStatus.rejected": "Your account was not approved",
  "public.login.accountLinkStatus.rejected.info":
    "You can't log in because your account was not approved by the practice. For further assistance contact the practice",
  "public.login.accountLinkStatus.inactive": "Your account is no longer active",
  "public.login.accountLinkStatus.inactive.info":
    "You can't log in because your account is not active anymore. For further assistance contact the practice",
  "public.login.accountLinkStatus.notFound": "Your account was not found",
  "public.login.accountLinkStatus.notFound.info": "We couldn't find your account. Please create a new account.",
  "public.login.accountLinkStatus.duplicate": "You already have an account",
  "public.login.accountLinkStatus.duplicate.info":
    "We couldn't create an account, because an account already exists with this email address.",
  "public.login.mfa.sms": "For verification, a code has been sent to your phone as SMS, Signal or Telegram message.",
  "public.login.mfa.email": "For verification, a code has been sent to your email address.",
  "public.login.mfa.label": "Verification code",
  "public.login.additionalData.description": "We need some additional information to create an account for you.",
  "public.login.additionalData.bsnRequired.description":
    "Your BSN is not yet known to us. Please fill in your BSN below.",
  "public.login.additionalData.error.bsnInvalid":
    "The BSN you entered does not pass the 11-check. Please check the number and try again.",
  "public.login.additionalData.error.bsnAlreadyExists":
    "This BSN is already known to the practice. Please contact the practice to log in.",
  "public.login.alreadyLoggedIn.description": "You are already logged in as {fullName}.",
  "public.login.alreadyLoggedIn.options1": "Do you want to ",
  "public.login.alreadyLoggedIn.options2": "log out",
  "public.login.alreadyLoggedIn.options3": " or ",
  "public.login.alreadyLoggedIn.options4": "continue as {fullName}",
  "public.login.loggedOut.manually": "You've been logged out.",
  "public.login.loggedOut.automatically": "You've been logged out automatically.",
  "public.login.expiry.idle.minutes": "You will be logged out automatically in {minutes} minutes due to inactivity.",
  "public.login.expiry.idle.seconds": "You will be logged out automatically in {seconds} seconds due to inactivity.",
  "public.login.expiry.token.minutes": "You will be logged out automatically in {minutes} minutes.",
  "public.login.expiry.token.seconds": "You will be logged out automatically in {seconds} seconds.",
  "public.login.expiry.token.extendText": "Do you want to stay logged in, and continue using the site?",
  "public.login.expiry.token.extendButton": "Extend session",
  "public.login.sensitiveDataNotification":
    "MijnPraktijk shows your personal health information. We advise you to handle it with care and to never share it with others.",

  "public.common.practiceWebsite": "Our website",
  "public.common.contactOrEmergency": "Contact or emergency",
  "public.common.contact": "Contact",
  "public.common.emergency": "Emergency",
  "public.common.emergencyLabel": "(only for emergencies)",
  "public.common.home": "Home",
  "public.common.login": "Log in",
  "public.common.logout": "Log out",
  "public.common.footer.text": "Made possible with",
  "public.common.signup": "Register at the practice",
  "public.common.fontSize": "Font size",
  "public.common.help": "Help",
  "public.common.appointments": "My appointments",
  "public.common.privacy": "Privacy statement",
  "public.common.step": "Step",
  "public.common.of": "of",
  "public.common.backToDashboard": "Go back to dashboard",
  "public.common.success": "Thanks for your query",

  "healthProfessional.role.GeneralPractitioner": "General practitioner",
  "healthProfessional.role.MedicalDoctor": "Medical doctor",
  "healthProfessional.role.PhysicianAssistant": "Physician assistant",
  "healthProfessional.role.NurseSpecialist": "Nurse specialist",
  "healthProfessional.role.DoctorsAssistant": "Doctor's assistant",
  "healthProfessional.role.PracticeSupportMentally": "Practice support mentally (POH-GGZ)",
  "healthProfessional.role.PracticeSupportSomatic": "Practice support somatic (POH-S)",
  "healthProfessional.role.PracticeSupportElderly": "Practice support elderly (POH-O)",
  "healthProfessional.role.PracticeSupportYouth": "Practice support youth (POH-J)",

  "validation.uuid.invalid": "This is not a valid UUID",
  "validation.email.invalid": "Email address is incorrect",

  "documentPreview.dub": "Document preview",

  "emailpreview.from": "from",
  "emailpreview.to": "to",
  "emailpreview.date": "date",
  "emailpreview.subject": "subject",

  "textEditor.menu.sitePageLink": "Link own website",
  "textEditor.bold": "Bold (Ctrl+B)",
  "textEditor.italic": "Italic (Ctrl+I)",
  "textEditor.underline": "Underline (Ctrl+U)",
  "textEditor.bulletList": "Bullet list",
  "textEditor.orderedList": "Numbered list",
  "textEditor.addLink": "Add link",
  "textEditor.link.removeLink": "Unlink",
  "textEditor.link.removeLink.tooltip": "Removes the URL",
  "textEditor.link.text.placeholder": "Label",
  "textEditor.link.url.placeholder": "URL (example: https://my-website.com)",
  "textEditor.addImage": "Add image",
  "textEditor.addFile": "Add file",
  "textEditor.align.left": "Align left",
  "textEditor.align.center": "Align center",
  "textEditor.align.right": "Align right",
  "textEditor.table.add": "Add table",
  "textEditor.table.delete": "Remove table",
  "textEditor.table.addRow": "Add row",
  "textEditor.table.removeRow": "Remove row",
  "textEditor.table.addColumn": "Add column",
  "textEditor.table.removeColumn": "Remove column",
  "textEditor.table.mergeCells": "Merge cells",
  "textEditor.toolbar": "Formatting toolbar",
  "textEditor.collapse": "Collapse editor",

  "multiSelectInput.moreSelected": "{count} more selected",
  "multiSelectInput.removeTag": "Remove {label}",

  "general.uncategorizedError.message":
    "It's not you. Try again later today. If it still doesn't work, please let us know this error code: {code}",

  "patients.swaplist.title": "Swap patients",
  "patients.swaplist.checklist.1": "Currently registered at your practice",
  "patients.swaplist.checklist.2": "No longer living in your postcode area",
  "patients.swaplist.checklist.3": "Living in a postcode area with a practice accepting patients",
  "patients.swaplist.description.1": "Patients below",
  "patients.swaplist.description.2": "Select the patients you want to swap and register them at an open practice.",
  "patients.swaplist.patients": "Patients",
  "patients.swaplist.filter.messagesSent.label": "Messages sent",
  "patients.swaplist.filter.messagesSent.0": "No messages sent",
  "patients.swaplist.filter.messagesSent.1": "1 message sent",
  "patients.swaplist.filter.messagesSent.2": "2 messages sent",
  "patients.swaplist.filter.messagesSent.3": "3 or more messages sent",
  "patients.swaplist.table.patient": "Patient",
  "patients.swaplist.table.address": "Current details",
  "patients.swaplist.table.practices": "Possible practices",
  "patients.swaplist.table.status": "Status",
  "patients.swaplist.table.status.pending": "{count, plural, =1 {Email sending} other {# Emails sending}}",
  "patients.swaplist.table.status.success": "{count, plural, =1 {Email sent} other {# Emails sent}}",
  "patients.swaplist.table.status.error": "{count, plural, =1 {Sending email failed} other {# Sending emails failed}}",
  "patients.swaplist.actions.count": "{count, plural, =1 {# patient selected} other {# patients selected}}:",
  "patients.swaplist.actions.delete": "Delete",
  "patients.swaplist.actions.sendEmail": "Send email",
  "patients.swaplist.actions.sendLetter": "Send letter",
  "patients.swaplist.email.preview.title": "Email example for illustration",
  "patients.swaplist.email.description":
    "Send the selected patients an email asking them to register with another practice. We've created a template that you can edit before sending.",
  "patients.swaplist.email.form.recipients": "Recipients",
  "patients.swaplist.email.form.subject": "Subject",
  "patients.swaplist.email.breadcrumbs.1": "Patients",
  "patients.swaplist.email.breadcrumbs.2": "Swap patients",
  "patients.swaplist.email.breadcrumbs.3": "Send email",
  "patients.swaplist.email.bcc": "BCC",
  "patients.swaplist.email.removeRecipient": "Remove {recipient} from recipients",
  "patients.swaplist.email.sendFailed": "The email could not be sent. Please try again.",
  "patients.swaplist.email.recipientWithoutEmail": "This patient has no email address and will not receive the email.",
  "patients.swaplist.email.skippedRecipients":
    "{count, plural, =1 {# selected patient has} other {# selected patients have}} no email address. They are skipped when the email is sent.",
  "patients.swaplist.email.noRecipientsWithEmail":
    "None of the selected patients has an email address, so no email can be sent. Go back to the list and select patients with an email address.",
  "patients.swaplist.filter.creationTime.label": "Creation time",
  "patients.swaplist.filter.creationTime.0": "Older than a year",
  "patients.swaplist.filter.creationTime.7": "Last 7 days",
  "patients.swaplist.filter.creationTime.30": "Last 30 days",
  "patients.swaplist.filter.creationTime.365": "Last 365 days",
  "patients.swaplist.filter.targetPractices.label": "Other practice",
  "patients.swaplist.filter.practiceAvailability.label": "Other practice availability",
  "patients.swaplist.filter.practiceAvailability.available": "Available",
  "patients.swaplist.filter.practiceAvailability.notAvailable": "Not available",
  "patients.swaplist.filter.postalCodeArea.label": "Postcode area",
  "patients.swaplist.filter.postalCodeArea.in": "Inside the postcode area of practice",
  "patients.swaplist.filter.postalCodeArea.out": "Outside the postcode area of practice",

  "patients.swaplist.removeconfirmation.title": "Delete this patient (or patients)?",
  "patients.swaplist.removeconfirmation.content.0":
    "When you remove a patient here, they are only removed from this list of swappable patients.",
  "patients.swaplist.removeconfirmation.content.1":
    "Is the patient listed somewhere else in MijnPraktijk? Then they will remain there.",

  "stepbuttons.prev": "Previous",
  "stepbuttons.next": "Next",

  "admin.maintenanceBanners.title": "Announcement banners",
  "admin.maintenanceBanners.create": "Banner",
  "admin.maintenanceBanners.create.title": "Create new banner",
  "admin.maintenanceBanners.edit.title": "Edit banner",
  "admin.maintenanceBanners.column.title": "Title",
  "admin.maintenanceBanners.column.type": "Type",
  "admin.maintenanceBanners.column.status": "Status",
  "admin.maintenanceBanners.column.schedule": "Schedule",
  "admin.maintenanceBanners.column.targeting": "Target location",
  "admin.maintenanceBanners.column.actions": "Actions",
  "admin.maintenanceBanners.form.intro":
    "An announcement banner is shown at the top of the page and will be visible to all users of MijnPraktijk. You can use it to announce maintenance or downtime. Active banners appear automatically within a minute.",
  "admin.maintenanceBanners.form.title": "Title",
  "admin.maintenanceBanners.form.title.maxLength": "Title should be limited to 300 characters",
  "admin.maintenanceBanners.form.isEnabled": "Active",
  "admin.maintenanceBanners.form.isEnabled.info":
    "A banner is only shown if active. You can create banners in advance and enable them later on.",
  "admin.maintenanceBanners.form.type": "Type",
  "admin.maintenanceBanners.form.from": "From",
  "admin.maintenanceBanners.form.to": "To",
  "admin.maintenanceBanners.form.target": "Show the announcement...",
  "admin.maintenanceBanners.form.area": "... on these parts of the portal ...",
  "admin.maintenanceBanners.form.his": "... if the practice has the following HIS ...",
  "admin.maintenanceBanners.form.targetHis": "HIS",
  "admin.maintenanceBanners.form.targetHis.placeholder": "Leave empty for all HIS systems",
  "admin.maintenanceBanners.form.practices": "... only at these practices ...",
  "admin.maintenanceBanners.form.practices.placeholder": "Leave empty for all practices",
  "admin.maintenanceBanners.targeting.caregivers": "Caregivers",
  "admin.maintenanceBanners.targeting.caregivers.short": "Caregivers",
  "admin.maintenanceBanners.targeting.patientRegistrations": "Patient: Online registration",
  "admin.maintenanceBanners.targeting.patientRegistrations.short": "Registrations",
  "admin.maintenanceBanners.targeting.patientGeneral": "Patient: Everywhere except registration",
  "admin.maintenanceBanners.targeting.patientGeneral.short": "Patients",
  "admin.maintenanceBanners.type.info": "Information",
  "admin.maintenanceBanners.type.success": "Success",
  "admin.maintenanceBanners.type.warning": "Warning",
  "admin.maintenanceBanners.type.error": "Error",
  "admin.maintenanceBanners.status.visible": "Visible",
  "admin.maintenanceBanners.status.inactive": "Inactive",
  "admin.maintenanceBanners.status.scheduled": "Scheduled",
  "admin.maintenanceBanners.status.expired": "Expired",
  "admin.maintenanceBanners.create.success": "Banner created",
  "admin.maintenanceBanners.update.success": "Banner updated",
  "admin.maintenanceBanners.delete.title": "Remove banner",
  "admin.maintenanceBanners.delete.confirmation": "This removes the banner and cannot be undone.",
  "admin.maintenanceBanners.form.setToCurrentDay": "Set to current day",
  "admin.maintenanceBanners.scheduled": "Visible",
  "admin.maintenanceBanners.scheduledEnd": "Hidden",

  "onboarding.welcome.title": "Welcome to MijnPraktijk",
  "onboarding.welcome.intro": "We streamline work in your practice, so you can focus on care",
  "onboarding.email.label": "Email address",
  "onboarding.password.label": "Choose your password",
  "onboarding.password.inputInfo": "Minimum {characterLength} characters. At least 1 number and 1 special character",
  "onboarding.password.action": "Set password",
  "onboarding.mfa.title": "Super! Last step:",
  "onboarding.error.info": "Something went wrong. You can try it again or go to the login page",
  "onboarding.continue.info": "Welcome back! You have set your password already. Log in to continue.",
  "onboarding.complete.title": "Done!",
  "onboarding.complete.info":
    "You have activated your account.\n\nYou will still need the authenticator app to log in, so do not delete it.",

  "onboarding.item.moreInfo": "More information",
  "onboarding.todoCount": "{count, plural, one {# to-do} other {# to-do's}}",

  "test.icpcCode": "ICPC code **THIS IS ONLY USED IN TESTS**",
  "test.hintComponent": "Translated hint",
  "test.phoneNumber": "Phone number",
  "test.email": "Email address",
  "test.firstName": "First name",
  "test.lastName": "Last name",
  "test.countryOfBirth": "Country of birth",
  "test.practiceAgb": "Practice AGB code",
  "test.firstName.placeholder": "Type your first name",
  "test.lastName.placeholder": "Type your last name",
  "test.email.placeholder": "Type your email address",
  "test.phoneNumber.placeholder": "Type your phone number",
  "test.bsn.placeholder": "Type your citizen service number (BSN)",

  "test.managedForm.dropdown": "This is a dropdown",
  "test.managedForm.checkbox": "Checkboxes (select multiple)",
  "test.managedForm.radio": "Radio options (select one)",

  "reminder.snooze.title": "Pause or unsubscribe?",
  "reminder.snooze.description":
    "Currently we send you a monthly reminder asking you whether you have space for new patients.",
  "reminder.snooze.question": "Would you like to:",
  "reminder.snooze.pause": "Snooze them",
  "reminder.snooze.pauseDuration": "for the next 6 months",
  "reminder.snooze.unsubscribe": "Unsubscribe completely",
  "reminder.snooze.success.title": "Success!",
  "reminder.snooze.success.description.6months": "The reminder email will be snoozed for the next 6 months.",
  "reminder.snooze.success.description.forever": "You have been unsubscribed from this reminder email.",

  // ErrorCode API errors (ErrorCodeException)
  "error.AppointmentCreationClosed": "Appointment booking is closed",
  "error.AppointmentSlotUnavailable":
    "Unfortunately this time slot has just been booked and is no longer available. Please choose another time.",
  "error.CommunicationLogAccessDenied": "Access denied to the logs",
  "error.DashboardDatabaseBuildRunning": "A dashboard build is already running. Try again in a few minutes.",
  "error.DashboardDatabaseNotBuilt": "The dashboard for this care group isn't ready yet. Check back later.",
  "error.DuplicatePassword": "This password has already been used before",
  "error.DuplicateUserInvitation": "An active invitation already exists for this name and email address.",
  "error.EconsultLinkCallerNumberInvalid": "The caller's number is hidden or invalid",
  "error.EconsultLinkCallerNumberNotMobile": "The caller's number is not a mobile number",
  "error.EconsultLinkTemplateDisabled": "The SMS template for the e-consult link is disabled for this practice",
  "error.EconsultLinkSmsFailed": "The text message could not be sent. Please try again.",
  "error.EconsultPatientMissingPhoneNumber": "We have no phone number on record for you",
  "error.EmailDomainBlocked":
    "This email address uses a domain that is not allowed. Contact the sender of the invitation.",
  "error.EmailNotDeliverable":
    "This email address contains special characters (such as accents) that we can't deliver to. Please use an address without accented characters.",
  "error.InvitationAccountAlreadyExists":
    "You already created an account with this link — log in to connect it instead.",
  "error.HISLogout": "You have been logged out of the HIS",
  "error.IdentificationCheckError": "Failed to start the ID check",
  "error.InvitationNotRedeemable": "This invitation link is no longer valid",
  "error.InvitationRecipientMismatch": "This invitation link was sent to a different email address",
  "error.LoginInactiveUser": "This account is not active",
  "error.FileContentTypeNotAllowed": "This file type is not allowed.",
  "error.FileTooLarge": "This file is too large. The maximum size is {maxMb}MB.",
  "error.ImageDimensionsTooLarge": "This image's dimensions are too large.",
  "error.ImageProcessingFailed": "This image could not be processed. It may be corrupt or too large.",
  "error.MaintenanceBannerPracticeUnknown":
    "One or more of the selected practices no longer exist. Reload the page and pick the practices again",
  "error.NoAgendaConfigured": "No agendas configured",
  "error.NOT_CURRENT_QUESTION":
    "The questionnaire session got out of sync. We're trying to navigate you to the right question. Please refresh the page if this does not work.",
  "error.PatientAccountDuplicateEmail": "This email address is already in use",
  "error.PatientAccountEmailAlreadyInUse": "This email address is already in use",
  "error.PatientAccountInvalidCredentials": "Invalid credentials",
  "error.PatientAccountInvalidCredentialsMfa": "Invalid credentials or verification code",
  "error.PatientAccountLoginAttemptsExceeded": "Login attempts exceeded",
  "error.PatientAccountLoginExpired": "Login expired",
  "error.PatientAccountPasswordResetExpired": "Password reset link expired",
  "error.PatientAccountSmsAttemptsExceeded": "SMS attempts exceeded",
  "error.PatientAccountSmsIncorrectCode": "SMS incorrect code",
  "error.PatientBSNAlreadyExists": "Patient with this BSN already exists in HIS",
  "error.PatientBSNInvalid": "BSN does not satisfy 11-check validation",
  "error.PostalCodeP4DoesNotExist": "This postal code doesn't exist",
  "error.PracticeConfigurationAccessDenied": "You no longer have administrator access to this practice",
  "error.PracticeRegistrationAgbUnknown":
    "AGB code {agbCode} is not a known practice AGB code. Pick a practice from the list — a GP's personal AGB code does not work",
  "error.RegistrationAlreadyCreated": "Already created registration request",
  "error.RegistrationClosed": "Registration closed",
  "error.RegistrationHisCannotReceive":
    "This practice's HIS cannot receive registrations, so there is nothing to send. Register this person in the HIS manually.",
  "error.RegistrationHisFailed": "Failed to register patient in HIS",
  "error.RegistrationRequestAcceptedHisKnown": "Patient known in HIS",
  "error.RegistrationRequestCompletedHisUnknown": "Patient not known in HIS",
  "error.RegistrationRequestDuplicateActive":
    "This person already has an active registration request or is already a patient in this practice",
  "error.RegistrationRequestExistingPatient": "Patient already exists, create an account instead of registration",
  "error.ReregistrationHisUnknown": "Patient not known in HIS",
  "error.SwapListNoRecipientsWithEmail": "None of the selected patients has an email address, so no email can be sent.",
  "error.UnsupportedImportFile": "Only CSV and Excel (.xlsx) files are supported",
  "error.UsernameAlreadyInUse": "Username is already in use",
  "error.UziCertificatePublicUnreadable": "The public file could not be read",
  "error.UziCertificateIssuerNotAccepted": "This is not a UZI or PKIoverheid certificate",
  "error.UziCertificatePrivateUnreadable": "The private file could not be read",
  "error.UziCertificateKeyPairMismatch": "The public and private file do not belong together",
  "error.VecozoCovPermissionDenied": "Vecozo COV permission is not granted",
  "error.VecozoIonPermissionDenied": "Vecozo ION permission is not granted for AGB code {agbCode}",
  "error.WalkInConsultCreationClosed": "Walk-in consult is closed",
  "error.WalkInConsultCreationQueueFull": "Walk-in consult queue is full",
  "error.loginInvalidCredentials": "Invalid credentials",
  "error.loginInvalidCredentialsTotp": "Invalid credentials - TOTP",
  "error.mfaCallNotAllowed": "MFA call not allowed",
  "error.mfaContextMismatch": "MFA session context mismatch",
  "error.mfaDeliveryCooldown": "MFA delivery cooldown",
  "error.mfaDeliveryFailed": "Could not send the verification code. Please try again later",
  "error.mfaInvalidToken": "Invalid MFA token",
  "error.mfaMaxAttemptsReached": "MFA session max attempts reached",
  "error.mfaSessionExpired": "MFA session expired",
  "error.mfaSessionNotFound": "MFA session not found",

  // Zod validation errors
  "error.email": "This is not a valid email address",
  "error.stringLength": "There must be a value",
  "error.dateTime.future": "Must be a date in the future",

  "error.generic.name.min": `Name/Identifier must be at least ${NAME_MIN_LENGTH} characters`,
  "error.generic.name.max": `Name/Identifier must be at most ${NAME_MAX_LENGTH} characters`,
  // Generic Zod issue translations, keyed by ZodIssueCode.
  // Available variables per code come from the Zod issue metadata.
  "error.zod.required": "{field} is required",
  "error.zod.too_small": "{field} must be at least {minimum, plural, one {# character} other {# characters}}",
  "error.zod.too_big": "{field} must be at most {maximum, plural, one {# character} other {# characters}}",
  "error.zod.invalid_type": "{field} is invalid",
  "error.zod.invalid_format": "{field} has an invalid format",
  "error.zod.invalid_value": "{field} has an invalid value",
  "error.zod.not_multiple_of": "{field} must be a multiple of {divisor}",
  "error.zod.unrecognized_keys": "Unrecognized fields received",
  "error.zod.invalid_union": "{field} is invalid",
  "error.zod.invalid_key": "{field} has an invalid key",
  "error.zod.invalid_element": "{field} has an invalid element",
  "error.zod.custom": "{field} is invalid",
  "error.zod.field.default": "Field",

  // Domain type validation errors
  "login.username.info": "At least 5 characters. No '@' or other special symbols.",
  "error.usernameMinLength": "Username must be at least 5 characters",
  "error.usernameMaxLength": "Username must be at most 64 characters",
  "error.usernameInvalidChars": "Username contains invalid characters",
  "error.usernameReserved": "This username is reserved",
  "error.usernameAlreadyInUse": "This username is already in use",
  "error.emailOrUsernameRequired": "Email or username is required",
  "error.customQuestion.required": "This field is required",
  "error.customQuestion.selectOption": "Please select an option",
  "error.customQuestion.checkboxRequired": "Please select at least one option",
  "error.phoneNumber.invalid": "Invalid phone number",
  "error.phoneNumber.invalidRegion": "Phone number must be from region {region}",
  "error.phoneNumber.landlineNotAllowed": "Landline phone numbers are not allowed",
  "error.bsn.invalid": "Invalid BSN",
  "error.agbCode.invalid": "Invalid AGB code",
  "error.country.invalid": "Invalid country",
  "error.fullName.invalid": "Invalid full name",
  "error.gender.invalid": "Invalid gender",
  "error.postalCode.invalid": "Invalid postal code",
  "error.postalCode.p6Format": "Postal code must be in P6 format (e.g. 1234AB)",
  "error.dayDate.invalid": "Invalid date",
  "error.dayDate.minimumAge": "Must be at least {minimumAge, plural, one {# year} other {# years}} old",
  "error.dayDate.afterDate": "Birth date must be after 01-01-1900",
  "error.token.onlyDigits": "The code must contain only digits",
  "error.token.length": "The code must be exactly {length, plural, one {# digit} other {# digits}}",
  "error.slug.minLength": `The domain name must be at least ${MIN_SLUG_LENGTH} character`,
  "error.slug.maxLength": `The domain name can be at most ${MAX_SLUG_LENGTH} characters`,
  "error.slug.reserved": "The domain name is not allowed",

  "error.login.totp.validation": "The code must contain 6 digits",

  "error.page.minLimit": `Limit must be at least ${MIN_LIMIT}`,
  "error.page.maxLimit": `Limit must not exceed ${MAX_LIMIT}`,
  "error.page.offset": `Offset must be bigger than 0`,
  "error.page.search": `Search text must not exceed ${MAX_QUERY_LENGTH}`,

  "error.plato.openAnswer": "An open answers must be provided",
}
